/**
 * Unified Agent Store — Phase 6
 *
 * Replaces the legacy ai-panel-store WebSocket-based state with a unified
 * intent-aware store that drives the single assistant surface (Chat/Plan/Write).
 *
 * Uses sendAgentTurn() from agent-client.ts for NDJSON streaming.
 * Maintains all state that DraftEditor needs (segments, selection editing).
 *
 * Parent spec §2, §12 Stage 3
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import {
  sendAgentTurn,
  cancelAgentTurn,
  pollAsyncJob,
  replayTurnEvents,
  type AgentIntent,
  type StateDefault,
  type StreamEvent,
  type AsyncJobResult,
  type TurnAskAnswer,
} from '@/lib/agent-client'
import {
  appendMemorySavedLine,
  parseMemorySavedLine,
  undoMemorySavedLine,
  type MemorySavedLine,
} from '@/lib/memory-saved'
import { useActiveJobsStore } from '@/stores/active-jobs-store'
import { useUnitBriefStore } from '@/stores/unit-brief-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { useDraftingStore } from '@/stores/drafting-store'
import {
  activateAgentThread,
  createAgentThread,
  editSelection,
  getActiveTurn,
  getArtifactDocument,
  getArtifactOutline,
  getOrCreateAgentSession,
  getAuthToken,
  listAgentThreads,
  resolveProposal,
  saveSegment,
  type AgentSessionResponse,
  type PendingDraftProposal,
  type AgentThread,
  type AgentThreadCarryMode,
  type ActiveTurnInfo,
} from '@/lib/api'
import type {
  DraftSegment,
  EditSelectionResponse,
  LivePlanJson,
  Outline,
  PlanSuggestion,
  ResponsePlan,
  VerifierIssue,
  VerifierMetrics,
  VerifierStatus,
} from '@/lib/api'
import {
  createPlanEditDraft,
  isEditablePlanVersion,
  planDraftHasUnsavableContent,
  planDraftSignature,
  planDraftToLivePlan,
  validatePlanDraft,
  type PlanEditDraft,
} from '@/lib/plan-edit'
import { captureEvent } from '@/lib/posthog'
import type { Scope } from '@/types/scope'
import type { ChatAttachmentRef } from '@/types/assistant-attachments'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'
import { themesLiveOnUnitBrief } from '@/lib/brief-themes'
import type { SourceHighlightData } from '@/stores/assistant-store'
import {
  reduceActivityPhases,
  initialPhaseState,
  optimisticPhaseState,
  applyProgressNarration,
  settleActivityPhases,
  type PhaseState,
  type PersistedActivityStep,
} from '@/lib/activity-phases'
import { useSectionAssignmentsStore } from '@/stores/section-assignments-store'
import { useScheduleStore } from '@/stores/schedule-store'
import { useWordFormStore } from '@/stores/word-form-store'
import { buildSegmentDiff, applyHunkDecisions } from '@/components/drafting/segmentDiff'
import { REJECT_CONFLICT } from '@/lib/pending-ai-change-notices'
import { displayTurnUserMessage } from '@/lib/question-answer-message'
import { trimDanglingMarkdown } from '@/lib/partial-markdown'
import { recapOpeningParagraph } from '@/lib/document-job-progress'
import {
  decideStagedSuggestion,
  pendingSuggestionFromProposal,
  stagedFieldsFromEvent,
  stagedMergedHtml,
  type PendingSuggestion,
  type StagedSuggestionFields,
} from '@/lib/staged-suggestion'
import { useAiEditsStore, parseDraftEditApplied } from '@/stores/ai-edits-store'
import { readOnlySegmentsFromDocument } from '@/lib/read-only-segments'
import { useQuestionsStore, parseRaisedQuestions, parseQuestionAnswered } from '@/stores/questions-store'
import {
  SUGGESTION_ACCEPT_FAILED,
  SUGGESTION_ALREADY_ACCEPTED,
  SUGGESTION_REJECT_FAILED,
  SUGGESTION_SECTION_GONE,
} from '@/lib/ai-edit-copy'

// ===================
// Types
// ===================

export type { AgentIntent, StateDefault }

export interface AgentCitationSource {
  documentId: string
  documentName: string
  pageNumber: number | null
  snippet?: string
  /** Verified verbatim quote for the cited claim (quote-first highlighting). Absent on legacy rows. */
  quote?: string
  /** V1 (PDF bbox) or V2 (text-search) highlight data for precise sentence highlighting. */
  highlightData?: SourceHighlightData | null
}

export type ProposalChatStatus = 'pending' | 'accepted' | 'rejected'

export interface ProposalChatMeta {
  proposalId: string
  sectionId: string
  sectionName?: string
  status: ProposalChatStatus
  /** Word counts from the diff so the stub can render "+30 / -12 words". */
  diffStats?: { added: number; removed: number }
}

export type PlanChatStatus = 'pending' | 'accepted' | 'rejected'

export interface PlanChatMeta {
  planId: string
  version: number
  plan: LivePlanJson
  status: PlanChatStatus
  /**
   * The active scope's sectionKey at append time (§4.3a-C3). Keys the stale
   * guard per section: a brief edit for this sectionKey marks the plan stale.
   */
  sectionKey: string
  /** §4.1/§5 — review-time suggestions, never persisted to plan_json. */
  suggestions: PlanSuggestion[]
  /**
   * The most recently dismissed suggestion, kept so the dismissal can be undone.
   * Single level on purpose — a second dismissal replaces the first, which is
   * what "Undo" means everywhere else in the product. It rides the plan message
   * rather than a store-level map so it dies with the card it belongs to; no
   * separate reset path can leave it stranded.
   */
  dismissedSuggestion?: { suggestion: PlanSuggestion; index: number } | null
  /**
   * Renders as a collapsed, read-only "Plan approved" card. Set on an approved
   * plan put back from history on reload/section switch, and live on a plan a
   * turn drafted from — the turn that proposed it or a later "draft it" — which
   * the backend approves at turn end. The same card either way. A card the
   * writer accepts in this view stays open as before.
   */
  collapsed?: boolean
}

export interface PlanConflictState {
  planId: string
  /** The version now on the server (from the 409 body). */
  currentVersion: number
  /** The version the rejected save was based on. */
  requestedBaseVersion: number
}

/**
 * Outcome of `savePlanEdits`. The failure reasons are deliberately distinct:
 *  • `conflict`     — 409. Recoverable: refresh the plan and re-apply.
 *  • `not_editable` — TERMINAL 400. The plan left `draft` while the card was
 *    open (approved or rejected elsewhere). Retrying can never succeed, so this
 *    must NOT be routed through the `refresh_retry_conflict` affordance.
 *  • `invalid`      — a plan invariant (empty/duplicate heading, bad word
 *    count). Fixable in the card; `conflictingSectionIds` says where.
 *  • `error`        — anything else (transport, 403, 500).
 */
export type SavePlanEditsResult =
  | { ok: true; staleSectionIds: string[] }
  | {
      ok: false
      reason: 'conflict' | 'not_editable' | 'invalid' | 'error'
      message: string
      conflictingSectionIds?: string[]
    }

export type { MemorySavedLine, MemorySavedStatus } from '@/lib/memory-saved'

export interface AgentChatMessage {
  id: string
  role: 'user' | 'assistant' | 'status' | 'system' | 'proposal' | 'plan'
  content: string
  timestamp: string
  /** True when this message is currently being streamed (chunk-by-chunk). */
  isStreaming?: boolean
  intent?: AgentIntent
  /** Non-null for status messages that indicate an event type. */
  eventType?: string
  /** Epoch ms when this transient status should be removed. */
  expiresAt?: number
  /** True when the status is in the fade-out transition phase. */
  isExiting?: boolean
  /**
   * True for a status line that must survive in the transcript — cancellation,
   * background completion, failure. The panel hides TRANSIENT status lines once
   * activity rows exist (the rows narrate the same thing), so without this flag
   * the durable ones were unrenderable for the life of the session.
   */
  durable?: boolean
  /**
   * Persisted activity steps from a completed turn (for historical messages).
   * Raw OLD-shape steps; the panel folds them through `hydratePhasesFromSteps`.
   */
  persistedSteps?: PersistedActivityStep[]
  /** Citation sources from evidence retrieval (tender/bid documents). */
  sources?: AgentCitationSource[]
  /** Citation sources from library retrieval. */
  librarySources?: AgentCitationSource[]
  /**
   * 'saved' on a turn that wrote a draft (or proposed one for review): its
   * markers are the saved draft's numbering, which the editor shows, so the
   * panel displays each marker's own label. A question-only turn has none and
   * keeps the compact 1..n display.
   */
  citationLabels?: 'saved'
  /**
   * Snippet of editor text the user had selected when sending this message.
   * Renders above the message body so the chat thread reads coherently
   * ("Re: '...the contractor shall...': make this more concise").
   */
  quotedText?: string
  /**
   * Display refs for files attached to a user message. Never carries the
   * s3Key — chips only.
   */
  attachments?: Array<{
    attachmentId: string
    filename: string
    mimeType: string
    size: number
  }>
  /** Set on `role: 'proposal'` messages — proposalId, status, diff summary. */
  proposal?: ProposalChatMeta
  /** Set on `role: 'plan'` messages — the structured live plan + accept lifecycle. */
  plan?: PlanChatMeta
  /**
   * Bid memory the agent saved during this reply, one "Saved to memory · Undo"
   * line each, in save order. Undo with `undoMemorySaved(message.id, memoryId)`.
   * Live only: a reply reloaded from history does not carry it.
   */
  memorySaved?: MemorySavedLine[]
}

export interface SuggestedAction {
  actionId: string
  label: string
  utterance: string
}

export interface SuggestedActionOffer {
  offerId: string
  sourceTurnId: string
  recommendedActionId: string
  expiresAt: string
  actions: SuggestedAction[]
}

export interface SelectionState {
  text: string
  range: { from: number; to: number }
  sectionId?: string
}

/**
 * A selection rewrite waiting on Accept/Reject. `sourceText` is the exact
 * document text the range held when the rewrite was asked for — the editor
 * checks it on Accept so a document that moved underneath (an agent write, a
 * segment refresh) cannot land the rewrite over unrelated prose.
 */
export type PendingSelectionEdit = EditSelectionResponse & {
  sourceText?: string
}

export interface VerificationState {
  status: VerifierStatus
  summary: string
  issues: VerifierIssue[]
  metrics: VerifierMetrics
  fixInstructions?: string
  timestamp: string
}

export type WritingWorkflowState =
  | 'discovering'
  | 'clarifying'
  | 'plan_preview'
  | 'awaiting_approval'
  | 'drafting'
  | 'revising'
  | 'error'
  | 'idle'

export type ProgressPhase =
  | 'searching_evidence'
  | 'reviewing_evidence'
  | 'discovering'
  | 'drafting'
  | 'revising'
  | 'verifying'
  | 'background'
  | 'finalizing'

export interface ProgressCardState {
  active: boolean
  phase: ProgressPhase | null
  detail: string | null
  isBackground: boolean
  percent: number | null
}

export interface ProgressRecapState {
  message: string
  kind: 'success' | 'cancelled' | 'error'
  expiresAt: number
}

export type StreamRecoveryState = 'idle' | 'recovering' | 'replay_complete' | 'replay_unavailable'

interface LastSubmittedTurn {
  message: string
  selectedEvidence?: string[]
  factBankOnly?: boolean
  threadId: string
  scope: Scope
  retryAttempted: boolean
  quotedText?: string
}

export type HunkDecision = 'accepted' | 'rejected' | 'pending'

/**
 * The one AI change the editor's review strip is showing. `mode` (R1 W2):
 * absent = legacy, the AI text is already in the section (R0 strip);
 * `staged` = a suggestion over the section's current text, nothing applied.
 */
export interface DraftPatchPreview extends StagedSuggestionFields {
  proposalId: string
  sectionId: string
  /**
   * The turn that made it, as the session's pending proposal says: null for a
   * document job's suggestion (its reviewer notes may be shown with it);
   * absent when the preview came from a live turn's events.
   */
  turnId?: string | null
  before: string
  after: string
  appliedPatch: {
    newVersion?: number
    affectedSectionIds?: string[]
    planSectionOrder?: string[]
    segments?: DraftSegment[]
  } | null
  /**
   * Per-hunk accept/reject decisions for partial-acceptance (Track 3).
   * Keys are hunk IDs from buildSegmentDiff; values default to 'pending'
   * until the user clicks ✓ or ✗ on an inline hunk control.
   */
  hunkDecisions: Record<string, HunkDecision>
}

interface SessionInitContext {
  bidId: string
  artifactId: string
  sectionKey: string
  scopeKind: 'unit' | 'artifact'
}

interface SendTurnOptions {
  isConflictRetry?: boolean
  suggestedOfferId?: string
  suggestedActionId?: string
  suggestedActionSourceTurnId?: string
  /** Layer 1: answer-time Fact Bank scope for this turn. */
  factBankOnly?: boolean
  attachments?: Array<{
    attachmentId: string
    s3Key: string
    filename: string
    mimeType: string
    size: number
  }>
  /**
   * Quoted snippet to render above the user's message bubble. Used when the
   * user asks a question about a selection in the editor — the bubble shows
   * the snippet as context while the `message` field carries the inlined
   * snippet to the LLM.
   */
  quotedText?: string
  /** R1 Q2: the turn answers one of the AI's questions (the message is the answer). */
  askAnswer?: TurnAskAnswer
  /**
   * R1 E1f: the panel is advice-only (a finalised or in-review document). The
   * turn only advises, and nobody can accept or reject a suggestion there, so
   * one waiting in the editor does not hold the turn back. Client-only: never
   * sent to the server, which withholds every write tool on such a turn.
   */
  adviceOnly?: boolean
}

interface AgentStoreState {
  // ===================
  // Session & Intent
  // ===================
  sessionId: string | null
  sessionInitContext: SessionInitContext | null
  activeScope: Scope | null
  activeThreadId: string | null
  expectedRevision: string | null
  threads: AgentThread[]
  threadPreviewById: Record<string, string>
  selectedIntent: AgentIntent
  stateDefault: StateDefault | null

  // ===================
  // Turn state
  // ===================
  currentTurnId: string | null
  isStreaming: boolean
  isCancellable: boolean

  // ===================
  // Async job state
  // ===================
  asyncJobId: string | null
  asyncJobProgress: number
  asyncJobMessage: string
  isPolling: boolean
  progressCard: ProgressCardState
  progressRecap: ProgressRecapState | null

  // ===================
  // Conversation
  // ===================
  messages: AgentChatMessage[]
  /** Streaming buffer — current assistant message being built from chunks. */
  streamingMessageId: string | null

  activeSuggestedOffer: SuggestedActionOffer | null
  writingWorkflowState: WritingWorkflowState
  pendingMutationKind: 'drafting' | 'revising' | null
  streamRecoveryState: StreamRecoveryState
  lastSubmittedTurn: LastSubmittedTurn | null
  errorAction: 'refresh_retry_conflict' | null
  isLockedByAnotherTab: boolean
  /**
   * R1 E1f: the session load says the whole-document job is offered in this
   * panel (switch on, narrative document). The panel's own block on requests
   * about several sections steps aside while it is true.
   */
  documentJobEnabled: boolean

  // ===================
  // Plan state (Plan intent)
  // ===================
  currentPlan: ResponsePlan | null
  proposedPlan: Record<string, unknown> | null
  /**
   * Plan IDs whose underlying brief moved on after generation (§4.3a-C3). A
   * stale plan must NOT be drafted from — `acceptPlanMessage` no-ops and the
   * card surfaces a "regenerate first" affordance.
   */
  stalePlanIds: Set<string>
  /**
   * In-progress card edits, keyed by planId. Store-owned rather than card-owned
   * because the card is derived from `messages[].plan.plan` and is remounted
   * wholesale whenever the server plan changes (`buildHydratedPlanMessage`, or a
   * second `set_plan` minting a fresh message id).
   */
  planEdits: Record<string, PlanEditDraft>
  /**
   * Set when a plan save lost the optimistic-lock race. Drives the existing
   * `errorAction: 'refresh_retry_conflict'` banner and redirects
   * `recoverFromRevisionConflict` to refresh the plan instead of replaying a turn.
   */
  planConflict: PlanConflictState | null
  draftPatchPreview: DraftPatchPreview | null
  isResolvingDraftPatchPreview: boolean

  // ===================
  // Segment state (Write intent + DraftEditor)
  // ===================
  segments: DraftSegment[]
  planSectionOrder: string[]
  lastVerification: VerificationState | null

  // ===================
  // Selection editing (DraftEditor integration)
  // ===================
  selection: SelectionState | null
  selectionEditResult: PendingSelectionEdit | null
  isEditingSelection: boolean
  activeSelectionEditRequestId: number
  applySelectionRewrite: ((from: number, to: number, replacement: string) => void) | null
  flushDraftSavesBeforeThreadChange: (() => Promise<void>) | null

  // ===================
  // Activity phases (§2.7 real-time pipeline visibility)
  // ===================
  /** Raw last stream event — the seam the activity-phase reducer folds over. */
  lastStreamEvent: StreamEvent | null
  /**
   * §2.7 activity phases for the in-flight/last turn. Folded from
   * `lastStreamEvent` by the store-level subscription at the bottom of this file
   * (mirrors tender-chat-store / assistant-store). Rendered by `<ActivityPhases>`.
   */
  activityPhases: PhaseState

  // ===================
  // Panel open/close
  // ===================
  isPanelOpen: boolean

  // ===================
  // Error
  // ===================
  error: string | null

  // ===================
  // Actions — Session
  // ===================
  initSession: (bidId: string, artifactId: string, sectionKey: string, scopeKind?: 'unit' | 'artifact') => Promise<void>
  /**
   * Fill `segments` with a section's saved text WITHOUT opening an AI session —
   * for a view-only user, whom `POST /agent/sessions` refuses. Reads the Full
   * response model (`GET /artifacts/{id}/document`); display-only, never saved.
   * Rejects when the read fails, so the caller can say so.
   */
  loadReadOnlySegments: (artifactId: string, sectionKey: string) => Promise<void>
  setSessionId: (sessionId: string) => void
  /** Re-attach progress indicator to an already-running background job after navigating back. */
  resumeBackgroundJobIfAny: () => void
  /**
   * Called by the active-turn poller when the backend reports the turn is no
   * longer in flight (completed / failed / cancelled — or reaped as stale).
   * Re-hydrates the session so the final assistant message lands in the UI.
   * Safe to call from outside the panel mount.
   */
  reconcileResumedTurn: (turnId: string) => Promise<void>
  loadThreads: () => Promise<void>
  createThread: (carryMode: AgentThreadCarryMode) => Promise<void>
  switchThread: (threadId: string) => Promise<void>

  // ===================
  // Actions — Intent
  // ===================
  setSelectedIntent: (intent: AgentIntent) => void

  // ===================
  // Actions — Turn
  // ===================
  sendTurn: (message: string, selectedEvidence?: string[], options?: SendTurnOptions) => Promise<void>
  cancelTurn: () => Promise<void>
  recoverFromRevisionConflict: () => Promise<void>
  appendAssistantMessage: (content: string) => void
  clearSuggestedOffer: () => void
  /**
   * Undo a "Saved to memory" line: deletes the item. Never throws; the line's
   * status carries the outcome. A second press while one is in flight, or on
   * a line already undone, does nothing.
   */
  undoMemorySaved: (messageId: string, memoryId: string) => Promise<void>
  /** Internal stream-event reducer used by sendTurn. */
  _handleStreamEvent: (event: StreamEvent) => void
  /** Test hook — drop any deferred plan card (mirrors _handleStreamEvent). */
  _clearPendingPlanMessage: () => void
  acceptDraftPatchPreview: () => Promise<void>
  rejectDraftPatchPreview: () => Promise<void>
  /** Approve the live plan carried by a `role: 'plan'` message and mark it accepted. */
  acceptPlanMessage: (planId: string) => Promise<void>
  /**
   * Reject the live plan carried by a `role: 'plan'` message and mark it
   * rejected. Twin of `acceptPlanMessage` — the catch lives here because
   * main.tsx's unhandled-rejection handler deliberately skips ApiRequestError,
   * so a component-level catch would show the writer nothing.
   */
  rejectPlanMessage: (planId: string) => Promise<void>

  // ===================
  // Actions — Plan card editing (AI reco 3A)
  // ===================
  /**
   * Fork an edit draft for `planId` from its plan message. Idempotent — an
   * existing draft is never clobbered. Every mutator below calls this first, so
   * the card only needs it to enter edit mode.
   */
  beginPlanEdit: (planId: string) => void
  updatePlanGoal: (planId: string, goal: string) => void
  updatePlanSection: (
    planId: string,
    sectionId: string,
    patch: { heading?: string; targetWordCount?: number }
  ) => void
  movePlanSection: (planId: string, sectionId: string, direction: -1 | 1) => void
  removePlanSection: (planId: string, sectionId: string) => void
  /** Appends an empty (or seeded) key point and returns its stable id, or null. */
  addPlanKeyPoint: (planId: string, sectionId: string, text?: string) => string | null
  /** Addressed by key-point ID — never by rendered index (machinery lines are hidden). */
  updatePlanKeyPoint: (
    planId: string,
    sectionId: string,
    keyPointId: string,
    text: string
  ) => void
  removePlanKeyPoint: (planId: string, sectionId: string, keyPointId: string) => void
  /** Discard the draft and leave edit mode. Also clears a pending plan conflict. */
  revertPlanEdits: (planId: string) => void
  /** PATCH the whole edited plan under the optimistic lock. */
  savePlanEdits: (planId: string) => Promise<SavePlanEditsResult>
  /**
   * Re-fetch a plan that lost the optimistic-lock race and drop the local draft.
   * Invoked by `recoverFromRevisionConflict` when a `planConflict` is pending.
   */
  resolvePlanConflict: () => Promise<void>
  /**
   * Flag every pending live plan message for `sectionKey` as stale (§4.3a-C3).
   * Fired from in-session brief edits (notes/pins/requirement/theme links).
   */
  markPlanStaleForSection: (sectionKey: string) => void
  /**
   * Fold a suggestion into the Brief (link/pin) then remove it from the plan
   * message and mark the plan stale (§4.3a — brief changed). Fires the
   * `suggestion_accepted` KPI event.
   */
  acceptPlanSuggestion: (planId: string, suggestion: PlanSuggestion) => Promise<void>
  /**
   * Remove a suggestion from the plan message without folding it into the Brief.
   * Stashes it on the message so `restorePlanSuggestion` can put it back, and
   * fires the `suggestion_dismissed` KPI event.
   */
  dismissPlanSuggestion: (planId: string, suggestionId: string) => void
  /**
   * Undo the last dismissal on this plan message — re-inserts the suggestion at
   * the position it was removed from. Suggestions are event-only (§4.1), so a
   * dismissal that isn't undone before the card is replaced is gone for good.
   */
  restorePlanSuggestion: (planId: string) => void
  /**
   * Re-run plan generation through the live write-intent path (Option B — no
   * "Generate Plan" button). The fresh plan_patch_applied appends an un-stale
   * card with a new briefSnapshotAt.
   */
  regeneratePlan: () => Promise<void>
  /**
   * Set the user's accept/reject decision for a single hunk inside the
   * current preview. Used by inline ✓/✗ controls in the editor diff view.
   * Bulk accept/reject still go through accept/rejectDraftPatchPreview.
   */
  setHunkDecision: (hunkId: string, decision: HunkDecision) => void

  // ===================
  // Actions — Segments (DraftEditor)
  // ===================
  setSegments: (segments: DraftSegment[], planSectionOrder: string[]) => void
  updateSegmentVersion: (
    sectionId: string,
    newVersion: number,
    updates?: Partial<Pick<DraftSegment, 'content' | 'wordCount' | 'status'>>
  ) => void
  /** Insert a segment (e.g. created by a first manual save) or replace it if present. */
  upsertSegment: (segment: DraftSegment) => void
  updateSegmentStatus: (sectionId: string, status: DraftSegment['status']) => void
  /**
   * Ask the drafter to rewrite the named stale sections — the action behind the
   * editor's stale banner. Sends exactly ONE write-intent turn naming every
   * section, never one turn per section: `sendTurn` has no isStreaming guard and
   * `abortController` is a module singleton. Gated on an idle turn.
   *
   * (Replaces `requestRegenerateSegment`, which only re-marked an
   * already-stale segment stale — the banner's button was a no-op.)
   */
  regenerateStaleSegments: (sectionIds: string[]) => Promise<void>

  // ===================
  // Actions — Selection editing (DraftEditor)
  // ===================
  setSelection: (selection: SelectionState | null) => void
  editCurrentSelection: (
    sessionId: string,
    instruction: string,
    selectedEvidence?: string[],
    options?: { attachments?: ChatAttachmentRef[]; factBankOnly?: boolean }
  ) => Promise<void>
  clearSelectionEdit: () => void
  setApplySelectionRewrite: (fn: ((from: number, to: number, replacement: string) => void) | null) => void
  setThreadTransitionSaveFlusher: (fn: (() => Promise<void>) | null) => void

  // ===================
  // Actions — Panel
  // ===================
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void

  // ===================
  // Actions — General
  // ===================
  clearError: () => void
  reset: () => void
}

// ===================
// Abort controller (held outside Zustand — not serialisable)
// ===================

let abortController: AbortController | null = null
let pollAbortController: AbortController | null = null
let initSessionRequestCounter = 0
let initSessionInFlight: { scopeId: string; promise: Promise<void> } | null = null

const STATUS_VISIBLE_MS = 30_000
const STATUS_FADE_MS = 500
const STATUS_TTL_MS = STATUS_VISIBLE_MS + STATUS_FADE_MS
const PROGRESS_RECAP_VISIBLE_MS = 15_000
const MAX_SEND_TURN_AUTO_RETRIES = 1
const SESSION_BUSY_HINT_TTL_MS = 5_000

type StatusTimerEntry = {
  fadeTimer: ReturnType<typeof setTimeout>
  removeTimer: ReturnType<typeof setTimeout>
}

// ===================
// Bid memory lines ("Saved to memory · Undo")
// ===================

/**
 * An assistant placeholder with nothing in it: no text and no saved-memory
 * line. Only these are dropped when a turn ends or hands off; a reply whose
 * only content is a "Saved to memory" line must survive, or its Undo goes too.
 */
function isEmptyAssistantPlaceholder(message: AgentChatMessage | null | undefined): boolean {
  return Boolean(
    message
      && message.role === 'assistant'
      && message.content.trim().length === 0
      && !(message.memorySaved && message.memorySaved.length > 0)
  )
}

/**
 * State patch for the reply placeholder when the stream hands over to a job
 * poll: drop it if empty (as before), settle it if it only carries memory
 * lines, leave it alone if it has text.
 */
function releaseReplyPlaceholder(state: {
  messages: AgentChatMessage[]
  streamingMessageId: string | null
}): Partial<{ messages: AgentChatMessage[]; streamingMessageId: string | null }> {
  const placeholder = state.streamingMessageId
    ? state.messages.find((m) => m.id === state.streamingMessageId)
    : null
  if (!placeholder || placeholder.role !== 'assistant' || placeholder.content.trim().length > 0) return {}
  if (isEmptyAssistantPlaceholder(placeholder)) {
    return { messages: state.messages.filter((m) => m.id !== placeholder.id), streamingMessageId: null }
  }
  return {
    messages: state.messages.map((m) => (m.id === placeholder.id ? { ...m, isStreaming: false } : m)),
    streamingMessageId: null,
  }
}

const statusTimers = new Map<string, StatusTimerEntry>()
const lastProcessedSeqByTurn = new Map<string, number>()
let progressRecapTimer: ReturnType<typeof setTimeout> | null = null
let sessionBusyHintTimer: ReturnType<typeof setTimeout> | null = null
const CARRY_FORWARD_SEED_PROMPT = 'Carry forward prior thread decisions into this thread.'
const CARRY_FORWARD_FALLBACK_SUMMARY = 'Key decisions from your previous chat were carried forward into this chat.'

// Turn-level citation accumulators — replaced on each evidence_search_completed,
// attached to the assistant message at turn_completed.
let turnCitations: AgentCitationSource[] = []
let turnLibraryCitations: AgentCitationSource[] = []
// Set when the current turn wrote a draft or proposed one (`draft_patch_applied`
// / `draft_patch_proposed`) — the live twin of the persisted
// `assistantMetadata.citationLabels`. Attached with the citations.
let turnWroteDraft = false

// Deferred plan card (Specs/plan-card-ux-spec.md D1) — a fresh plan's chat card
// is held here until the turn's terminal text settles, so the reading order is
// always "assistant narration, then plan card". Flushed on every terminal path
// (turn_completed unless async_pending, turn_error, cancel, both async-polling
// completion callbacks, and defensively at sendTurn start).
let pendingPlanMessage: AgentChatMessage | null = null

// Set when the current turn wrote draft content — a streamed
// `draft_patch_applied`, or a background job whose payload reports sections
// written. A pending "Accept & draft" card is only for reviewing a FRESH plan
// (no draft yet), so a stashed card is never flushed as pending once its turn
// has drafted, matching `buildHydratedPlanMessage` on reload. Kept as a flag
// rather than re-read from `segments`: a draft event may carry only
// `affectedSectionIds`, no content.
// Per-turn: cleared with every flush and every reset of the pending card.
let pendingPlanTurnDrafted = false

// The plan the current turn's latest draft was written from, as its
// `draft_patch_applied` names it (planId + plan row version). The backend
// approves exactly that plan when the turn ends, if it is still that draft
// (`findPlanDraftedFrom` in turn-pipeline.ts; Kapil 2026-09-28: a plan the
// draft was written from counts as approved), and a reload shows it as the
// collapsed "Plan approved" card after the reply of the turn that proposed it.
// The flush makes the same change live: this turn's stashed card, or a pending
// card an earlier turn left on screen. A re-plan of the same plan clears it —
// the row moved past the drafted version, so the backend approves nothing. A
// draft that names no plan clears it too. A background job sets it at poll
// completion from its result's `draftedFromPlan` (`readJobDraftedFromPlan`) —
// the worker approves that plan the same way once it wrote a section.
let pendingDraftedPlan: { planId: string; version: number | null } | null = null

function clearPendingPlanMessage(): void {
  pendingPlanMessage = null
  pendingPlanTurnDrafted = false
  pendingDraftedPlan = null
}

/** Is `meta` the card of the plan this turn drafted from (same plan, same version)? */
function isDraftedPlanCard(
  meta: PlanChatMeta | undefined,
  drafted: { planId: string; version: number | null }
): boolean {
  return Boolean(
    meta
    && meta.planId === drafted.planId
    && (drafted.version === null || meta.version === drafted.version)
  )
}

/**
 * Turn the pending card of the plan a turn drafted from into the collapsed,
 * read-only "Plan approved" card, in place — the card a reload puts after the
 * reply of the turn that proposed it. Pending cards only: a card the writer
 * already accepted (Accept & draft) stays open as before, a rejected card stays
 * rejected, and a card at another version is left alone because the backend
 * skips that plan too. An open inline edit of the plan goes with it — an
 * approved plan can no longer be edited.
 */
function approveDraftedPlanCards(drafted: { planId: string; version: number | null }): void {
  const isTarget = (message: AgentChatMessage): boolean =>
    message.role === 'plan' && message.plan?.status === 'pending' && isDraftedPlanCard(message.plan, drafted)
  if (!useAgentStore.getState().messages.some(isTarget)) return
  useAgentStore.setState((state) => ({
    messages: state.messages.map((message) =>
      isTarget(message) && message.plan
        ? {
            ...message,
            plan: { ...message.plan, status: 'accepted' as const, suggestions: [], collapsed: true },
          }
        : message
    ),
    planEdits: withoutPlanDraft(state.planEdits, drafted.planId),
    planConflict: state.planConflict?.planId === drafted.planId ? null : state.planConflict,
  }))
}

function hasDraftContent(segments: DraftSegment[]): boolean {
  return segments.some((segment) => (segment.content?.trim().length ?? 0) > 0)
}

/**
 * The plan a background job drafted from, when its result names one AND the
 * job wrote a section — exactly when the worker approves it
 * (`approvePlanDraftedByJob` in backend/src/handlers/agent-worker.ts). Counted
 * from the job's own numbers (`generatedSectionIds`, or a stopped run's
 * `completedSections`), never from segment content already in the payload,
 * which may predate the job. Draft-all and questionnaire runs name no plan.
 */
function readJobDraftedFromPlan(
  payload: Record<string, unknown> | null | undefined
): { planId: string; version: number | null } | null {
  if (!payload) return null
  const named = payload['draftedFromPlan']
  if (!named || typeof named !== 'object') return null
  const planId = (named as Record<string, unknown>)['planId']
  const version = (named as Record<string, unknown>)['version']
  if (typeof planId !== 'string' || planId.length === 0) return null
  const generated = payload['generatedSectionIds']
  const completed = readSectionCount(payload['completedSections'])
  const wroteSections = (Array.isArray(generated) && generated.length > 0) || (completed !== null && completed > 0)
  if (!wroteSections) return null
  return { planId, version: typeof version === 'number' && Number.isFinite(version) ? version : null }
}

/** Did a background job's result write any section (fully or before stopping)? */
function asyncPayloadWroteSections(payload: Record<string, unknown> | null | undefined): boolean {
  if (!payload) return false
  const generated = payload['generatedSectionIds']
  if (Array.isArray(generated) && generated.length > 0) return true
  const completed = readSectionCount(payload['completedSections'])
  if (completed !== null && completed > 0) return true
  const segments = payload['segments']
  return Array.isArray(segments) && hasDraftContent(segments as DraftSegment[])
}

/**
 * Error codes whose `message` is synthesised by the API client rather than sent
 * by the server. `HTTP_*` comes from the status-line fallback in `api.ts`
 * (literally "Request failed"), and the network/unknown codes are transport
 * noise — none of them tell a bid manager what happened or what to do next.
 */
const GENERIC_API_ERROR_CODES = new Set(['NETWORK_ERROR', 'UNKNOWN_ERROR'])

/**
 * Choose the banner copy for a failed action: keep genuinely explanatory
 * messages (a semantic server envelope, or one of our own deliberately-authored
 * throws) and swap transport noise for the caller's actionable fallback.
 *
 * Live E2E on 2026-07-31 caught the need for this — a failed plan approve
 * rendered the raw "Request failed", because a plain `error.message` preference
 * always wins against an ApiRequestError.
 */
const ACCEPT_FAILED_MESSAGE = "Couldn't accept the suggestion — try again."

/**
 * The target artifact's outline, with the unit-brief store holding that same
 * artifact's briefs — never another document's.
 *
 * Both stores hold whichever artifact was loaded last and outlive route
 * changes, and the AI panel also runs on pages that never load them (a
 * schedule). An accept that read its stored theme or pin list from them would
 * write the previous document's list onto this one — and both versions are
 * often 1, so a 409 would not catch it. When either store isn't for
 * `artifactId`, this fetches that artifact's outline bundle and hydrates the
 * brief store with it. Throws the generic accept error when the fetch fails,
 * or when yet another artifact took the brief store over while it was in flight.
 */
async function loadBriefContextFor(artifactId: string): Promise<Outline | null> {
  const drafting = useDraftingStore.getState()
  const briefHolder = useUnitBriefStore.getState().artifactId
  if (
    drafting.currentArtifactId === artifactId &&
    !drafting.isLoadingOutline &&
    drafting.outline &&
    briefHolder === artifactId
  ) {
    return drafting.outline
  }
  let response: Awaited<ReturnType<typeof getArtifactOutline>>
  try {
    response = await getArtifactOutline(artifactId)
  } catch {
    throw new Error(ACCEPT_FAILED_MESSAGE)
  }
  const holderNow = useUnitBriefStore.getState().artifactId
  if (holderNow !== artifactId) {
    // A third document was opened meanwhile — its page owns the store now.
    if (holderNow !== briefHolder) throw new Error(ACCEPT_FAILED_MESSAGE)
    useUnitBriefStore.getState().hydrate(artifactId, response.unitBriefs ?? [])
  }
  return response.outline
}

function writerFacingMessage(error: unknown, fallback: string): string {
  // Read `code` off the error rather than testing `instanceof ApiRequestError`:
  // identity checks are unreliable across mocked/duplicated module instances,
  // and the code is the only thing this decision actually needs.
  const code = (error as { code?: unknown } | null | undefined)?.code
  if (typeof code === 'string' && (code.startsWith('HTTP_') || GENERIC_API_ERROR_CODES.has(code))) {
    return fallback
  }
  return error instanceof Error && error.message ? error.message : fallback
}

/**
 * Apply `mutate` to the draft for `planId`, flagging it dirty and clearing the
 * last save's section flags. A missing draft is a no-op — mutators call
 * `beginPlanEdit` first, so the only way here is a plan with no card.
 */
function mutatePlanDraft(
  planEdits: Record<string, PlanEditDraft>,
  planId: string,
  mutate: (draft: PlanEditDraft) => PlanEditDraft
): Record<string, PlanEditDraft> {
  const draft = planEdits[planId]
  if (!draft) return planEdits
  return {
    ...planEdits,
    [planId]: { ...mutate(draft), isDirty: true, conflictingSectionIds: [] },
  }
}

/** Patch draft bookkeeping (save state, section flags) without touching content. */
function patchPlanDraft(
  planEdits: Record<string, PlanEditDraft>,
  planId: string,
  patch: Partial<Pick<PlanEditDraft, 'isSaving' | 'conflictingSectionIds'>>
): Record<string, PlanEditDraft> {
  const draft = planEdits[planId]
  if (!draft) return planEdits
  return { ...planEdits, [planId]: { ...draft, ...patch } }
}

function withoutPlanDraft(
  planEdits: Record<string, PlanEditDraft>,
  planId: string
): Record<string, PlanEditDraft> {
  if (!(planId in planEdits)) return planEdits
  const next = { ...planEdits }
  delete next[planId]
  return next
}

/**
 * `details.error` on the TERMINAL "plan left `draft`" 400.
 * Twin: `PLAN_NOT_EDITABLE_DETAILS` in `backend/src/handlers/plans.ts`.
 */
const PLAN_NOT_EDITABLE_CODE = 'plan_not_editable'

/** Single copy for the terminal-400 banner so the two branches can't drift. */
const PLAN_NOT_EDITABLE_MESSAGE =
  'This plan was approved or rejected elsewhere, so it can no longer be edited. Reload to see the latest.'

/**
 * Plan-card edit state is CONVERSATION state: a draft belongs to a `role:'plan'`
 * message, and the card treats "a draft exists" as "this card is in edit mode".
 * So EVERY path that replaces `messages` must clear it too — otherwise the
 * rebuilt card silently re-opens on a frozen fork carrying a stale
 * `baseVersion`, whose next save can only 409.
 *
 * Spread this into every such `set()`; grep the name to find them all. Returns a
 * fresh object per call so no two store states can alias one `planEdits` map.
 */
function clearedPlanEditState(): Pick<AgentStoreState, 'planEdits' | 'planConflict'> {
  return { planEdits: {}, planConflict: null }
}

/**
 * Reconcile drafts against a REBUILT message list, for the one path that
 * replaces `messages` without changing the conversation: `reconcileResumedTurn`.
 *
 * A draft is a fork of one specific `(planId, version)`. It survives only when
 * that exact card comes back — same plan, same version. If the card is gone
 * (approved, drafted, superseded) or its version moved while the resumed turn
 * was reconciling, the draft is a frozen fork whose only possible save is a 409,
 * so it goes.
 *
 * Note that "the planId still has a card" is NOT sufficient: the version moving
 * under the same id is precisely the frozen-fork case. The version comparison is
 * what makes this safe.
 *
 * The keep branch rests on a BACKEND invariant: **every live write to
 * `plan_json` bumps `version`**. Without it, content could move while the
 * version stood still and a stale draft would be kept as valid.
 *
 * Two live paths enforce it, and neither is the only one — don't assume the lock
 * is the whole story:
 *   • `updatePlanJsonWithLock` — `SET plan_json = $1, version = version + 1`
 *     under an optimistic-lock predicate (PATCH /plans, applyPatch).
 *   • `createOrReplaceDraft` — upserts with `version = response_plans.version + 1`
 *     on conflict. Bumps, but does NOT go through the lock.
 *
 * Two exported functions VIOLATE it and are only safe because they are
 * callerless V1 leftovers: `updatePlanningStatus` (`SET planning_status, plan_json`)
 * and `resetPlanning` (`SET … plan_json = '{}'`). Both write `plan_json` with no
 * version bump. If either gains a caller — or a new unbumped `SET plan_json` is
 * added — this predicate silently starts keeping stale drafts and must change.
 */
function reconcilePlanDrafts(
  planEdits: Record<string, PlanEditDraft>,
  messages: AgentChatMessage[]
): Record<string, PlanEditDraft> {
  const planIds = Object.keys(planEdits)
  if (planIds.length === 0) return planEdits

  const versionByPlanId = new Map<string, number>()
  for (const message of messages) {
    if (message.role === 'plan' && message.plan) {
      versionByPlanId.set(message.plan.planId, message.plan.version)
    }
  }

  const next: Record<string, PlanEditDraft> = {}
  for (const planId of planIds) {
    const draft = planEdits[planId]!
    if (versionByPlanId.get(planId) === draft.baseVersion) {
      next[planId] = draft
    }
  }
  return Object.keys(next).length === planIds.length ? planEdits : next
}

/** Is the plan card for `planId` still on screen? */
function hasPlanCard(messages: AgentChatMessage[], planId: string): boolean {
  return messages.some((m) => m.role === 'plan' && m.plan?.planId === planId)
}

/** Read `details` off an API error without an `instanceof` check (mock-safe). */
function errorDetails(error: unknown): Record<string, unknown> {
  const details = (error as { details?: unknown } | null | undefined)?.details
  return details && typeof details === 'object' ? (details as Record<string, unknown>) : {}
}

function flushPendingPlanMessage(): void {
  const message = pendingPlanMessage
  const turnDrafted = pendingPlanTurnDrafted
  const draftedPlan = pendingDraftedPlan
  clearPendingPlanMessage()
  // A pending card an earlier turn left on screen, for the plan this turn drafted from.
  if (draftedPlan) approveDraftedPlanCards(draftedPlan)
  if (!message) return
  const planMeta = message.plan
  if (!planMeta) return
  // The turn drafted from the plan it proposed, so the backend approved it:
  // show the same collapsed, read-only "Plan approved" card a reload puts after
  // this turn's reply (`insertApprovedPlanCards`). Never stale-checked — it is
  // a record, not a decision still waiting on the writer.
  if (draftedPlan && isDraftedPlanCard(planMeta, draftedPlan)) {
    useAgentStore.setState((state) => ({
      messages: [
        ...state.messages,
        {
          ...message,
          plan: { ...planMeta, status: 'accepted' as const, suggestions: [], collapsed: true },
        },
      ],
    }))
    return
  }
  // A draft already exists (this turn's, or an earlier one's), so "Accept &
  // draft" would only start a second run over it. Same rule as reload: a draft
  // plan with written sections gets no card.
  if (turnDrafted || hasDraftContent(useAgentStore.getState().segments)) return
  // Reload-safe staleness, computed at flush time — the brief can move mid-turn.
  const briefUpdatedAt = planMeta.sectionKey
    ? useUnitBriefStore.getState().getUpdatedAt(planMeta.sectionKey)
    : null
  const snapshotAt = planMeta.plan.briefSnapshotAt ?? null
  const isStale = Boolean(briefUpdatedAt && (!snapshotAt || briefUpdatedAt > snapshotAt))
  useAgentStore.setState((state) => ({
    messages: [...state.messages, message],
    ...(isStale
      ? { stalePlanIds: new Set(state.stalePlanIds).add(planMeta.planId) }
      : {}),
  }))
}

/**
 * Mirror a background fill run into the open workbook's review queue.
 *
 * `stage_cell_fills` emits `fill_proposals_staged` when it runs inside a sync
 * tool loop, but the `schedule_fill` WORKER stages straight into the database —
 * no stream, no event. Without this the headline flow (a long fill run) finishes
 * completely invisibly: the grid keeps showing whatever it had when the job was
 * handed off.
 *
 * Gated on the open schedule matching the session's artifact, so a fill run for
 * one workbook never repaints another, and a drafting job never refetches
 * proposals at all.
 */
function refreshScheduleProposalsIfOpen(): void {
  const artifactId = useAgentStore.getState().activeScope?.artifactId
  if (!artifactId) return
  const scheduleStore = useScheduleStore.getState()
  if (scheduleStore.artifactId === artifactId) void scheduleStore.refreshProposals()
  // The buyer Word form (R3) reviews the same fill proposals.
  const wordFormStore = useWordFormStore.getState()
  if (wordFormStore.artifactId === artifactId) void wordFormStore.refreshProposals()
}

/**
 * Rebuild the pending plan card from persisted session state on reload
 * (plan-card-ux §5b A2). Live, the card is deferred/appended by the event stream
 * and lost on refresh; here we reconstruct the same `role:'plan'` message from the
 * session GET so a reviewed-but-not-drafted plan survives a reload.
 *
 * Returns null unless the active plan is still a draft AND no draft segment has
 * content yet (the same existing-draft rule `flushPendingPlanMessage` applies live) — approved plans and
 * already-drafted sections get no card. The id is deterministic
 * (`plan-hydrated-<planId>-<version>`) so re-hydration replaces rather than
 * duplicates. Suggestions are event-only and legitimately do not survive reload.
 */
function buildHydratedPlanMessage(
  result: AgentSessionResponse,
  sectionKey: string
): AgentChatMessage | null {
  const planId = result.activePlanId
  const activePlan = result.activePlan
  if (!planId || !activePlan || result.activePlanStatus !== 'draft') {
    return null
  }
  const hasExistingDraft = Array.isArray(result.segments)
    && result.segments.some((segment) => (segment.content?.trim().length ?? 0) > 0)
  if (hasExistingDraft) {
    return null
  }
  // Prefer the plan ROW's version: it is what `PATCH /plans/{id}` compares
  // `expectedVersion` against. The session's `activePlanVersion` is a mirror
  // that can LAG the row — an inline edit whose session sync loses the session's
  // own optimistic lock leaves it behind (handlers/plans.ts warns and proceeds
  // by design). Hydrating from the mirror in that state makes the writer's very
  // next edit 409 on a change they made themselves; it self-heals through the
  // refresh affordance, but only after a round trip and a banner.
  const version = typeof result.activePlanRowVersion === 'number'
    ? result.activePlanRowVersion
    : typeof result.activePlanVersion === 'number'
      ? result.activePlanVersion
      : 0
  return {
    id: `plan-hydrated-${planId}-${version}`,
    role: 'plan',
    content: '',
    timestamp: new Date().toISOString(),
    plan: {
      planId,
      version,
      plan: activePlan as unknown as LivePlanJson,
      status: 'pending',
      sectionKey,
      suggestions: [],
    },
  }
}

/**
 * Same brief-updatedAt vs `briefSnapshotAt` staleness check as
 * `flushPendingPlanMessage`, applied to a hydrated plan card at append time.
 */
function isHydratedPlanCardStale(message: AgentChatMessage): boolean {
  const planMeta = message.plan
  if (!planMeta) return false
  const briefUpdatedAt = planMeta.sectionKey
    ? useUnitBriefStore.getState().getUpdatedAt(planMeta.sectionKey)
    : null
  const snapshotAt = planMeta.plan.briefSnapshotAt ?? null
  return Boolean(briefUpdatedAt && (!snapshotAt || briefUpdatedAt > snapshotAt))
}

/** The plan id a turn recorded as produced, or null (old turn / no plan). */
function producedPlanIdOf(turn: TurnHistoryEntry): string | null {
  const planId = turn.assistantMetadata?.producedPlan?.planId
  return typeof planId === 'string' && planId.length > 0 ? planId : null
}

function readApprovedPlans(
  raw: AgentSessionResponse['approvedPlans']
): Array<{ planId: string; version: number; plan: Record<string, unknown> }> {
  if (!Array.isArray(raw)) return []
  return raw.filter((entry) => (
    Boolean(entry)
    && typeof entry.planId === 'string'
    && entry.planId.length > 0
    && typeof entry.version === 'number'
    && Boolean(entry.plan)
    && typeof entry.plan === 'object'
  ))
}

/**
 * Put each approved plan back in the conversation as a collapsed, read-only
 * "Plan approved" card, directly after the reply of the LAST turn that produced
 * it (its `producedPlan` link) — where the live card sat. Falls back to after
 * that turn's user bubble when it saved no reply.
 *
 * Turns written before the link existed carry none, so their plan gets NO card:
 * a guessed position would put a plan beside the wrong exchange. Pending (draft)
 * plans are not placed here — `buildHydratedPlanMessage` still owns them — and
 * rejected plans never arrive (the session only sends approved ones).
 *
 * Ids are deterministic (`plan-approved-<planId>`) so a re-hydration replaces
 * rather than duplicates.
 */
function insertApprovedPlanCards(
  messages: AgentChatMessage[],
  result: AgentSessionResponse
): AgentChatMessage[] {
  const approvedPlans = readApprovedPlans(result.approvedPlans)
  const turns: TurnHistoryEntry[] = Array.isArray(result.turnHistory) ? result.turnHistory : []
  if (approvedPlans.length === 0 || turns.length === 0) return messages

  const cardsByAnchorId = new Map<string, AgentChatMessage[]>()
  for (const approved of approvedPlans) {
    const producingTurn = [...turns].reverse().find((turn) => producedPlanIdOf(turn) === approved.planId)
    if (!producingTurn) continue
    const anchor = messages.find((m) => m.id === `${producingTurn.turnId}:assistant`)
      ?? messages.find((m) => m.id === `${producingTurn.turnId}:user`)
    if (!anchor) continue
    const card: AgentChatMessage = {
      id: `plan-approved-${approved.planId}`,
      role: 'plan',
      content: '',
      timestamp: anchor.timestamp,
      plan: {
        planId: approved.planId,
        version: approved.version,
        plan: approved.plan as unknown as LivePlanJson,
        status: 'accepted',
        sectionKey: result.sectionKey,
        suggestions: [],
        collapsed: true,
      },
    }
    cardsByAnchorId.set(anchor.id, [...(cardsByAnchorId.get(anchor.id) ?? []), card])
  }
  if (cardsByAnchorId.size === 0) return messages
  return messages.flatMap((message) => [message, ...(cardsByAnchorId.get(message.id) ?? [])])
}

/**
 * Rebuild the plan cards a hydrated conversation lost: approved plans go back
 * in place, collapsed (`insertApprovedPlanCards`); the pending plan card (if
 * any) is appended to the END and reported when stale. Pure — the caller merges
 * the returned `stalePlanId` into `stalePlanIds` inside its own `set`.
 */
function appendHydratedPlanCard(
  baseMessages: AgentChatMessage[],
  result: AgentSessionResponse
): { messages: AgentChatMessage[]; stalePlanId: string | null } {
  const withApprovedCards = insertApprovedPlanCards(baseMessages, result)
  const planMessage = buildHydratedPlanMessage(result, result.sectionKey)
  if (!planMessage) {
    return { messages: withApprovedCards, stalePlanId: null }
  }
  return {
    messages: [...withApprovedCards, planMessage],
    stalePlanId: isHydratedPlanCardStale(planMessage) ? planMessage.plan!.planId : null,
  }
}

function isTransientStatusMessage(message: AgentChatMessage): boolean {
  return message.role === 'status' && (message.eventType === 'status' || message.eventType === undefined)
}

function clearStatusTimer(messageId: string): void {
  const timers = statusTimers.get(messageId)
  if (!timers) return
  clearTimeout(timers.fadeTimer)
  clearTimeout(timers.removeTimer)
  statusTimers.delete(messageId)
}

function clearAllStatusTimers(): void {
  for (const messageId of statusTimers.keys()) {
    clearStatusTimer(messageId)
  }
}

function clearProgressRecapTimer(): void {
  if (!progressRecapTimer) return
  clearTimeout(progressRecapTimer)
  progressRecapTimer = null
}

function makeUnitScope(artifactId: string, sectionKey: string): Scope {
  return {
    kind: 'unit',
    artifactId,
    unitId: sectionKey,
  }
}

function makeArtifactScope(artifactId: string): Scope {
  return {
    kind: 'artifact',
    artifactId,
  }
}

function serializeScope(scope: Scope): string {
  return `${scope.kind}::${scope.artifactId}::${scope.unitId ?? ''}`
}

function getExpectedRevisionFromSegments(scope: Scope | null, segments: DraftSegment[]): string | null {
  if (!scope) return null
  if (scope.kind === 'unit') {
    const segment = segments.find((item) => item.sectionId === scope.unitId)
    return segment ? String(segment.version) : null
  }

  // Single-document artifacts use a virtual document segment key.
  const documentSegment = segments.find((item) => item.sectionId === DOCUMENT_SECTION_KEY)
  if (documentSegment) return String(documentSegment.version)

  // Multi-section artifact revisions can advance on any section; use the
  // highest known segment version as the optimistic-lock token.
  const maxVersion = segments.reduce((max, segment) => {
    if (typeof segment.version !== 'number' || !Number.isFinite(segment.version)) return max
    return Math.max(max, segment.version)
  }, Number.NEGATIVE_INFINITY)

  return Number.isFinite(maxVersion) ? String(maxVersion) : null
}

/**
 * The token an agent turn's revision guard compares against.
 *
 * Unit scope: the section's segment version — the stored `expectedRevision`.
 * Artifact scope (single document): the artifact's OUTLINE version. The
 * backend reads `bid_artifacts.outline_version` for artifact scope, not the
 * document segment's content version, so the segment-derived stored token
 * missed on every send and only the conflict auto-retry got the turn through.
 * The drafting store holds the outline of the page the panel sits on and keeps
 * its version current through outline edits, so read it at send time. When it
 * is not loaded for this artifact, fall back to the stored token and let the
 * conflict retry cover the gap.
 */
function resolveTurnRevision(scope: Scope, storedRevision: string | null): string | null {
  if (scope.kind !== 'artifact') return storedRevision
  const drafting = useDraftingStore.getState()
  if (
    drafting.currentArtifactId === scope.artifactId
    && drafting.outline
    && !drafting.isLoadingOutline
    && Number.isFinite(drafting.outlineVersion)
    && drafting.outlineVersion > 0
  ) {
    return String(drafting.outlineVersion)
  }
  return storedRevision
}

function resolveExpectedRevisionFromSession(
  scope: Scope,
  session: AgentSessionResponse,
  segments: DraftSegment[]
): string | null {
  const scopedRevision = getExpectedRevisionFromSegments(scope, segments)

  // For unit scope, only send optimistic-lock tokens sourced from the unit segment itself.
  // Session-level counters can be 0 or represent unrelated revision domains.
  if (scope.kind === 'unit') return scopedRevision

  const numericCandidates: number[] = []
  const scopedNumeric = scopedRevision !== null && Number.isFinite(Number(scopedRevision))
    ? Number(scopedRevision)
    : null
  if (scopedNumeric !== null && scopedNumeric >= 0) numericCandidates.push(scopedNumeric)
  if (
    typeof session.activeDraftVersion === 'number'
    && Number.isFinite(session.activeDraftVersion)
    && session.activeDraftVersion >= 0
  ) {
    numericCandidates.push(session.activeDraftVersion)
  }
  if (
    typeof session.activePlanVersion === 'number'
    && Number.isFinite(session.activePlanVersion)
    && session.activePlanVersion >= 0
  ) {
    numericCandidates.push(session.activePlanVersion)
  }
  if (numericCandidates.length > 0) {
    return String(Math.max(...numericCandidates))
  }

  return scopedRevision
}

function getUserFacingTurnError(code: string | undefined, message: string): string {
  if (code === 'precondition_required' && /\bexpectedrevision\b/i.test(message)) {
    return "Couldn't apply that because the draft changed. Refresh and try again."
  }

  if (code === 'revision_conflict') {
    return 'This content was updated. Get latest draft and retry.'
  }

  if (code === 'TURN_EXECUTION_ERROR' && /unexpected error occurred/i.test(message)) {
    return 'The agent hit a temporary issue. Please try again.'
  }

  // SESSION_BUSY carries `retryable: false`, so "try again" is the one thing
  // that cannot work — the previous turn has to finish first. Says nothing
  // about *which* tab: the common case is this one, still finishing an async
  // draft.
  if (code === 'SESSION_BUSY') {
    return 'Still finishing your previous request — send this once it lands.'
  }

  // Access gates (Specs/security-hardening-spec.md Part 3).
  if (code === 'FORBIDDEN') {
    return "You don't have access to this bid."
  }
  if (code === 'ORG_DEACTIVATED') {
    return 'Your organisation is deactivated. Contact your administrator.'
  }

  return message
}

function isRevisionPreconditionError(code: string | undefined, message: string): boolean {
  if (code === 'revision_conflict') return true
  return code === 'precondition_required' && /\bexpectedrevision\b/i.test(message)
}

type PersistedCitationEntry = {
  documentId?: unknown
  documentName?: unknown
  pageNumber?: unknown
  snippet?: unknown
  quote?: unknown
  highlightData?: unknown
}

type TurnHistoryEntry = {
  turnId: string
  threadId?: string | null
  userMessage: string
  assistantResponse: string | null
  assistantMetadata?: {
    suggestedActions?: unknown
    activitySteps?: PersistedActivityStep[]
    citations?: PersistedCitationEntry[]
    libraryCitations?: PersistedCitationEntry[]
    /** 'saved' when the turn wrote a draft — see `AgentChatMessage.citationLabels`. */
    citationLabels?: unknown
    /** The plan this turn produced — see `insertApprovedPlanCards`. */
    producedPlan?: { planId?: unknown; version?: unknown } | null
    /** 'job_recap' on the recap a document job writes when it ends. */
    kind?: unknown
  } | null
  attachments?: Array<{
    attachmentId: string
    filename: string
    mimeType: string
    size: number
  }>
  createdAt: string
  completedAt: string | null
}

function hydrateCitationSources(
  raw: PersistedCitationEntry[] | undefined
): AgentCitationSource[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const sources = raw
    .map((entry): AgentCitationSource | null => {
      if (!entry || typeof entry !== 'object') return null
      const documentId = entry.documentId
      const documentName = entry.documentName
      if (typeof documentId !== 'string' || typeof documentName !== 'string') return null
      const pageNumber = typeof entry.pageNumber === 'number' ? entry.pageNumber : null
      const source: AgentCitationSource = { documentId, documentName, pageNumber }
      if (typeof entry.snippet === 'string') source.snippet = entry.snippet
      if (typeof entry.quote === 'string') source.quote = entry.quote
      if (entry.highlightData) source.highlightData = entry.highlightData as SourceHighlightData
      return source
    })
    .filter((source): source is AgentCitationSource => source !== null)
  return sources.length > 0 ? sources : undefined
}

function parseSuggestedActionOffer(raw: unknown): SuggestedActionOffer | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const offerId = record['offerId']
  const sourceTurnId = record['sourceTurnId']
  const recommendedActionId = record['recommendedActionId']
  const expiresAt = record['expiresAt']
  const actions = record['actions']

  if (
    typeof offerId !== 'string'
    || typeof sourceTurnId !== 'string'
    || typeof recommendedActionId !== 'string'
    || typeof expiresAt !== 'string'
    || !Array.isArray(actions)
  ) {
    return null
  }

  const normalizedActions = actions
    .map((action) => {
      if (!action || typeof action !== 'object') return null
      const actionRecord = action as Record<string, unknown>
      const actionId = actionRecord['actionId']
      const label = actionRecord['label']
      const utterance = actionRecord['utterance']
      if (
        typeof actionId !== 'string'
        || typeof label !== 'string'
        || typeof utterance !== 'string'
      ) {
        return null
      }
      return { actionId, label, utterance }
    })
    .filter((action): action is SuggestedAction => action !== null)

  if (normalizedActions.length === 0) return null

  const expiresAtMs = Date.parse(expiresAt)
  if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) {
    return null
  }

  return {
    offerId,
    sourceTurnId,
    recommendedActionId,
    expiresAt,
    actions: normalizedActions,
  }
}

function hydrateSuggestedOfferFromTurnHistory(
  turnHistory: TurnHistoryEntry[] | undefined,
  activeThreadId: string | null
): SuggestedActionOffer | null {
  if (!Array.isArray(turnHistory) || turnHistory.length === 0) return null

  for (let idx = turnHistory.length - 1; idx >= 0; idx -= 1) {
    const turn = turnHistory[idx]
    if (!turn) continue
    const threadId = turn.threadId ?? activeThreadId
    if (!threadId || (activeThreadId && threadId !== activeThreadId)) continue
    const offer = parseSuggestedActionOffer(turn.assistantMetadata?.suggestedActions)
    // Only consider the most recent turn in the active thread.
    // Don't fall back to older turns whose suggestions are stale
    // (e.g. after async draft generation completes without new suggestions).
    if (!offer) return null
    if (offer.sourceTurnId !== turn.turnId) return null
    return offer
  }

  return null
}

/**
 * A deliberately narrow subset that the server's ensureHtml leaves unchanged.
 * Markdown, mixed rows, attributes, unsafe tags and stray "<" need server
 * normalization; do not guess their equivalence with the frontend converter
 * (which strips citations). Unknown content remains reviewable.
 */
function isKnownSuggestionHtml(content: string): boolean {
  const trimmed = content.trim()
  if (!/^<(?:p|h[1-6]|ul|ol|blockquote|table)\s*>/i.test(trimmed)) return false
  return !trimmed.replace(
    /<\/?(?:p|h[1-6]|ul|ol|li|blockquote|table|thead|tbody|tfoot|tr|th|td|caption|hr|br|a|strong|b|em|i|u|s|strike|span|sub|sup|code|pre)\s*\/?>/gi,
    '',
  ).includes('<')
}

/** Only authoritative newer text on the exact proposal row proves it outdated. */
function isHydratedSuggestionOutdated(
  pending: PendingDraftProposal,
  segments: ReadonlyArray<DraftSegment>,
): boolean {
  if (pending.mode !== 'staged' || !pending.segmentId) return false
  const current = segments.find((segment) => segment.id === pending.segmentId)
  const baseVersion = pending.baseSegmentVersion
  if (!current || typeof baseVersion !== 'number'
    || !Number.isSafeInteger(baseVersion) || baseVersion < 0
    || !Number.isSafeInteger(current.version) || current.version < 0
    || current.version <= baseVersion) return false
  // The server accepts already-saved equivalent text even after the version
  // advances. Preserve that path, and leave ambiguous legacy text to it.
  return current.content.trim() !== pending.after.trim()
    && isKnownSuggestionHtml(current.content)
    && isKnownSuggestionHtml(pending.after)
}

/**
 * Rebuild the review-strip state from the session's pending proposal. Shape is
 * identical to what the live draft_patch_proposed + draft_patch_applied pair
 * produces, so accept/reject run unchanged.
 */
function hydrateDraftPatchPreview(
  pending: PendingDraftProposal | null | undefined,
  segments: ReadonlyArray<DraftSegment>,
): Pick<
  AgentStoreState,
  'draftPatchPreview' | 'writingWorkflowState' | 'pendingMutationKind'
> {
  if (!pending) {
    return { draftPatchPreview: null, writingWorkflowState: 'idle', pendingMutationKind: null }
  }
  const staged = pendingSuggestionFromProposal(pending)
  return {
    draftPatchPreview: {
      proposalId: pending.proposalId,
      sectionId: pending.sectionId,
      turnId: pending.turnId,
      before: pending.before,
      after: pending.after,
      appliedPatch: { affectedSectionIds: [pending.sectionId] },
      hunkDecisions: {},
      // R1: a staged suggestion is shown over the section's current text and
      // its markers resolve against its own refs. Legacy rows carry no mode.
      ...(staged
        ? {
            mode: 'staged' as const,
            baseSegmentVersion: staged.baseSegmentVersion,
            afterCitationRefs: staged.afterCitationRefs,
            afterLibraryCitationRefs: staged.afterLibraryCitationRefs,
            ...(isHydratedSuggestionOutdated(pending, segments) ? { outdated: true } : {}),
          }
        : {}),
    },
    writingWorkflowState: 'awaiting_approval',
    pendingMutationKind: 'revising',
  }
}

/**
 * Feed the per-document suggestion list from a session load (R1 W2). Prefers
 * the backend's full list; an older backend only sends the one pending
 * proposal, which is listed when it is a staged suggestion.
 */
function syncPendingSuggestions(result: Pick<AgentSessionResponse, 'pendingProposal' | 'pendingSuggestions'>): void {
  const list: PendingSuggestion[] = Array.isArray(result.pendingSuggestions)
    ? result.pendingSuggestions
      .map((pending) => pendingSuggestionFromProposal({ ...pending, mode: pending.mode ?? 'staged' }))
      .filter((suggestion): suggestion is PendingSuggestion => suggestion !== null)
    : [pendingSuggestionFromProposal(result.pendingProposal)]
      .filter((suggestion): suggestion is PendingSuggestion => suggestion !== null)
  useAiEditsStore.getState().setSuggestions(list)
}

/**
 * Insert the chat-side "Review in editor" stub for a rehydrated proposal
 * directly after the assistant message of the turn that proposed it. No stub
 * when that turn is not in this thread — the editor strip is the decision
 * point regardless.
 */
function appendPendingProposalStub(
  messages: AgentChatMessage[],
  pending: PendingDraftProposal | null | undefined,
  segments: ReadonlyArray<{ sectionId: string; heading?: string }>,
): AgentChatMessage[] {
  if (!pending?.turnId) return messages
  const anchorIndex = messages.findIndex((m) => m.id === `${pending.turnId}:assistant`)
  if (anchorIndex === -1) return messages
  const sectionName = segments.find((s) => s.sectionId === pending.sectionId)?.heading
  const stub: AgentChatMessage = {
    id: `${pending.turnId}:proposal`,
    role: 'proposal',
    content: '',
    timestamp: messages[anchorIndex]?.timestamp ?? new Date().toISOString(),
    proposal: {
      proposalId: pending.proposalId,
      sectionId: pending.sectionId,
      ...(sectionName ? { sectionName } : {}),
      status: 'pending',
    },
  }
  return [...messages.slice(0, anchorIndex + 1), stub, ...messages.slice(anchorIndex + 1)]
}

function hydrateMessagesFromTurnHistory(
  turnHistory: TurnHistoryEntry[] | undefined
): AgentChatMessage[] {
  if (!Array.isArray(turnHistory) || turnHistory.length === 0) {
    return []
  }

  const messages: AgentChatMessage[] = []

  for (const [index, turn] of turnHistory.entries()) {
    // A turn that produced no assistant response, immediately followed by a
    // retry carrying the identical user message, would render as two identical
    // user bubbles — show only the retry's.
    const next = turnHistory[index + 1]
    const isOrphanedDuplicate =
      !(typeof turn.assistantResponse === 'string' && turn.assistantResponse.trim().length > 0)
      && typeof turn.userMessage === 'string'
      && next?.userMessage === turn.userMessage
    if (isOrphanedDuplicate) continue

    const isCarrySummarySeed = turn.userMessage === CARRY_FORWARD_SEED_PROMPT
    if (isCarrySummarySeed) {
      if (typeof turn.assistantResponse === 'string' && turn.assistantResponse.trim().length > 0) {
        messages.push({
          id: `${turn.turnId}:system`,
          role: 'system',
          content: turn.assistantResponse,
          timestamp: turn.completedAt ?? turn.createdAt,
          intent: 'write',
        })
      }
      continue
    }

    if (typeof turn.userMessage === 'string' && turn.userMessage.trim().length > 0) {
      messages.push({
        id: `${turn.turnId}:user`,
        role: 'user',
        // R1 UX1: an answer to one of the AI's questions reads as it did live.
        content: displayTurnUserMessage(turn.userMessage),
        timestamp: turn.createdAt,
        intent: 'write',
        ...(turn.attachments && turn.attachments.length > 0
          ? { attachments: turn.attachments }
          : {}),
      })
    }
    if (typeof turn.assistantResponse === 'string' && turn.assistantResponse.trim().length > 0) {
      const persistedSteps = turn.assistantMetadata?.activitySteps
      const sources = hydrateCitationSources(turn.assistantMetadata?.citations)
      const librarySources = hydrateCitationSources(turn.assistantMetadata?.libraryCitations)
      messages.push({
        id: `${turn.turnId}:assistant`,
        role: 'assistant',
        // A document job's recap: its opening paragraph only. Its lists (what
        // the review left open, the questions) live on the document overview.
        content: turn.assistantMetadata?.kind === 'job_recap'
          ? recapOpeningParagraph(turn.assistantResponse)
          : turn.assistantResponse,
        timestamp: turn.completedAt ?? turn.createdAt,
        intent: 'write',
        ...(persistedSteps && persistedSteps.length > 0 ? { persistedSteps } : {}),
        ...(sources ? { sources } : {}),
        ...(librarySources ? { librarySources } : {}),
        ...(turn.assistantMetadata?.citationLabels === 'saved' ? { citationLabels: 'saved' as const } : {}),
      })
    }
  }

  return messages
}

function toChatPreviewText(input: string, maxLength = 96): string {
  const normalized = input.replace(/\s+/g, ' ').trim()
  if (!normalized) return ''
  const userFriendly = normalized
    .replace(/\bthreads\b/gi, 'chats')
    .replace(/\bthread\b/gi, 'chat')
  if (userFriendly.length <= maxLength) return userFriendly
  return `${userFriendly.slice(0, maxLength - 1)}…`
}

function hydrateThreadPreviewsFromTurnHistory(
  turnHistory: TurnHistoryEntry[] | undefined,
  activeThreadId: string | null
): Record<string, string> {
  if (!Array.isArray(turnHistory) || turnHistory.length === 0) {
    return {}
  }

  const previews: Record<string, string> = {}

  for (const turn of turnHistory) {
    const threadId = turn.threadId ?? activeThreadId
    if (!threadId || previews[threadId]) continue

    const isCarrySummarySeed = turn.userMessage === CARRY_FORWARD_SEED_PROMPT
    if (!isCarrySummarySeed) {
      const userPreview = toChatPreviewText(displayTurnUserMessage(turn.userMessage))
      if (userPreview) {
        previews[threadId] = userPreview
        continue
      }
    }

    const assistantPreview = toChatPreviewText(turn.assistantResponse ?? '')
    if (assistantPreview) {
      previews[threadId] = assistantPreview
    }
  }

  return previews
}

const RESOLUTION_NOT_RECORDED = 'Your decision was applied but not recorded — the review bar may reappear when you return to this section.'

/**
 * Record an accept/reject against the backend. Awaited (not fire-and-forget) so
 * a failure can tell the user why the strip might come back — the local state
 * has already been updated optimistically either way.
 */
async function recordResolutionOrWarn(
  sessionId: string,
  proposalId: string,
  decision: 'accepted' | 'rejected',
  setError: (message: string) => void,
): Promise<void> {
  try {
    await resolveProposal(sessionId, proposalId, decision)
  } catch {
    setError(RESOLUTION_NOT_RECORDED)
  }
}

function estimateWordCountFromHtml(html: string): number {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  if (!text) return 0
  return text.split(' ').length
}

function getWorkflowStateFromStatus(message: string): WritingWorkflowState | null {
  const normalized = message.trim().toLowerCase()
  if (normalized.includes('clarifying')) return 'clarifying'
  if (normalized.includes('writing draft')) return 'drafting'
  if (normalized.includes('applying revisions')) return 'revising'
  return null
}

function getProgressPhaseFromStatus(message: string): ProgressPhase | null {
  const normalized = message.trim().toLowerCase()
  if (normalized.includes('writing draft') || normalized.includes('drafting')) return 'drafting'
  if (normalized.includes('applying revisions') || normalized.includes('revising')) return 'revising'
  if (normalized.includes('verification') || normalized.includes('coverage')) return 'verifying'
  if (normalized.includes('clarifying') || normalized.includes('processing')) return 'discovering'
  return null
}

function getProgressPhaseStatusText(phase: ProgressPhase | null): string {
  switch (phase) {
    case 'searching_evidence':
      return 'Searching evidence'
    case 'reviewing_evidence':
      return 'Reviewing evidence'
    case 'drafting':
      return 'Drafting content'
    case 'revising':
      return 'Revising content'
    case 'background':
      return 'Working in background'
    case 'verifying':
      return 'Verifying output'
    case 'finalizing':
      return 'Finalising response'
    case 'discovering':
    default:
      return 'Understanding request'
  }
}

function getEvidenceDetailFromStatus(message: string): string | null {
  const normalized = message.trim().toLowerCase()
  if (normalized.includes('no matching evidence found')) return 'No matching evidence found.'
  if (normalized.includes('using basic search ranking')) return 'Broadening search.'
  if (normalized.includes('using keyword search only')) return 'Broadening search.'
  return null
}

function getEvidenceDetailFromCompletion(
  retrievalStatus: unknown,
  fallbackUsed: unknown
): string | null {
  if (retrievalStatus === 'empty') return 'No matching evidence found.'
  if (retrievalStatus === 'partial') return 'Expanded search beyond selected evidence.'
  if (retrievalStatus === 'weak') return 'Weak match — the closest evidence was only loosely related.'
  if (fallbackUsed === true) return 'Broadening search.'
  return null
}

// The background-draft copy below (this function, `buildStoppedDraftMessage`,
// `buildPartialFailureNote`, and `cancelTurn`'s Stop line) is mirrored in
// backend/src/lib/worker-turn-closing.ts, which stores it on the turn so a
// reload says what the live panel said. Change one, change both.
function buildAsyncCompletionMessage(payload: Record<string, unknown> | null): string {
  if (!payload) {
    return 'Background drafting finished.'
  }

  const generatedIds = payload['generatedSectionIds']
  const skippedIds = payload['skippedSectionIds']
  const generatedCount = Array.isArray(generatedIds) ? generatedIds.length : 0
  const skippedCount = Array.isArray(skippedIds) ? skippedIds.length : 0

  if (generatedCount > 0) {
    const sectionLabel = generatedCount === 1 ? 'section' : 'sections'
    return `Background drafting finished. Updated ${generatedCount} ${sectionLabel}.`
  }

  if (skippedCount > 0) {
    const sectionLabel = skippedCount === 1 ? 'section was' : 'sections were'
    return `Background drafting finished, but ${skippedCount} ${sectionLabel} skipped due to concurrent updates.`
  }

  return 'Background drafting finished, but no section updates were needed.'
}

function readSectionCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value)
  }
  return null
}

/**
 * Copy for a background draft that stopped early. Sections are persisted one at
 * a time, so a stop leaves the ones already written in place — telling the
 * writer "no further changes applied" was simply untrue. Only claim nothing
 * changed when the payload says zero sections landed; stay neutral when an
 * older backend sends no counts at all.
 */
export function buildStoppedDraftMessage(payload: Record<string, unknown> | null | undefined): string {
  const completed = readSectionCount(payload?.['completedSections'])
  const total = readSectionCount(payload?.['totalSections'])
  if (completed === null) return 'Stopped drafting.'
  if (completed <= 0) return 'Stopped drafting. No sections were written, so nothing changed.'
  const verb = completed === 1 ? 'was' : 'were'
  if (total !== null && total > 0) {
    return `Stopped. ${completed} of ${total} sections ${verb} already drafted before the stop, and they're in the editor.`
  }
  const noun = completed === 1 ? 'section' : 'sections'
  const pronoun = completed === 1 ? "it's" : "they're"
  return `Stopped. ${completed} ${noun} ${verb} already drafted before the stop, and ${pronoun} in the editor.`
}

/**
 * Copy appended to a background failure that still wrote some sections. Without
 * it the writer sees only the raw error and assumes the run produced nothing.
 */
export function buildPartialFailureNote(payload: Record<string, unknown> | null | undefined): string | null {
  const completed = readSectionCount(payload?.['completedSections'])
  if (completed === null || completed <= 0) return null
  const total = readSectionCount(payload?.['totalSections'])
  const verb = completed === 1 ? 'was' : 'were'
  if (total !== null && total > 0) {
    return `${completed} of ${total} sections ${verb} drafted before it stopped, and they're in the editor.`
  }
  const noun = completed === 1 ? 'section' : 'sections'
  const pronoun = completed === 1 ? "it's" : "they're"
  return `${completed} ${noun} ${verb} drafted before it stopped, and ${pronoun} in the editor.`
}

// ===================
// ID generator
// ===================

function nextMsgId(): string {
  return crypto.randomUUID()
}

const QUOTED_TEXT_MAX_LENGTH = 240

/**
 * Trim a selection snippet to a length that reads cleanly in the chat thread
 * without dominating the message bubble. The middle is collapsed so both ends
 * remain visible — useful when the user selected a paragraph.
 */
function truncateQuotedText(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim()
  if (collapsed.length <= QUOTED_TEXT_MAX_LENGTH) return collapsed
  const head = collapsed.slice(0, QUOTED_TEXT_MAX_LENGTH / 2 - 1)
  const tail = collapsed.slice(collapsed.length - QUOTED_TEXT_MAX_LENGTH / 2 + 1)
  return `${head}…${tail}`
}

/**
 * Flip a proposal chat message's status without disturbing other messages.
 * Used when the user accepts/rejects from either the editor inline diff
 * controls or the proposal stub card in the chat — both routes converge here.
 */
function updateProposalMessageStatus(
  messages: AgentChatMessage[],
  proposalId: string,
  status: ProposalChatStatus,
): AgentChatMessage[] {
  let changed = false
  const next = messages.map((m) => {
    if (m.role !== 'proposal' || m.proposal?.proposalId !== proposalId) return m
    if (m.proposal.status === status) return m
    changed = true
    return { ...m, proposal: { ...m.proposal, status } }
  })
  return changed ? next : messages
}

// ===================
// Store
// ===================

export const useAgentStore = create<AgentStoreState>()(
  devtools(
    (set, get) => {
      /**
       * Accept or reject a STAGED suggestion (R1 W2). The section was never
       * touched, so nothing is restored on reject, and accept saves on the
       * server (compare-and-set on the version the suggestion was made
       * against). A refused accept leaves the strip up marked outdated, over
       * the section's newer text, until the user dismisses it.
       */
      const resolveStagedPreview = async (
        preview: DraftPatchPreview,
        decision: 'accepted' | 'rejected',
      ): Promise<void> => {
        const sessionId = get().sessionId
        if (!sessionId || get().isResolvingDraftPatchPreview) return
        // An outdated suggestion can only be dismissed.
        if (decision === 'accepted' && preview.outdated) return

        const mergedHtml = decision === 'accepted' ? stagedMergedHtml(preview, get().segments) : undefined
        set({ isResolvingDraftPatchPreview: true, error: null })
        const outcome = await decideStagedSuggestion(sessionId, preview.proposalId, decision, mergedHtml)
        const clearPreview = {
          draftPatchPreview: null,
          isResolvingDraftPatchPreview: false,
          writingWorkflowState: 'idle' as const,
          pendingMutationKind: null,
        }

        switch (outcome.kind) {
          case 'accepted': {
            const saved = outcome.segment
            set((state) => ({
              ...clearPreview,
              segments: saved
                ? state.segments.map((segment) => (
                  segment.sectionId === preview.sectionId || segment.sectionId === saved.sectionId
                    ? {
                      ...segment,
                      content: saved.content,
                      version: saved.version,
                      status: saved.status,
                      wordCount: saved.wordCount,
                      // The server saved the suggestion's own refs with its text.
                      ...(Array.isArray(preview.afterCitationRefs) ? { citationRefs: preview.afterCitationRefs } : {}),
                      ...(Array.isArray(preview.afterLibraryCitationRefs)
                        ? { libraryCitationRefs: preview.afterLibraryCitationRefs }
                        : {}),
                    }
                    : segment
                ))
                : state.segments,
              ...(saved && state.activeScope?.kind === 'unit' ? { expectedRevision: String(saved.version) } : {}),
              error: null,
              messages: updateProposalMessageStatus(state.messages, preview.proposalId, 'accepted'),
            }))
            useAiEditsStore.getState().removeSuggestion(preview.proposalId)
            return
          }
          case 'rejected':
            set((state) => ({
              ...clearPreview,
              error: null,
              messages: updateProposalMessageStatus(state.messages, preview.proposalId, 'rejected'),
            }))
            useAiEditsStore.getState().removeSuggestion(preview.proposalId)
            return
          case 'outdated': {
            const { currentVersion, currentContent } = outcome
            set((state) => ({
              draftPatchPreview: state.draftPatchPreview?.proposalId === preview.proposalId
                ? { ...state.draftPatchPreview, outdated: true }
                : state.draftPatchPreview,
              isResolvingDraftPatchPreview: false,
              segments: currentVersion !== null && currentContent !== null
                ? state.segments.map((segment) => (
                  segment.sectionId === preview.sectionId && segment.version < currentVersion
                    ? { ...segment, content: currentContent, version: currentVersion }
                    : segment
                ))
                : state.segments,
              ...(currentVersion !== null && state.activeScope?.kind === 'unit'
                ? { expectedRevision: String(currentVersion) }
                : {}),
            }))
            return
          }
          case 'gone':
            set({ ...clearPreview, error: SUGGESTION_SECTION_GONE })
            useAiEditsStore.getState().removeSuggestion(preview.proposalId)
            return
          case 'already_accepted':
            set((state) => ({
              ...clearPreview,
              error: SUGGESTION_ALREADY_ACCEPTED,
              messages: updateProposalMessageStatus(state.messages, preview.proposalId, 'accepted'),
            }))
            useAiEditsStore.getState().removeSuggestion(preview.proposalId)
            return
          case 'failed':
            set({
              isResolvingDraftPatchPreview: false,
              error: outcome.message
                ?? (decision === 'accepted' ? SUGGESTION_ACCEPT_FAILED : SUGGESTION_REJECT_FAILED),
            })
            return
        }
      }

      const clearTransientStatusMessages = () => {
        clearAllStatusTimers()
        set((state) => ({
          messages: state.messages.filter((m) => !isTransientStatusMessage(m)),
        }))
      }

      const showInlineTransientStatus = (content: string, timestamp = new Date().toISOString()) => {
        if (!content.trim()) return
        const stateSnapshot = get()
        if (!stateSnapshot.isStreaming && !stateSnapshot.isPolling && !stateSnapshot.isCancellable) return

        clearTransientStatusMessages()

        const statusId = nextMsgId()
        set((state) => ({
          ...(() => {
            const streamingMessage = state.streamingMessageId
              ? state.messages.find((m) => m.id === state.streamingMessageId)
              : null
            const messagesWithoutStreaming = state.streamingMessageId
              ? state.messages.filter((m) => m.id !== state.streamingMessageId)
              : state.messages
            return {
              messages: [
                ...messagesWithoutStreaming,
                {
                  id: statusId,
                  role: 'status' as const,
                  content,
                  timestamp,
                  eventType: 'status',
                  expiresAt: Date.now() + STATUS_TTL_MS,
                  isExiting: false,
                },
                ...(streamingMessage ? [streamingMessage] : []),
              ],
            }
          })(),
        }))

        const fadeTimer = setTimeout(() => {
          set((state) => ({
            messages: state.messages.map((m) =>
              m.id === statusId ? { ...m, isExiting: true } : m
            ),
          }))
        }, STATUS_VISIBLE_MS)

        const removeTimer = setTimeout(() => {
          clearStatusTimer(statusId)
          set((state) => ({
            messages: state.messages.filter((m) => m.id !== statusId),
          }))
        }, STATUS_TTL_MS)

        statusTimers.set(statusId, { fadeTimer, removeTimer })
      }

      const appendDurableStatusMessage = (content: string, eventType: string) => {
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id: nextMsgId(),
              role: 'status' as const,
              content,
              timestamp: new Date().toISOString(),
              eventType,
              durable: true,
            },
          ],
        }))
      }

      /**
       * A background run that failed or was cancelled part-way still persisted
       * every section it got through. The worker returns those on the failed and
       * cancelled payloads too, so apply them exactly as the completed branch
       * does — otherwise the written sections stay invisible until a reload and
       * the writer re-runs work that is already done.
       */
      const applyPartialSegmentsFromPayload = (
        payload: Record<string, unknown> | null | undefined
      ) => {
        if (!payload) return
        const segs = payload['segments']
        const order = payload['planSectionOrder']
        if (Array.isArray(segs)) {
          const typedSegs = segs as DraftSegment[]
          set((state) => ({
            segments: typedSegs,
            expectedRevision: getExpectedRevisionFromSegments(state.activeScope, typedSegs)
              ?? state.expectedRevision,
          }))
        }
        if (Array.isArray(order)) {
          set({ planSectionOrder: order as string[] })
        }
        const scope = get().activeScope
        if (Array.isArray(segs) && scope?.artifactId) {
          void useSectionAssignmentsStore.getState().fetchAssignments(scope.artifactId)
        }
      }

      const showProgressRecap = (kind: ProgressRecapState['kind'], message: string) => {
        clearProgressRecapTimer()
        const expiresAt = Date.now() + PROGRESS_RECAP_VISIBLE_MS
        set({
          progressRecap: {
            message,
            kind,
            expiresAt,
          },
        })
        progressRecapTimer = setTimeout(() => {
          set({ progressRecap: null })
          progressRecapTimer = null
        }, PROGRESS_RECAP_VISIBLE_MS)
      }

      /**
       * Poll a background job to its end and land the outcome in the panel.
       *
       * One path for every way a job reaches this view: started here
       * (`async_handoff`), re-attached after a remount on the same section
       * (`resumeBackgroundJobIfAny`), or found still running when a section is
       * re-opened mid-turn (`initSession`). Callers set the pre-poll state
       * (`isPolling`, `asyncJobId`, the progress card); this owns progress, the
       * closing message and the activity rows. Before it was shared, the
       * re-attach path never rendered a closing message, and no path settled
       * the activity rows — they kept animating after the job had finished.
       */
      const attachAsyncJobPoll = (jobId: string, pollingSessionId: string): void => {
        pollAbortController?.abort()
        const pollController = new AbortController()
        pollAbortController = pollController
        pollAsyncJob(
          jobId,
          pollingSessionId,
          (progress, message) => {
            const progressMessage = message?.trim()
              ? message
              : `${getProgressPhaseStatusText('background')} (${Math.round(progress)}%)`
            showInlineTransientStatus(progressMessage)
            // Throttled to ticks that actually moved: a fill run stages
            // in batches, so a repeated percentage means no new rows.
            const hasMoved = get().asyncJobProgress !== progress
            set((state) => ({
              asyncJobProgress: progress,
              asyncJobMessage: message,
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: 'background',
                detail: null,
                isBackground: true,
                percent: progress,
              },
              // The worker's own narration ("Generating "Approach" (1/2)") on
              // the running activity row. The transient status line above is
              // hidden once rows exist, so without this the writer saw a
              // single frozen "Understanding request" for the whole job.
              ...(message?.trim()
                ? { activityPhases: applyProgressNarration(state.activityPhases, message) }
                : {}),
            }))
            if (hasMoved) refreshScheduleProposalsIfOpen()
          },
          pollController.signal
        )
          .then((result: AsyncJobResult) => {
            if (pollAbortController === pollController) {
              pollAbortController = null
            }
            // A newer poll owns the panel now — this one's outcome is stale.
            if (pollAbortController !== null) return
            if (get().sessionId !== pollingSessionId) {
              return
            }
            // The job names the plan it drafted from; once it wrote a section
            // the worker approved that plan, so the flush turns its card into
            // "Plan approved" — the same flip as the sync lane, and it wins over
            // the drop below. Written sections still retire any OTHER stashed
            // pending card: "Accept & draft" over a finished draft is wrong.
            const jobDraftedPlan = readJobDraftedFromPlan(result.resultPayload)
            if (jobDraftedPlan) pendingDraftedPlan = jobDraftedPlan
            if (asyncPayloadWroteSections(result.resultPayload)) {
              pendingPlanTurnDrafted = true
            }
            flushPendingPlanMessage()

            set((state) => ({
              isPolling: false,
              asyncJobId: null,
              isCancellable: false,
              // `turn_completed` keeps currentTurnId alive for the duration of
              // the poll, so poll exit has to release it. Left stale, the next
              // turn's turn_error is dropped by the id check and the UI falls
              // back to the generic "No reply came back".
              currentTurnId: null,
              asyncJobProgress: 0,
              asyncJobMessage: '',
              writingWorkflowState: 'idle',
              pendingMutationKind: null,
              progressCard: {
                active: false,
                phase: null,
                detail: null,
                isBackground: false,
                percent: null,
              },
              activityPhases: settleActivityPhases(
                state.activityPhases,
                result.status === 'failed' ? 'failed' : 'completed'
              ),
            }))
            // Unconditional across every terminal status: a run that
            // failed or was cancelled part-way still staged the rows it
            // got through, and those must show up.
            refreshScheduleProposalsIfOpen()
            if (result.status === 'completed' && result.resultPayload) {
              const segs = result.resultPayload['segments'] as DraftSegment[] | undefined
              const order = result.resultPayload['planSectionOrder'] as string[] | undefined
              const payloadNewVersionRaw = result.resultPayload['newVersion']
              const payloadNewVersion = typeof payloadNewVersionRaw === 'number'
                ? payloadNewVersionRaw
                : (
                    typeof payloadNewVersionRaw === 'string'
                      && payloadNewVersionRaw.trim() !== ''
                      && Number.isFinite(Number(payloadNewVersionRaw))
                  )
                  ? Number(payloadNewVersionRaw)
                  : null
              if (segs) {
                set((state) => ({
                  segments: segs,
                  expectedRevision: getExpectedRevisionFromSegments(state.activeScope, segs)
                    ?? state.expectedRevision,
                }))
              }
              if (order) set({ planSectionOrder: order })
              if (payloadNewVersion !== null) {
                set({ expectedRevision: String(payloadNewVersion) })
              }
              // Refetch section assignments so collaboration status reflects backend autoAdvanceToInProgress
              const asyncScope = get().activeScope
              if (asyncScope?.artifactId) {
                void useSectionAssignmentsStore.getState().fetchAssignments(asyncScope.artifactId)
              }
              const terseStatus = buildAsyncCompletionMessage(result.resultPayload)
              const rawSummary = result.resultPayload['draftSummaryMessage']
              const summaryText = typeof rawSummary === 'string' ? rawSummary.trim() : ''
              // Prefer the model's "what I drafted and why" recap as an assistant
              // bubble; fall back to the terse status line when it's absent.
              // The worker stores the same text on the turn, so a reload shows
              // exactly this.
              set((state) => ({
                messages: [
                  ...state.messages,
                  summaryText.length > 0
                    ? {
                        id: nextMsgId(),
                        role: 'assistant' as const,
                        content: summaryText,
                        timestamp: new Date().toISOString(),
                        eventType: 'async_completed',
                      }
                    : {
                        id: nextMsgId(),
                        role: 'status' as const,
                        content: terseStatus,
                        timestamp: new Date().toISOString(),
                        eventType: 'async_completed',
                        durable: true,
                      },
                ],
              }))
              showProgressRecap('success', terseStatus)
            } else if (result.status === 'completed') {
              const completionMessage = buildAsyncCompletionMessage(null)
              set((state) => ({
                messages: [
                  ...state.messages,
                  {
                    id: nextMsgId(),
                    role: 'status' as const,
                    content: completionMessage,
                    timestamp: new Date().toISOString(),
                    eventType: 'async_completed',
                    durable: true,
                  },
                ],
              }))
              showProgressRecap('success', completionMessage)
            } else if (result.status === 'failed') {
              applyPartialSegmentsFromPayload(result.resultPayload)
              const rawFailure = result.errorMessage ?? 'Background task failed'
              const partialNote = buildPartialFailureNote(result.resultPayload)
              const failureMessage = partialNote
                ? `${rawFailure} ${partialNote}`
                : rawFailure
              set({
                error: failureMessage,
                writingWorkflowState: 'error',
              })
              showProgressRecap('error', failureMessage)
            } else if (result.status === 'cancelled') {
              applyPartialSegmentsFromPayload(result.resultPayload)
              const cancelledMessage = buildStoppedDraftMessage(result.resultPayload)
              set((state) => ({
                writingWorkflowState: 'idle',
                pendingMutationKind: null,
                messages: [
                  ...state.messages,
                  {
                    id: nextMsgId(),
                    role: 'status' as const,
                    content: cancelledMessage,
                    timestamp: new Date().toISOString(),
                    eventType: 'async_cancelled',
                    durable: true,
                  },
                ],
              }))
              showProgressRecap('cancelled', cancelledMessage)
            }
          })
          .catch((error: unknown) => {
            if (pollAbortController === pollController) {
              pollAbortController = null
            }
            // Superseded by a newer poll — leave its state alone.
            if (pollAbortController !== null) return
            if (get().sessionId !== pollingSessionId) {
              return
            }
            flushPendingPlanMessage()

            const isCancelled =
              error instanceof Error &&
              'code' in error &&
              (error as { code: string }).code === 'CANCELLED'

            set((state) => ({
              isPolling: false,
              asyncJobId: null,
              isCancellable: false,
              // Release on the failure path too — see the .then() above.
              currentTurnId: null,
              asyncJobProgress: 0,
              asyncJobMessage: '',
              progressCard: {
                active: false,
                phase: null,
                detail: null,
                isBackground: false,
                percent: null,
              },
              writingWorkflowState: isCancelled ? 'idle' : 'error',
              pendingMutationKind: null,
              // A Stop is not a failure — the durable "Stopped" line says what
              // happened, so the rows close quietly rather than in red.
              activityPhases: settleActivityPhases(state.activityPhases, isCancelled ? 'completed' : 'failed'),
              ...(isCancelled
                ? {}
                : { error: error instanceof Error ? error.message : 'Background polling failed' }),
            }))
            if (!isCancelled) {
              showProgressRecap('error', error instanceof Error ? error.message : 'Background polling failed')
            }
          })
      }

      /**
       * Bring a turn that is still running back into view after the writer
       * returns to its section (the stream that was feeding the panel was cut
       * when they left).
       *
       * This is the existing stream-recovery replay (`replayTurnEvents` →
       * `_handleStreamEvent`, the path an interrupted stream already takes),
       * pointed at the resumed turn from its first event. Replaying through the
       * live handler rebuilds exactly what the writer saw before leaving: the
       * activity rows, any reply text streamed so far — and, for a background
       * draft, the `async_handoff` that starts polling its job again.
       *
       * A sync turn's stream cannot be re-attached, so after the replay its rows
       * hold at their last phase and the active-turn poller reconciles the saved
       * result when the turn ends. If the replay fails (or the hand-off event has
       * expired), a background job is polled directly from `asyncJobId`.
       */
      const recoverResumedTurn = async (turn: ActiveTurnInfo, resumedSessionId: string): Promise<void> => {
        const isStillResuming = () =>
          get().sessionId === resumedSessionId && get().currentTurnId === turn.turnId

        // The rows were reset for this section, so replay from the first event —
        // a de-dup cursor left from before the section switch would skip them.
        lastProcessedSeqByTurn.delete(turn.turnId)
        try {
          await replayTurnEvents(turn.turnId, 0, (event) => {
            if (!isStillResuming()) return
            get()._handleStreamEvent(event)
          })
        } catch {
          // Keep the working row already on screen; the pollers below still
          // land the result.
        }

        if (!isStillResuming()) return
        if (!turn.asyncJobId || get().isPolling) return

        const jobId = turn.asyncJobId
        set((state) => {
          return {
            asyncJobId: jobId,
            asyncJobProgress: 0,
            asyncJobMessage: '',
            isPolling: true,
            isCancellable: true,
            writingWorkflowState: 'drafting',
            // The job poll owns completion from here, same as after a live
            // hand-off; the active-turn poller stands down.
            streamRecoveryState: 'idle',
            progressCard: {
              active: true,
              phase: 'background',
              detail: null,
              isBackground: true,
              percent: 0,
            },
            ...releaseReplyPlaceholder(state),
          }
        })
        attachAsyncJobPoll(jobId, resumedSessionId)
      }

      const clearTurnRuntimeState = () => {
        abortController?.abort()
        abortController = null
        pollAbortController?.abort()
        pollAbortController = null
        clearProgressRecapTimer()
        clearTransientStatusMessages()
        lastProcessedSeqByTurn.clear()
        // Drop any deferred plan card so it can't leak into the next context
        // (cross-section flush guard, plan-card-ux D1).
        clearPendingPlanMessage()
      }

      const resetThreadConversationState = () => {
        clearTurnRuntimeState()
        set((state) => ({
          currentTurnId: null,
          isStreaming: false,
          isCancellable: false,
          asyncJobId: null,
          asyncJobProgress: 0,
          asyncJobMessage: '',
          isPolling: false,
          progressCard: {
            active: false,
            phase: null,
            detail: null,
            isBackground: false,
            percent: null,
          },
          progressRecap: null,
          messages: [],
          // The activity rows belong to the conversation being cleared — left
          // behind, a resumed turn's still-running rows would animate forever
          // in a thread they have nothing to do with.
          activityPhases: initialPhaseState(),
          lastStreamEvent: null,
          streamingMessageId: null,
          activeSuggestedOffer: null,
          writingWorkflowState: 'idle',
          pendingMutationKind: null,
          streamRecoveryState: 'idle',
          lastSubmittedTurn: null,
          errorAction: null,
          proposedPlan: null,
          // `messages` is cleared here, so every plan card goes with it. A draft
          // left behind would be an orphan holding a frozen `base`/`baseVersion`
          // — and because Task 3 treats "a draft exists" as "the card is in edit
          // mode", returning to this thread would re-open the card on a fork of
          // a plan that may have moved on since.
          ...clearedPlanEditState(),
          draftPatchPreview: null,
          isResolvingDraftPatchPreview: false,
          lastVerification: null,
          selectionEditResult: null,
          isEditingSelection: false,
          activeSelectionEditRequestId: state.activeSelectionEditRequestId + 1,
          error: null,
        }))
      }

      const hydrateSessionForActiveThread = (
        scope: Scope,
        result: AgentSessionResponse
      ) => {
        const hydratedSegments = Array.isArray(result.segments) ? result.segments : []
        const hydratedSectionOrder = Array.isArray(result.planSectionOrder)
          ? result.planSectionOrder
          : hydratedSegments.map((segment) => segment.sectionId)
        const { messages: hydratedMessages, stalePlanId: hydratedStalePlanId } =
          appendHydratedPlanCard(hydrateMessagesFromTurnHistory(result.turnHistory), result)
        const expectedRevisionFromSession = resolveExpectedRevisionFromSession(scope, result, hydratedSegments)
        const activeThreadId = result.activeThreadId ?? result.threads?.[0]?.id ?? null
        const hydratedThreads = Array.isArray(result.threads) ? result.threads : []
        const hydratedThreadPreviews = hydrateThreadPreviewsFromTurnHistory(result.turnHistory, activeThreadId)
        const hydratedSuggestedOffer = hydrateSuggestedOfferFromTurnHistory(result.turnHistory, activeThreadId)
        const messagesWithProposalStub = appendPendingProposalStub(
          hydratedMessages,
          result.pendingProposal,
          hydratedSegments
        )
        syncPendingSuggestions(result)

        set((state) => ({
          sessionId: result.sessionId,
          activeScope: scope,
          activeThreadId,
          expectedRevision:
            expectedRevisionFromSession
            ?? (scope.kind === 'artifact' ? state.expectedRevision : null),
          threads: hydratedThreads,
          threadPreviewById: {
            ...state.threadPreviewById,
            ...hydratedThreadPreviews,
          },
          currentTurnId: null,
          isStreaming: false,
          isCancellable: false,
          asyncJobId: null,
          asyncJobProgress: 0,
          asyncJobMessage: '',
          isPolling: false,
          progressCard: {
            active: false,
            phase: null,
            detail: null,
            isBackground: false,
            percent: null,
          },
          progressRecap: null,
          messages: messagesWithProposalStub,
          streamingMessageId: null,
          activeSuggestedOffer: hydratedSuggestedOffer,
          streamRecoveryState: 'idle',
          lastSubmittedTurn: null,
          errorAction: null,
          currentPlan: null,
          proposedPlan: null,
          ...clearedPlanEditState(),
          ...hydrateDraftPatchPreview(result.pendingProposal, hydratedSegments),
          isResolvingDraftPatchPreview: false,
          segments: hydratedSegments,
          planSectionOrder: hydratedSectionOrder,
          lastVerification: null,
          selectionEditResult: null,
          isEditingSelection: false,
          activeSelectionEditRequestId: state.activeSelectionEditRequestId + 1,
          ...(hydratedStalePlanId
            ? { stalePlanIds: new Set(state.stalePlanIds).add(hydratedStalePlanId) }
            : {}),
          error: null,
        }))
      }

      const reloadActiveThreadHistory = async () => {
        const { sessionId, sessionInitContext, activeScope } = get()
        if (!sessionId || !sessionInitContext) return

        const result = await getOrCreateAgentSession(
          sessionInitContext.bidId,
          sessionInitContext.artifactId,
          sessionInitContext.sectionKey,
          sessionInitContext.scopeKind
        )

        if (get().sessionId !== sessionId) return

        const fallbackScope: Scope = sessionInitContext.scopeKind === 'artifact'
          ? { kind: 'artifact', artifactId: sessionInitContext.artifactId }
          : { kind: 'unit', artifactId: sessionInitContext.artifactId, unitId: sessionInitContext.sectionKey }
        hydrateSessionForActiveThread(activeScope ?? fallbackScope, result)
      }

      return {
      // ===================
      // Initial state
      // ===================
      sessionId: null,
      sessionInitContext: null,
      activeScope: null,
      activeThreadId: null,
      expectedRevision: null,
      threads: [],
      threadPreviewById: {},
      selectedIntent: 'write',
      stateDefault: null,
      currentTurnId: null,
      isStreaming: false,
      isCancellable: false,
      asyncJobId: null,
      asyncJobProgress: 0,
      asyncJobMessage: '',
      isPolling: false,
      progressCard: {
        active: false,
        phase: null,
        detail: null,
        isBackground: false,
        percent: null,
      },
      progressRecap: null,
      messages: [],
      streamingMessageId: null,
      activeSuggestedOffer: null,
      writingWorkflowState: 'idle',
      pendingMutationKind: null,
      streamRecoveryState: 'idle',
      lastSubmittedTurn: null,
      errorAction: null,
      isLockedByAnotherTab: false,
      documentJobEnabled: false,
      currentPlan: null,
      proposedPlan: null,
      stalePlanIds: new Set<string>(),
      ...clearedPlanEditState(),
      draftPatchPreview: null,
      isResolvingDraftPatchPreview: false,
      segments: [],
      planSectionOrder: [],
      lastVerification: null,
      selection: null,
      selectionEditResult: null,
      isEditingSelection: false,
      activeSelectionEditRequestId: 0,
      applySelectionRewrite: null,
      flushDraftSavesBeforeThreadChange: null,
      lastStreamEvent: null,
      activityPhases: initialPhaseState(),
      isPanelOpen: true,
      error: null,

      // ===================
      // Session
      // ===================

      initSession: async (bidId, artifactId, sectionKey, scopeKind = 'unit') => {
        const nextScope = scopeKind === 'artifact'
          ? makeArtifactScope(artifactId)
          : makeUnitScope(artifactId, sectionKey)
        const nextScopeId = serializeScope(nextScope)
        if (initSessionInFlight?.scopeId === nextScopeId) {
          await initSessionInFlight.promise
          return
        }

        const requestId = ++initSessionRequestCounter
        const initPromise = (async () => {
          try {
            const result = await getOrCreateAgentSession(bidId, artifactId, sectionKey, scopeKind)
            if (requestId !== initSessionRequestCounter) {
              return
            }

            // Probe for an in-flight turn whose worker is still processing. The
            // backend runs a check-on-read reaper, so this returns null for
            // stale orphaned rows. If a live turn is present, we preserve its
            // handles through the state wipe so the panel can show a resume
            // banner and the poller can pick it up. Failure is non-fatal — we
            // fall through to a normal (clean) initialization.
            let resumeTurn: ActiveTurnInfo | null = null
            try {
              const active = await getActiveTurn(result.sessionId)
              if (requestId !== initSessionRequestCounter) {
                return
              }
              resumeTurn = active.turn
            } catch {
              resumeTurn = null
            }

            let activeThreadId = result.activeThreadId ?? result.threads?.[0]?.id ?? null
            let hydratedThreads = Array.isArray(result.threads) ? result.threads : []
            if (!activeThreadId) {
              const created = await createAgentThread(result.sessionId, 'carry_summary')
              activeThreadId = created.activeThreadId
              hydratedThreads = [...hydratedThreads, created.thread]
            }

            const previousSessionId = get().sessionId
            const previousScope = get().activeScope
            const previousScopeId = previousScope ? serializeScope(previousScope) : null
            const previousThreadId = get().activeThreadId
            const previousExpectedRevision = get().expectedRevision
            const hydratedSegments = Array.isArray(result.segments) ? result.segments : []
            const hydratedSectionOrder = Array.isArray(result.planSectionOrder)
              ? result.planSectionOrder
              : hydratedSegments.map((segment) => segment.sectionId)
            const { messages: hydratedMessages, stalePlanId: hydratedStalePlanId } =
              appendHydratedPlanCard(hydrateMessagesFromTurnHistory(result.turnHistory), result)
            const expectedRevisionFromSession = resolveExpectedRevisionFromSession(nextScope, result, hydratedSegments)
            const expectedRevision =
              expectedRevisionFromSession
              ?? (nextScope.kind === 'artifact' && previousScopeId === nextScopeId
                ? previousExpectedRevision
                : null)
            const hydratedThreadPreviews = hydrateThreadPreviewsFromTurnHistory(result.turnHistory, activeThreadId)
            const hydratedSuggestedOffer = hydrateSuggestedOfferFromTurnHistory(result.turnHistory, activeThreadId)
            const hydratedMessagesWithStub = appendPendingProposalStub(
              hydratedMessages,
              result.pendingProposal,
              hydratedSegments
            )

            if (
              previousSessionId !== result.sessionId
              || previousScopeId !== nextScopeId
              || previousThreadId !== activeThreadId
            ) {
              abortController?.abort()
              abortController = null
              pollAbortController?.abort()
              pollAbortController = null
              clearTransientStatusMessages()

              // Resume path: if the backend reports a turn is still in flight
              // for this session, carry its handles through the wipe so the
              // poller can reconcile when it finishes. Without this, the old
              // code path nulled everything out and the session looked idle
              // while the worker kept running — causing the "ghost" bug.
              const hasResumeTurn = Boolean(resumeTurn)
              const resumedStreamingMessageId = hasResumeTurn ? `resumed-${resumeTurn!.turnId}` : null
              const nowIso = new Date().toISOString()
              // The in-flight turn is ALREADY in `turnHistory` (the backend does
              // not filter by status), so `hydrateMessagesFromTurnHistory` has
              // usually emitted its user bubble as `${turnId}:user`. Appending
              // the placeholder unconditionally rendered the writer's prompt
              // twice on every resume.
              const resumeUserAlreadyHydrated = hasResumeTurn
                && hydratedMessages.some((m) => (
                  m.role === 'user'
                  && (m.id === `${resumeTurn!.turnId}:user` || m.content === resumeTurn!.userMessage)
                ))
              const messagesWithResumePlaceholder: AgentChatMessage[] = hasResumeTurn
                ? [
                  ...hydratedMessagesWithStub,
                  ...(resumeUserAlreadyHydrated
                    ? []
                    : [{
                      id: `user-${resumeTurn!.turnId}`,
                      role: 'user' as const,
                      content: resumeTurn!.userMessage,
                      timestamp: resumeTurn!.createdAt,
                    }]),
                  {
                    id: resumedStreamingMessageId!,
                    role: 'assistant',
                    content: '',
                    timestamp: nowIso,
                    isStreaming: true,
                    intent: resumeTurn!.intentSelected,
                  },
                ]
                : hydratedMessagesWithStub

              syncPendingSuggestions(result)
              set((state) => ({
                sessionId: result.sessionId,
                sessionInitContext: { bidId, artifactId, sectionKey, scopeKind },
                activeScope: nextScope,
                activeThreadId,
                expectedRevision,
                threads: hydratedThreads,
                threadPreviewById: hydratedThreadPreviews,
                currentTurnId: hasResumeTurn ? resumeTurn!.turnId : null,
                isStreaming: false,
                isCancellable: hasResumeTurn,
                asyncJobId: hasResumeTurn ? resumeTurn!.asyncJobId : null,
                asyncJobProgress: 0,
                asyncJobMessage: '',
                isPolling: false,
                progressCard: {
                  active: false,
                  phase: null,
                  detail: null,
                  isBackground: false,
                  percent: null,
                },
                progressRecap: null,
                messages: messagesWithResumePlaceholder,
                streamingMessageId: resumedStreamingMessageId,
                activeSuggestedOffer: hydratedSuggestedOffer,
                streamRecoveryState: hasResumeTurn ? 'recovering' : 'idle',
                lastSubmittedTurn: null,
                errorAction: null,
                currentPlan: null,
                proposedPlan: null,
                ...clearedPlanEditState(),
                ...hydrateDraftPatchPreview(result.pendingProposal, hydratedSegments),
                isResolvingDraftPatchPreview: false,
                segments: hydratedSegments,
                planSectionOrder: hydratedSectionOrder,
                lastVerification: null,
                selection: null,
                selectionEditResult: null,
                isEditingSelection: false,
                activeSelectionEditRequestId: state.activeSelectionEditRequestId + 1,
                applySelectionRewrite: null,
                isLockedByAnotherTab: result.isLocked === true,
                documentJobEnabled: result.documentJobEnabled === true,
                ...(hydratedStalePlanId
                  ? { stalePlanIds: new Set(state.stalePlanIds).add(hydratedStalePlanId) }
                  : {}),
                // Activity rows belong to one turn in one section. The previous
                // section's rows must not follow the writer here; a resumed
                // turn shows a working row at once, and its real rows are
                // rebuilt from its persisted events just below.
                activityPhases: hasResumeTurn ? optimisticPhaseState() : initialPhaseState(),
                lastStreamEvent: null,
                error: null,
              }))
              if (hasResumeTurn) {
                void recoverResumedTurn(resumeTurn!, result.sessionId)
              }
              return
            }

            set((state) => ({
              sessionId: result.sessionId,
              sessionInitContext: { bidId, artifactId, sectionKey, scopeKind },
              activeScope: nextScope,
              activeThreadId,
              expectedRevision,
              threads: hydratedThreads,
              threadPreviewById: {
                ...state.threadPreviewById,
                ...hydratedThreadPreviews,
              },
              activeSuggestedOffer: hydratedSuggestedOffer,
              segments: hydratedSegments,
              planSectionOrder: hydratedSectionOrder,
              isLockedByAnotherTab: result.isLocked === true,
              documentJobEnabled: result.documentJobEnabled === true,
              error: null,
            }))

            // If a background job is already running for this session (e.g. the
            // user started a draft, navigated away, and came back), re-attach
            // the progress indicator and resume polling to surface the result.
            void useActiveJobsStore.getState().fetchActive()
            get().resumeBackgroundJobIfAny()
          } catch (err) {
            if (requestId !== initSessionRequestCounter) {
              return
            }
            set({ error: err instanceof Error ? err.message : "Couldn't start the AI session. Try again." })
          }
        })()

        initSessionInFlight = { scopeId: nextScopeId, promise: initPromise }
        try {
          await initPromise
        } finally {
          if (initSessionInFlight?.promise === initPromise) {
            initSessionInFlight = null
          }
        }
      },

      loadReadOnlySegments: async (artifactId, sectionKey) => {
        // Shares initSession's counter, so whichever load started last wins.
        const requestId = ++initSessionRequestCounter
        const document = await getArtifactDocument(artifactId)
        if (requestId !== initSessionRequestCounter) return
        set({
          segments: readOnlySegmentsFromDocument(document, sectionKey),
          planSectionOrder: [],
        })
      },

      setSessionId: (sessionId) => {
        set({ sessionId })
      },

      reconcileResumedTurn: async (turnId) => {
        // Only reconcile if the store still thinks this is the active resumed
        // turn. If the user navigated elsewhere (currentTurnId changed) or the
        // recovery already resolved, bail out — whoever updated state is the
        // source of truth.
        const state = get()
        if (state.streamRecoveryState !== 'recovering') return
        if (state.currentTurnId !== turnId) return
        const { sessionInitContext } = state
        if (!sessionInitContext) return

        try {
          const refreshed = await getOrCreateAgentSession(
            sessionInitContext.bidId,
            sessionInitContext.artifactId,
            sessionInitContext.sectionKey,
            sessionInitContext.scopeKind
          )
          // Another init may have landed while we were fetching — don't
          // clobber it.
          const current = get()
          if (current.sessionId !== refreshed.sessionId) return
          if (current.currentTurnId !== turnId) return

          const { messages: hydratedMessages, stalePlanId: hydratedStalePlanId } =
            appendHydratedPlanCard(hydrateMessagesFromTurnHistory(refreshed.turnHistory), refreshed)
          const hydratedSegments = Array.isArray(refreshed.segments) ? refreshed.segments : []
          const hydratedSectionOrder = Array.isArray(refreshed.planSectionOrder)
            ? refreshed.planSectionOrder
            : hydratedSegments.map((segment) => segment.sectionId)

          // `messages` is replaced wholesale here, so plan drafts must be
          // reconciled — but SCOPED, not wiped. Unlike the thread/scope/session
          // wipes, this is the SAME conversation being re-hydrated behind the
          // writer's back, and blowing away a draft they're typing into because
          // an unrelated turn reconciled would discard their text without a
          // prompt. Keep the forks the rebuild reproduces exactly; drop the rest.
          const reconciledPlanEdits = reconcilePlanDrafts(current.planEdits, hydratedMessages)
          // A conflict only ever exists alongside its draft; if the draft didn't
          // survive, resolving the conflict would offer to discard edits that
          // are already gone.
          const retainedPlanConflict =
            current.planConflict && reconciledPlanEdits[current.planConflict.planId]
              ? current.planConflict
              : null
          // ...and the BANNER retires with it. A conflict is only ever
          // recoverable through the banner it put up, so the reverse holds too:
          // leaving `error`/`errorAction` armed over a conflict that's gone
          // strands the writer with "refresh and reapply" pointing at nothing.
          // The banner's button calls `recoverFromRevisionConflict`, which
          // without a `planConflict` skips the plan branch and FALLS THROUGH to
          // turn recovery — harmless only because `lastSubmittedTurn` is null on
          // the resume path (`initSession` nulls it). If one ever survived into
          // this state, a stranded banner would REPLAY A TURN. Retire all three
          // together, as `revertPlanEdits` does.
          const isRetiringPlanConflict = current.planConflict !== null && retainedPlanConflict === null
          // A replayed set_plan may have stashed a card; the rebuilt history
          // already carries every plan card, so a later flush would duplicate it.
          clearPendingPlanMessage()
          set({
            messages: hydratedMessages,
            planEdits: reconciledPlanEdits,
            planConflict: retainedPlanConflict,
            ...(isRetiringPlanConflict ? { errorAction: null, error: null } : {}),
            segments: hydratedSegments,
            planSectionOrder: hydratedSectionOrder,
            ...(hydratedStalePlanId
              ? { stalePlanIds: new Set(current.stalePlanIds).add(hydratedStalePlanId) }
              : {}),
            currentTurnId: null,
            isStreaming: false,
            isCancellable: false,
            streamingMessageId: null,
            streamRecoveryState: 'replay_complete',
            // The saved reply renders its own activity steps; the live rows
            // rebuilt for the resumed turn would show them twice.
            activityPhases: initialPhaseState(),
            lastStreamEvent: null,
            asyncJobId: null,
            isPolling: false,
            progressCard: {
              active: false,
              phase: null,
              detail: null,
              isBackground: false,
              percent: null,
            },
          })
        } catch (err) {
          set((state) => ({
            streamRecoveryState: 'replay_unavailable',
            error: err instanceof Error ? err.message : 'Failed to reconcile in-flight turn.',
            // Nothing more will arrive for these rows — stop them animating.
            activityPhases: settleActivityPhases(state.activityPhases, 'failed'),
          }))
        }
      },

      resumeBackgroundJobIfAny: () => {
        const { sessionId, isPolling, isStreaming } = get()
        if (!sessionId) return
        if (isPolling || isStreaming) return

        const running = useActiveJobsStore
          .getState()
          .getJobsForSession(sessionId)
          .find((j) => j.status === 'pending' || j.status === 'running')
        if (!running) return

        set({
          asyncJobId: running.jobId,
          asyncJobProgress: running.progress,
          asyncJobMessage: running.progressMessage ?? '',
          isPolling: true,
          isCancellable: true,
          progressCard: {
            active: true,
            phase: 'background',
            detail: null,
            isBackground: true,
            percent: running.progress,
          },
        })

        // Same completion path as a job started in this view, so the closing
        // message renders in place here too (it used to apply the segments and
        // say nothing).
        attachAsyncJobPoll(running.jobId, sessionId)
      },

      loadThreads: async () => {
        const { sessionId } = get()
        if (!sessionId) return

        try {
          const response = await listAgentThreads(sessionId)
          if (get().sessionId !== sessionId) return
          set({
            activeThreadId: response.activeThreadId,
            threads: response.threads,
            error: null,
          })
        } catch (error) {
          if (get().sessionId !== sessionId) return
          set({ error: error instanceof Error ? error.message : "Couldn't load chat history. Try again." })
        }
      },

      createThread: async (carryMode) => {
        const {
          sessionId,
          activeScope,
          sessionInitContext,
          isStreaming,
          isPolling,
          flushDraftSavesBeforeThreadChange,
        } = get()
        if (!sessionId || !activeScope || !sessionInitContext) return

        if (isStreaming || isPolling) {
          set({ error: 'Please wait for the current response to finish before creating a new chat.' })
          return
        }

        if (flushDraftSavesBeforeThreadChange) {
          try {
            await flushDraftSavesBeforeThreadChange()
          } catch (error) {
            if (get().sessionId !== sessionId) return
            set({
              error: error instanceof Error
                ? error.message
                : 'Please resolve draft save issues before creating a new chat.',
            })
            return
          }
        }

        try {
          const created = await createAgentThread(sessionId, carryMode)
          if (get().sessionId !== sessionId) return

          set((state) => ({
            activeThreadId: created.activeThreadId,
            threads: [...state.threads.filter((thread) => thread.id !== created.thread.id), created.thread],
            error: null,
          }))

          resetThreadConversationState()
          await reloadActiveThreadHistory()

          // Backends may report `carriedSummary: true` before turn history reflects
          // the synthetic carry-summary seed turn. Keep UX explicit by showing a
          // lightweight system note when no summary message is hydrated.
          if (carryMode === 'carry_summary' && created.carriedSummary) {
            set((state) => {
              if (state.activeThreadId !== created.activeThreadId) return {}
              const hasSystemSummary = state.messages.some((message) => message.role === 'system')
              if (hasSystemSummary) return {}
              return {
                messages: [
                  {
                    id: `carry-forward-${created.thread.id}`,
                    role: 'system',
                    content: CARRY_FORWARD_FALLBACK_SUMMARY,
                    timestamp: new Date().toISOString(),
                    intent: 'write',
                  },
                  ...state.messages,
                ],
              }
            })
          }

          await get().loadThreads()
        } catch (error) {
          if (get().sessionId !== sessionId) return
          set({ error: error instanceof Error ? error.message : "Couldn't create a new chat. Try again." })
        }
      },

      switchThread: async (threadId) => {
        const {
          sessionId,
          activeScope,
          sessionInitContext,
          activeThreadId,
          isStreaming,
          isPolling,
          flushDraftSavesBeforeThreadChange,
        } = get()
        if (!sessionId || !activeScope || !sessionInitContext || !threadId) return
        if (threadId === activeThreadId) return

        if (isStreaming || isPolling) {
          set({ error: 'Please wait for the current response to finish before switching chats.' })
          return
        }

        if (flushDraftSavesBeforeThreadChange) {
          try {
            await flushDraftSavesBeforeThreadChange()
          } catch (error) {
            if (get().sessionId !== sessionId) return
            set({
              error: error instanceof Error
                ? error.message
                : 'Please resolve draft save issues before switching chats.',
            })
            return
          }
        }

        try {
          await activateAgentThread(sessionId, threadId)
          if (get().sessionId !== sessionId) return

          set({
            activeThreadId: threadId,
            error: null,
          })
          resetThreadConversationState()
          await reloadActiveThreadHistory()
          await get().loadThreads()
        } catch (error) {
          if (get().sessionId !== sessionId) return
          set({ error: error instanceof Error ? error.message : "Couldn't switch chats right now. Try again." })
        }
      },

      // ===================
      // Intent
      // ===================

      setSelectedIntent: (intent) => {
        set({ selectedIntent: intent })
      },

      // ===================
      // Turn
      // ===================

      sendTurn: async (message, _selectedEvidence, options) => {
        const {
          sessionId,
          activeScope,
          activeThreadId,
          expectedRevision,
          isPolling,
          draftPatchPreview,
        } = get()
        const panelIntent: AgentIntent = 'write'
        if (!sessionId || !activeScope || !activeThreadId || !message.trim()) return
        if (get().isLockedByAnotherTab) {
          set({ error: 'This session is in use on another device or tab. Wait for it to finish before sending a new message.' })
          return
        }

        // An advice-only turn (R1 E1f) is not held back: the suggestion cannot be decided there.
        if (draftPatchPreview && options?.adviceOnly !== true) {
          set({ error: 'Please accept or reject the current revision preview before sending a new message.' })
          return
        }

        if (isPolling) {
          set({ error: 'Please wait for the current background task to finish or cancel it first.' })
          return
        }

        clearTransientStatusMessages()
        clearProgressRecapTimer()
        set({
          progressRecap: null,
          streamRecoveryState: 'idle',
          errorAction: null,
          // A plan conflict is only ever recoverable through the banner it put
          // up. Sending a new message retires that banner (`errorAction: null`
          // above), so the conflict must retire with it — otherwise it outlives
          // its affordance and hijacks the NEXT conflict recovery, which would
          // silently drop the writer's turn instead of replaying it.
          planConflict: null,
        })

        // Flush any pending editor saves so the backend sees the latest draft content
        const flusher = get().flushDraftSavesBeforeThreadChange
        if (flusher) {
          try {
            await flusher()
          } catch {
            // Non-blocking: proceed with turn even if flush fails
          }
        }

        // Validate auth token exists before making request
        const token = await getAuthToken()
        if (!token) {
          set({
            error: 'Not authenticated. Please log in.',
            sessionId: null, // Clear session to trigger redirect
            sessionInitContext: null,
            activeScope: null,
            activeThreadId: null,
            expectedRevision: null,
            threads: [],
            threadPreviewById: {},
            activeSuggestedOffer: null,
            writingWorkflowState: 'error',
          })
          return
        }

        // Optimistically add user message
        const userMsgId = nextMsgId()
        const now = new Date().toISOString()
        const selectedEvidence = Array.isArray(_selectedEvidence) && _selectedEvidence.length > 0
          ? [..._selectedEvidence]
          : undefined
        // When the user asked a question about a selection, inline the selected
        // snippet into the LLM-bound message so the agent can ground its answer
        // in the right text. The chat bubble shows `content` (raw instruction)
        // with `quotedText` rendered above — display stays clean.
        const backendMessage = options?.quotedText
          ? `The user has selected this text in the editor:\n"""\n${options.quotedText}\n"""\n\nThey are asking a question about it (not requesting an edit):\n${message}`
          : message
        {
          set({
            lastSubmittedTurn: {
              message,
              ...(selectedEvidence ? { selectedEvidence } : {}),
              ...(options?.factBankOnly ? { factBankOnly: true } : {}),
              threadId: activeThreadId,
              scope: { ...activeScope },
              retryAttempted: options?.isConflictRetry === true,
              ...(options?.quotedText ? { quotedText: options.quotedText } : {}),
            },
          })
        }
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id: userMsgId,
              role: 'user' as const,
              content: message,
              timestamp: now,
              intent: panelIntent,
              ...(options?.quotedText ? { quotedText: options.quotedText } : {}),
              // Display refs only — the s3Key never enters view state.
              ...(options?.attachments && options.attachments.length > 0
                ? {
                  attachments: options.attachments.map((attachment) => ({
                    attachmentId: attachment.attachmentId,
                    filename: attachment.filename,
                    mimeType: attachment.mimeType,
                    size: attachment.size,
                  })),
                }
                : {}),
            },
          ],
          threadPreviewById: state.threadPreviewById[activeThreadId]
            ? state.threadPreviewById
            : {
              ...state.threadPreviewById,
              [activeThreadId]: toChatPreviewText(message),
            },
          activeSuggestedOffer: null,
          isStreaming: true,
          isCancellable: true,
          // Replace the previous turn's settled phases with the optimistic
          // Understanding row so the panel narrates the new turn immediately
          // (no stale summary, no blank gap) until turn_started resets it.
          activityPhases: optimisticPhaseState(),
          writingWorkflowState: 'discovering',
          pendingMutationKind: null,
          progressCard: {
            active: true,
            phase: 'discovering',
            detail: null,
            isBackground: false,
            percent: null,
          },
          error: null,
        }))

        // Defensive: a pending card from a previous turn must never be lost or
        // leak into this turn's ordering.
        flushPendingPlanMessage()

        // Create a streaming assistant message placeholder
        const assistantMsgId = nextMsgId()
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id: assistantMsgId,
              role: 'assistant' as const,
              content: '',
              timestamp: new Date().toISOString(),
              intent: panelIntent,
              isStreaming: true,
            },
          ],
          streamingMessageId: assistantMsgId,
        }))

        let recoveredFromInterruptedStream = false
        let retryExpectedRevision = resolveTurnRevision(
          activeScope,
          typeof expectedRevision === 'string' ? expectedRevision : null
        )
        let wasCancelled = false
        // Transport-failure retries (stream cut mid-flight) MUST reuse the
        // same key so the backend's idempotency replay dedupes a turn that
        // may have completed server-side — a rotated key would re-run the
        // whole turn and could double-apply a draft/plan patch. Turn-level
        // retries (revision precondition, retryable execution error) rotate:
        // that turn failed terminally, so its key would only replay the
        // failure.
        let turnIdempotencyKey = userMsgId

        try {
          for (let attempt = 0; attempt <= MAX_SEND_TURN_AUTO_RETRIES; attempt++) {
            abortController = new AbortController()
            recoveredFromInterruptedStream = false

            let retryOnFreshRevision = false
            let retryOnTransientTurnError = false
            let retryCurrentRevision: string | null = null
            let suppressCompletionAfterRetryDecision = false
            let sawAssistantChunks = false
            let sawMutationEvents = false
            let sawAsyncHandoff = false

            try {
              await sendAgentTurn(
                {
                  sessionId,
                  threadId: activeThreadId,
                  scope: activeScope,
                  message: backendMessage,
                  idempotencyKey: turnIdempotencyKey,
                  ...(typeof retryExpectedRevision === 'string' ? { expectedRevision: retryExpectedRevision } : {}),
                  // Layer 1: answer-time scope. selectedDocumentIds is the ONLY
                  // document-pin field the turn schema keeps (a `selectedEvidence`
                  // twin used to ride along and was stripped server-side);
                  // factBankOnly hard-scopes to the Fact Bank.
                  ...(selectedEvidence ? { selectedDocumentIds: selectedEvidence } : {}),
                  ...(options?.factBankOnly ? { factBankOnly: true } : {}),
                  ...(options?.suggestedOfferId ? { suggestedOfferId: options.suggestedOfferId } : {}),
                  ...(options?.suggestedActionId ? { suggestedActionId: options.suggestedActionId } : {}),
                  ...(options?.suggestedActionSourceTurnId
                    ? { suggestedActionSourceTurnId: options.suggestedActionSourceTurnId }
                    : {}),
                  ...(options?.attachments && options.attachments.length > 0
                    ? { attachments: options.attachments }
                    : {}),
                  ...(options?.askAnswer ? { askAnswer: options.askAnswer } : {}),
                },
                (event) => {
                  if (event.eventType === 'assistant_message_chunk') {
                    const chunk = event.data['chunk'] ?? event.data['text']
                    if (typeof chunk === 'string' && chunk.trim().length > 0) {
                      sawAssistantChunks = true
                    }
                  }
                  if (
                    event.eventType === 'plan_patch_proposed'
                    || event.eventType === 'plan_patch_applied'
                    || event.eventType === 'draft_patch_proposed'
                    || event.eventType === 'draft_patch_applied'
                  ) {
                    sawMutationEvents = true
                  }
                  if (event.eventType === 'async_handoff') {
                    sawAsyncHandoff = true
                  }

                  if (
                    event.eventType === 'turn_error'
                    && attempt < MAX_SEND_TURN_AUTO_RETRIES
                    && !sawAssistantChunks
                    && !sawMutationEvents
                    && !sawAsyncHandoff
                  ) {
                    const code = typeof event.data['code'] === 'string' ? event.data['code'] : undefined
                    const messageText = typeof event.data['message'] === 'string'
                      ? event.data['message']
                      : ''

                    if (isRevisionPreconditionError(code, messageText)) {
                      retryOnFreshRevision = true
                      suppressCompletionAfterRetryDecision = true
                      retryCurrentRevision = typeof event.data['currentRevision'] === 'string'
                        ? event.data['currentRevision']
                        : null
                      return
                    }

                    if (code === 'TURN_EXECUTION_ERROR' && event.data['retryable'] === true) {
                      retryOnTransientTurnError = true
                      suppressCompletionAfterRetryDecision = true
                      return
                    }
                  }

                  if (suppressCompletionAfterRetryDecision && event.eventType === 'turn_completed') {
                    return
                  }

                  get()._handleStreamEvent(event)
                },
                abortController.signal
              )
            } catch (err) {
              const errorCode =
                err instanceof Error && 'code' in err && typeof (err as { code?: unknown }).code === 'string'
                  ? (err as { code: string }).code
                  : null
              const isCancel = errorCode === 'CANCELLED'
              if (isCancel) wasCancelled = true
              let retryTransportFailure = false

              if (
                errorCode === 'STREAM_ABORTED'
                || errorCode === 'STREAM_IDLE_TIMEOUT'
                || errorCode === 'TIMEOUT'
              ) {
                const turnId = get().currentTurnId
                if (turnId) {
                  const afterSeq = lastProcessedSeqByTurn.get(turnId) ?? 0
                  set({ streamRecoveryState: 'recovering' })
                  showInlineTransientStatus('Recovering stream...')
                  try {
                    await replayTurnEvents(
                      turnId,
                      afterSeq,
                      (event) => get()._handleStreamEvent(event)
                    )
                    recoveredFromInterruptedStream = true
                    set({ streamRecoveryState: 'replay_complete' })
                    showInlineTransientStatus('Replay complete')
                  } catch {
                    set({ streamRecoveryState: 'replay_unavailable' })
                    showInlineTransientStatus('Replay unavailable')
                  }
                }
              }

              if (
                !isCancel
                && !recoveredFromInterruptedStream
                && attempt < MAX_SEND_TURN_AUTO_RETRIES
                && (errorCode === 'STREAM_ABORTED' || errorCode === 'TIMEOUT' || errorCode === 'STREAM_IDLE_TIMEOUT')
              ) {
                retryTransportFailure = true
              }

              if (retryTransportFailure) {
                clearTransientStatusMessages()
                set((state) => ({
                  error: null,
                  writingWorkflowState: 'discovering',
                  isStreaming: true,
                  isCancellable: true,
                  currentTurnId: null,
                  streamRecoveryState: state.streamRecoveryState === 'recovering'
                    ? 'idle'
                    : state.streamRecoveryState,
                  progressCard: {
                    active: true,
                    phase: 'discovering',
                    detail: null,
                    isBackground: false,
                    percent: null,
                  },
                  streamingMessageId: assistantMsgId,
                  messages: state.messages.map((m) =>
                    m.id === assistantMsgId ? { ...m, content: '', isStreaming: true } : m
                  ),
                }))
                continue
              }

              if (!isCancel && !recoveredFromInterruptedStream) {
                set((state) => ({
                  error: errorCode === 'STREAM_ABORTED'
                    ? 'Connection interrupted while receiving AI response. Please try again.'
                    : (err instanceof Error ? err.message : 'Agent turn failed'),
                  messages: state.messages.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, content: 'An error occurred. Please try again.', isStreaming: false }
                      : m
                  ),
                }))
              }

              break
            }

            const shouldRetry = (retryOnFreshRevision || retryOnTransientTurnError) && attempt < MAX_SEND_TURN_AUTO_RETRIES
            if (shouldRetry) {
              // Turn-level retry: the prior turn failed terminally, so a
              // fresh key is required (the old one would replay the failure).
              turnIdempotencyKey = nextMsgId()
              if (retryCurrentRevision !== null && retryCurrentRevision !== '') {
                retryExpectedRevision = retryCurrentRevision
                set({ expectedRevision: retryCurrentRevision })
              } else if (retryOnFreshRevision) {
                try {
                  await reloadActiveThreadHistory()
                  const refreshed = resolveTurnRevision(activeScope, get().expectedRevision)
                  retryExpectedRevision = typeof refreshed === 'string' ? refreshed : retryExpectedRevision
                } catch {
                  // Best effort; fall back to existing token.
                }
              }

              clearTransientStatusMessages()
              set((state) => ({
                error: null,
                writingWorkflowState: 'discovering',
                pendingMutationKind: null,
                isStreaming: true,
                isCancellable: true,
                currentTurnId: null,
                progressCard: {
                  active: true,
                  phase: 'discovering',
                  detail: null,
                  isBackground: false,
                  percent: null,
                },
                streamingMessageId: assistantMsgId,
                messages: state.messages.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: '', isStreaming: true } : m
                ),
              }))
              continue
            }

            break
          }
        } finally {
          let silentFailureDetected = false
          set((state) => {
            const isStuckDiscovering = state.writingWorkflowState === 'discovering'
            const assistantMsg = state.messages.find((m) => m.id === assistantMsgId)
            const hasEmptyResponse = Boolean(assistantMsg && !assistantMsg.content?.trim())
            // Only show "no response" error when the stream genuinely produced nothing:
            // no content appeared and no other error was set by catch/event handlers.
            // This includes both zero-event and partial-event streams.
            const silentFailure = isStuckDiscovering && hasEmptyResponse && !state.error && !state.isPolling && !wasCancelled
            silentFailureDetected = silentFailure

            return {
              isStreaming: false,
              isCancellable: false,
              streamingMessageId: null,
              progressCard: {
                active: false,
                phase: null,
                detail: null,
                isBackground: false,
                percent: null,
              },
              // Reset workflow state if stuck in discovering (stream completed without turn_completed)
              ...(isStuckDiscovering ? { writingWorkflowState: 'idle' } : {}),
              ...(silentFailure
                ? {
                    error: 'No reply came back. Please try again.',
                    messages: state.messages.map((m) =>
                      m.id === assistantMsgId
                        ? { ...m, content: 'No reply came back. Please try again.', isStreaming: false }
                        : m
                    ),
                  }
                : {
                    messages: state.messages.map((m) =>
                      m.id === assistantMsgId && m.isStreaming ? { ...m, isStreaming: false } : m
                    ),
                  }),
            }
          })
          if (silentFailureDetected) {
            showProgressRecap('error', 'No reply came back. Please try again.')
          }
          abortController = null
        }
      },

      cancelTurn: async () => {
        const { sessionId, currentTurnId, asyncJobId } = get()

        // Abort the fetch stream
        abortController?.abort()
        abortController = null
        pollAbortController?.abort()
        pollAbortController = null

        clearTransientStatusMessages()

        set({ isCancellable: false })

        // Also signal cancel on the server
        // R1 F1′: Stop is for the turn. A document job drafting this document
        // keeps going (the server leaves it alone and says so).
        let documentJobKept = false
        try {
          if (currentTurnId || sessionId) {
            const cancelParams: { turnId?: string; sessionId?: string } = {}
            if (currentTurnId) cancelParams.turnId = currentTurnId
            if (sessionId) cancelParams.sessionId = sessionId
            const cancelled = await cancelAgentTurn(cancelParams)
            documentJobKept = Boolean(cancelled?.documentJobKept)
          }
          if (asyncJobId && sessionId) {
            await cancelAgentTurn({ sessionId })
          }
        } catch {
          // Best effort — ignore cancel errors
        }

        set({
          isStreaming: false,
          isPolling: false,
          asyncJobId: null,
          asyncJobProgress: 0,
          asyncJobMessage: '',
          currentTurnId: null,
          streamingMessageId: null,
          writingWorkflowState: 'idle',
          pendingMutationKind: null,
          streamRecoveryState: 'idle',
          progressCard: {
            active: false,
            phase: null,
            detail: null,
            isBackground: false,
            percent: null,
          },
          // R1 UX1: the stream can stop mid-markup; drop what the cut left open.
          messages: get().messages.map((m) =>
            m.isStreaming ? { ...m, isStreaming: false, content: trimDanglingMarkdown(m.content) || '(Cancelled)' } : m
          ),
          // No terminal event follows a Stop, so close the rows here — a stop
          // is not a failure, and the durable "Stopped" line says what happened.
          activityPhases: settleActivityPhases(get().activityPhases, 'completed'),
        })
        flushPendingPlanMessage()
        // Sections are persisted one at a time, so a background draft that was
        // already running has left whatever it finished in the editor. The Stop
        // click cannot know how many, so say nothing it cannot stand behind —
        // the old "No further changes applied" was flatly untrue.
        const cancelledMessage = asyncJobId
          ? 'Stopped drafting. Any sections drafted before the stop are in the editor.'
          : documentJobKept
            // Placeholder copy until Kapil's hand test.
            ? 'Stopped. The document is still being drafted: use Cancel on its progress card to stop that too.'
            : 'Stopped drafting.'
        showProgressRecap('cancelled', cancelledMessage)
        appendDurableStatusMessage(cancelledMessage, 'turn_cancelled')
      },

      recoverFromRevisionConflict: async () => {
        // One banner and one button serve two independent conflicts: a
        // plan-card save that lost the optimistic lock, and a turn that needs
        // `lastSubmittedTurn` replayed. They are not mutually exclusive — both
        // can be armed at once — and the two recoveries are different work, so
        // this runs whichever are pending, in that order.
        //
        // Refresh the plan ONLY while the conflicted card is still on screen. A
        // conflict that outlived its card must not swallow a genuine
        // turn-conflict recovery — that would drop the writer's message and
        // leave the affordance dead for the rest of the session. `sendTurn` and
        // a thread switch both clear `planConflict`; this is the belt for
        // anything that removes the card without going through either.
        //
        // Both conflicts can be armed at once, so the plan refresh is NOT
        // terminal: fall through and replay a pending turn as well.
        const pendingPlanConflict = get().planConflict
        // Set once the plan refresh succeeded: `resolvePlanConflict` discards
        // the writer's unsaved edits, and that fact has to survive the replay
        // copy below instead of being silently overwritten.
        let planRefreshNotice: string | null = null
        if (pendingPlanConflict) {
          if (hasPlanCard(get().messages, pendingPlanConflict.planId)) {
            await get().resolvePlanConflict()
            // Only continue when the refresh SUCCEEDED. On failure
            // `resolvePlanConflict` re-arms `planConflict` + `errorAction` so
            // the writer can retry the refresh; falling through would burn
            // `retryAttempted`, blank that affordance, and replay against a
            // plan that was never refreshed.
            if (get().planConflict !== null) return
            planRefreshNotice = get().error
          } else {
            // Card gone: retire the stale conflict and fall through to the
            // normal turn recovery rather than leaving it armed for the next
            // click.
            set({ planConflict: null })
          }
        }
        // Both things happened — say both. Dropping the discard notice is how
        // this defect stayed invisible.
        const withPlanRefreshNotice = (message: string) =>
          planRefreshNotice ? `${planRefreshNotice} ${message}` : message
        const { sessionId, lastSubmittedTurn } = get()
        if (!sessionId || !lastSubmittedTurn) return
        const activeThreadId = get().activeThreadId
        const activeScope = get().activeScope
        if (!activeThreadId || !activeScope) return
        const scopeMatches = serializeScope(activeScope) === serializeScope(lastSubmittedTurn.scope)
        if (activeThreadId !== lastSubmittedTurn.threadId || !scopeMatches) {
          set({
            errorAction: null,
            error: withPlanRefreshNotice(
              'Please switch back to the same chat and scope where that request was sent, then retry.'
            ),
          })
          return
        }
        if (lastSubmittedTurn.retryAttempted) {
          set({
            errorAction: null,
            error: withPlanRefreshNotice(
              'Automatic retry already ran. Review the latest draft and resend your request if needed.'
            ),
          })
          return
        }

        const replayNotice = planRefreshNotice
          ? withPlanRefreshNotice('Your original request has been resent.')
          : null
        set({
          error: replayNotice,
          errorAction: null,
          streamRecoveryState: 'idle',
          lastSubmittedTurn: {
            ...lastSubmittedTurn,
            retryAttempted: true,
          },
        })
        try {
          await reloadActiveThreadHistory()
          if (get().sessionId !== sessionId) return
          await get().sendTurn(
            lastSubmittedTurn.message,
            lastSubmittedTurn.selectedEvidence,
            {
              isConflictRetry: true,
              ...(lastSubmittedTurn.factBankOnly ? { factBankOnly: true } : {}),
              ...(lastSubmittedTurn.quotedText ? { quotedText: lastSubmittedTurn.quotedText } : {}),
            }
          )
          // `sendTurn` blanks `error` as it starts, so the discard notice has to
          // be re-asserted once the replay has run — otherwise the writer is
          // never told their plan edits were thrown away. Never overwrite a
          // fresher failure raised by the turn itself.
          if (replayNotice && get().error === null && get().sessionId === sessionId) {
            set({ error: replayNotice })
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to refresh and retry',
            errorAction: null,
          })
        }
      },

      appendAssistantMessage: (content) => {
        if (!content.trim()) return
        set((state) => ({
          messages: [
            ...state.messages,
            {
              id: nextMsgId(),
              role: 'assistant' as const,
              content,
              timestamp: new Date().toISOString(),
              intent: 'write',
            },
          ],
        }))
      },

      clearSuggestedOffer: () => {
        set({ activeSuggestedOffer: null })
      },

      undoMemorySaved: (messageId, memoryId) =>
        undoMemorySavedLine(
          () => get().messages,
          (update) => set((state) => ({ messages: update(state.messages) })),
          messageId,
          memoryId
        ),

      // ===================
      // Internal: event handler
      // ===================

      _clearPendingPlanMessage: () => {
        clearPendingPlanMessage()
      },

      _handleStreamEvent: (incomingEvent: StreamEvent) => {
        const { streamingMessageId, currentTurnId, isStreaming, sessionId } = get()
        let event = incomingEvent
        if (sessionId && event.sessionId !== sessionId) {
          // A turn_error is never dropped: early backend failures can't always
          // echo the session/turn id (unparseable body), and dropping one leaves
          // the UI spinning forever. Re-target it at the streaming turn instead.
          if (event.eventType === 'turn_error' && isStreaming) {
            event = {
              ...event,
              sessionId,
              ...(currentTurnId ? { turnId: currentTurnId } : {}),
            }
          } else {
            console.warn('[agent-store] Dropping event: session mismatch', {
              storeSession: sessionId,
              eventSession: event.sessionId,
              eventType: event.eventType,
            })
            return
          }
        }

        if (event.seq > 0) {
          const lastSeq = lastProcessedSeqByTurn.get(event.turnId) ?? 0
          if (event.seq <= lastSeq) {
            return
          }
          lastProcessedSeqByTurn.set(event.turnId, event.seq)
        }

        // §2.7 activity-phase seam: expose the raw event to the store-level
        // subscription (bottom of file) that folds it through
        // `reduceActivityPhases`. This replaces the retired `ActivityStep`
        // accumulation (title clobber / immortal steps / fabricated durations /
        // no search rollup) — the reducer makes each defect impossible by
        // construction. Set BEFORE the switch below so ordering is preserved.
        set({ lastStreamEvent: event })

        switch (event.eventType) {
          case 'turn_started': {
            // Reset turn-level citation accumulators
            turnCitations = []
            turnLibraryCitations = []
            turnWroteDraft = false
            set({
              currentTurnId: event.turnId,
              stateDefault: (event.data['stateDefault'] as StateDefault) ?? null,
              writingWorkflowState: 'discovering',
              progressCard: {
                active: true,
                phase: 'discovering',
                detail: null,
                isBackground: false,
                percent: null,
              },
            })
            showInlineTransientStatus(getProgressPhaseStatusText('discovering'), event.timestamp)
            break
          }

          case 'evidence_search_started': {
            set({
              progressCard: {
                active: true,
                phase: 'searching_evidence',
                detail: null,
                isBackground: false,
                percent: null,
              },
            })
            showInlineTransientStatus(getProgressPhaseStatusText('searching_evidence'), event.timestamp)
            break
          }

          case 'evidence_search_completed': {
            // A `resolution` event refreshes the turn's citations (a revision's
            // saved refs, end-of-turn resolved pages) — it is not a search, so
            // it must not say "Reviewing evidence" while the reply is written.
            // Its citations are still captured below. Same rule as the
            // activity rows (activity-phases applySearchCompleted).
            if (event.data['mode'] !== 'resolution') {
              const detail = getEvidenceDetailFromCompletion(
                event.data['retrievalStatus'],
                event.data['fallbackUsed']
              )
              set((state) => ({
                progressCard: {
                  ...state.progressCard,
                  active: true,
                  phase: state.progressCard.phase ?? 'searching_evidence',
                  detail,
                  isBackground: false,
                  percent: null,
                },
              }))
              showInlineTransientStatus(
                detail
                  ? `${getProgressPhaseStatusText('searching_evidence')}: ${detail}`
                  : 'Reviewing evidence',
                event.timestamp
              )
            }

            // Capture citation metadata for attaching to the assistant message
            const rawCitations = event.data['citations']
            if (Array.isArray(rawCitations) && rawCitations.length > 0) {
              turnCitations = rawCitations.map((c: Record<string, unknown>): AgentCitationSource => {
                const src: AgentCitationSource = {
                  documentId: c['documentId'] as string,
                  documentName: c['documentName'] as string,
                  pageNumber: (c['pageNumber'] as number | null) ?? null,
                }
                if (typeof c['snippet'] === 'string') src.snippet = c['snippet']
                if (typeof c['quote'] === 'string') src.quote = c['quote']
                if (c['highlightData']) src.highlightData = c['highlightData'] as SourceHighlightData
                return src
              })
            }
            const rawLibCitations = event.data['libraryCitations']
            if (Array.isArray(rawLibCitations) && rawLibCitations.length > 0) {
              turnLibraryCitations = rawLibCitations.map((c: Record<string, unknown>): AgentCitationSource => {
                const src: AgentCitationSource = {
                  documentId: c['documentId'] as string,
                  documentName: c['documentName'] as string,
                  pageNumber: (c['pageNumber'] as number | null) ?? null,
                }
                if (typeof c['snippet'] === 'string') src.snippet = c['snippet']
                if (typeof c['quote'] === 'string') src.quote = c['quote']
                if (c['highlightData']) src.highlightData = c['highlightData'] as SourceHighlightData
                return src
              })
            }
            break
          }

          case 'citation_quotes': {
            // Post-validation quotes for the current turn's citations. Merge
            // positionally into the in-memory accumulators so the live panel
            // gets quote-first highlighting without a reload: label "N" →
            // turnCitations[N-1], "LN" → turnLibraryCitations[N-1]. Unparseable
            // or out-of-range labels are ignored; other citation fields are
            // never clobbered.
            const rawQuotes = event.data['quotes']
            if (rawQuotes && typeof rawQuotes === 'object' && !Array.isArray(rawQuotes)) {
              for (const [label, value] of Object.entries(rawQuotes as Record<string, unknown>)) {
                if (typeof value !== 'string' || value.length === 0) continue
                const match = /^(L?)(\d+)$/.exec(label)
                if (!match) continue
                const isLibrary = match[1] === 'L'
                const idx = Number.parseInt(match[2]!, 10)
                if (!Number.isInteger(idx) || idx < 1) continue
                const target = isLibrary ? turnLibraryCitations : turnCitations
                const citation = target[idx - 1]
                if (citation) citation.quote = value
              }
            }
            break
          }

          case 'evidence_review_started': {
            set((state) => ({
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: 'reviewing_evidence',
                detail: null,
                isBackground: false,
                percent: null,
              },
            }))
            showInlineTransientStatus(getProgressPhaseStatusText('reviewing_evidence'), event.timestamp)
            break
          }

          case 'evidence_review_completed': {
            set((state) => ({
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: state.progressCard.phase ?? 'reviewing_evidence',
                isBackground: false,
                percent: null,
              },
            }))
            showInlineTransientStatus('Preparing draft', event.timestamp)
            break
          }

          case 'status': {
            const statusText = (event.data['message'] as string | undefined) ?? ''
            if (!statusText) break
            const evidenceDetail = getEvidenceDetailFromStatus(statusText)
            if (evidenceDetail) {
              set((state) => (
                state.progressCard.active
                  ? {
                    progressCard: {
                      ...state.progressCard,
                      detail: evidenceDetail,
                    },
                  }
                  : {}
              ))
            }
            if (!isStreaming || !currentTurnId || event.turnId !== currentTurnId) break

            showInlineTransientStatus(statusText, event.timestamp)

            const workflowFromStatus = getWorkflowStateFromStatus(statusText)
            if (workflowFromStatus) {
              set((state) => ({
                writingWorkflowState: workflowFromStatus,
                pendingMutationKind: workflowFromStatus === 'revising'
                  ? 'revising'
                  : workflowFromStatus === 'drafting'
                    ? 'drafting'
                    : state.pendingMutationKind,
              }))
            }
            const progressPhaseFromStatus = getProgressPhaseFromStatus(statusText)
            if (progressPhaseFromStatus) {
              set((state) => ({
                progressCard: {
                  ...state.progressCard,
                  active: true,
                  phase: progressPhaseFromStatus,
                  ...(progressPhaseFromStatus !== 'searching_evidence' && progressPhaseFromStatus !== 'reviewing_evidence'
                    ? { detail: null }
                    : {}),
                  isBackground: false,
                  percent: null,
                },
              }))
            }
            break
          }

          case 'assistant_message_chunk': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            const chunk =
              (event.data['chunk'] as string | undefined) ??
              (event.data['text'] as string | undefined) ??
              ''
            if (!chunk) break
            set((state) => {
              if (state.progressCard.isBackground) return {}
              const nextPhase = state.pendingMutationKind === 'revising'
                ? 'revising'
                : state.pendingMutationKind === 'drafting'
                  ? 'drafting'
                  : (
                    state.progressCard.phase === 'discovering'
                    || state.progressCard.phase === 'searching_evidence'
                    || state.progressCard.phase === 'reviewing_evidence'
                  )
                    ? 'finalizing'
                    : state.progressCard.phase
              return {
                progressCard: {
                  ...state.progressCard,
                  active: true,
                  phase: nextPhase,
                  detail: null,
                  isBackground: false,
                  percent: null,
                },
              }
            })
            // Clear status messages once real content starts streaming
            clearTransientStatusMessages()
            if (streamingMessageId) {
              set((state) => ({
                messages: state.messages.map((m) =>
                  m.id === streamingMessageId
                    ? { ...m, content: m.content + chunk, isStreaming: true }
                    : m
                ),
              }))
            } else {
              // Late-arriving chunk after streamingMessageId was cleared — create a new message
              // instead of silently dropping. This prevents the "no response" silent failure.
              const lateId = nextMsgId()
              set((state) => ({
                messages: [
                  ...state.messages,
                  {
                    id: lateId,
                    role: 'assistant' as const,
                    content: chunk,
                    timestamp: event.timestamp,
                    intent: state.selectedIntent ?? 'write',
                  },
                ],
              }))
            }
            break
          }

          case 'assistant_suggested_actions': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            const offer = parseSuggestedActionOffer(event.data)
            if (!offer) {
              set({ activeSuggestedOffer: null })
              break
            }
            set({ activeSuggestedOffer: offer })
            break
          }

          case 'plan_patch_proposed': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            const plan = event.data['plan'] as Record<string, unknown> | undefined
            if (plan) {
              set({
                proposedPlan: plan,
                writingWorkflowState: 'plan_preview',
              })
            }
            break
          }

          case 'plan_patch_applied': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            const updatedPlan = event.data['plan'] as ResponsePlan | undefined
            const updatedSegments = event.data['segments'] as DraftSegment[] | undefined
            const sectionOrder = event.data['planSectionOrder'] as string[] | undefined
            const affectedSectionIds = event.data['affectedSectionIds']
            const newVersion = event.data['newVersion'] as number | undefined
            set((state) => ({
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: 'revising',
                detail: null,
                isBackground: false,
                percent: null,
              },
            }))
            if (updatedPlan) {
              set({ currentPlan: updatedPlan, proposedPlan: null })
            }
            if (updatedSegments) {
              set((state) => ({
                segments: updatedSegments,
                expectedRevision: getExpectedRevisionFromSegments(state.activeScope, updatedSegments)
                  ?? state.expectedRevision,
              }))
            } else if (Array.isArray(affectedSectionIds)) {
              const affected = new Set(
                affectedSectionIds.filter((id): id is string => typeof id === 'string')
              )
              if (affected.size > 0) {
                set((state) => ({
                  segments: state.segments.map((segment) =>
                    affected.has(segment.sectionId)
                      ? { ...segment, status: 'stale' as const }
                      : segment
                  ),
                }))
              }
            }
            if (sectionOrder) {
              set({ planSectionOrder: sectionOrder })
            }
            if (typeof newVersion === 'number') {
              set({ expectedRevision: String(newVersion) })
            }
            // Every set_plan is stashed; the flush decides what the writer sees.
            // A pending "Accept & draft" card appears only when reviewing a FRESH
            // plan (no draft yet). A mid-draft re-plan that drafts nothing stays
            // silent (today's currentPlan-only behaviour) — the flush drops it.
            // A plan this turn then drafts from flushes as the collapsed
            // "Plan approved" card (see `pendingDraftedPlan`). Product
            // decision 2026-06-24: no separate "Generate Plan" button — the card
            // surfaces in the normal flow when there's no draft.
            const planId = event.data['planId'] as string | undefined
            const planSuggestions = (event.data['suggestions'] as PlanSuggestion[] | undefined) ?? []
            // Re-planning the plan a draft in this turn was written from moves
            // its row past the drafted version: the backend approves nothing.
            if (planId && pendingDraftedPlan?.planId === planId) {
              pendingDraftedPlan = null
            }
            if (updatedPlan && planId) {
              // sectionKey keys the stale guard (§4.3a-C3). The session init
              // context carries the canonical sectionKey; fall back to the
              // unit scope's unitId (makeUnitScope sets unitId = sectionKey).
              const planScope = get().activeScope
              const planSectionKey =
                get().sessionInitContext?.sectionKey
                ?? (planScope?.kind === 'unit' ? planScope.unitId : undefined)
                ?? ''
              const livePlan = updatedPlan as unknown as LivePlanJson
              // Stash, don't append — the card flushes after the turn's text
              // settles (plan-card-ux D1). A second set_plan replaces it (upsert).
              pendingPlanMessage = {
                id: nextMsgId(),
                role: 'plan',
                content: '',
                timestamp: new Date().toISOString(),
                plan: {
                  planId,
                  version: typeof newVersion === 'number' ? newVersion : 0,
                  plan: livePlan,
                  status: 'pending',
                  sectionKey: planSectionKey,
                  suggestions: planSuggestions,
                },
              }
            }
            break
          }

          case 'draft_patch_proposed': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            turnWroteDraft = true
            const proposalId = event.data['proposalId']
            const diff = event.data['diff'] as Record<string, unknown> | undefined

            // A return mid-turn can deliver the same proposal twice: once as
            // `pendingProposal` on the session response and once as this
            // replayed event. The first one wins.
            if (get().draftPatchPreview?.proposalId === proposalId) break

            if (typeof proposalId === 'string' && diff) {
              const sectionId = diff['sectionId']
              const before = diff['before']
              const after = diff['after']
              if (typeof sectionId === 'string' && typeof before === 'string' && typeof after === 'string') {
                const sectionName = get().segments.find((s) => s.sectionId === sectionId)?.heading
                const proposalMessage: AgentChatMessage = {
                  id: nextMsgId(),
                  role: 'proposal',
                  content: '',
                  timestamp: new Date().toISOString(),
                  proposal: {
                    proposalId,
                    sectionId,
                    ...(sectionName ? { sectionName } : {}),
                    status: 'pending',
                  },
                }
                // R1: a staged suggestion leaves the section untouched; its
                // `segments` carry the suggested text and its own refs, which
                // stay on the preview and are never written into `segments`.
                const staged = stagedFieldsFromEvent(event.data, sectionId)
                set((state) => ({
                  draftPatchPreview: {
                    proposalId,
                    sectionId,
                    before,
                    after,
                    appliedPatch: null,
                    hunkDecisions: {},
                    ...staged,
                  },
                  writingWorkflowState: 'awaiting_approval',
                  pendingMutationKind: 'revising',
                  messages: [...state.messages, proposalMessage],
                }))
                if (staged.mode === 'staged') {
                  useAiEditsStore.getState().upsertSuggestion({
                    proposalId,
                    sectionId,
                    heading: sectionName ?? null,
                    before,
                    after,
                    baseSegmentVersion: staged.baseSegmentVersion ?? null,
                    afterCitationRefs: staged.afterCitationRefs ?? null,
                    afterLibraryCitationRefs: staged.afterLibraryCitationRefs ?? null,
                    turnId: event.turnId,
                  })
                }
              }
            }
            break
          }

          case 'draft_edit_applied': {
            // R1: an edit the user asked for is saved and can be undone. The
            // text arrives with the `draft_patch_applied` that follows; this
            // only records the "Updated X · Undo" notice. No review strip, and
            // the editor stays editable.
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            turnWroteDraft = true
            const state = get()
            const messageId = state.streamingMessageId
              ?? [...state.messages].reverse().find((m) => m.role === 'assistant')?.id
              ?? null
            const edit = parseDraftEditApplied(event.data, {
              sessionId: event.sessionId || state.sessionId || '',
              turnId: event.turnId,
              messageId,
            })
            if (edit && edit.sessionId) useAiEditsStore.getState().recordEdit(edit)
            break
          }

          case 'draft_patch_applied': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            const proposalId = event.data['proposalId'] as string | undefined
            const updatedSegments = event.data['segments'] as DraftSegment[] | undefined
            const sectionOrder = event.data['planSectionOrder'] as string[] | undefined
            const affectedSectionIds = event.data['affectedSectionIds']
            const newVersion = event.data['newVersion'] as number | undefined
            set((state) => ({
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: 'revising',
                detail: null,
                isBackground: false,
                percent: null,
              },
            }))

            // Any draft written in the turn retires the pending "Accept & draft"
            // card. The plan it names was drafted from, so its card flushes as
            // "Plan approved" (see `pendingDraftedPlan`).
            pendingPlanTurnDrafted = true
            turnWroteDraft = true
            const draftedPlanId = event.data['planId']
            const draftedPlanVersion = event.data['planVersion']
            pendingDraftedPlan = typeof draftedPlanId === 'string' && draftedPlanId.length > 0
              ? {
                  planId: draftedPlanId,
                  version: typeof draftedPlanVersion === 'number' && Number.isFinite(draftedPlanVersion)
                    ? draftedPlanVersion
                    : null,
                }
              : null

            // A later turn applying an edit's proposal is the chat undo tool.
            if (typeof proposalId === 'string') {
              useAiEditsStore.getState().noteDraftApplied(proposalId, event.turnId)
            }

            const preview = get().draftPatchPreview
            const isPreviewMatch = (
              preview &&
              typeof proposalId === 'string' &&
              preview.proposalId === proposalId
            )
            if (isPreviewMatch) {
              set({
                draftPatchPreview: {
                  ...preview,
                  appliedPatch: {
                    ...(typeof newVersion === 'number' ? { newVersion } : {}),
                    ...(Array.isArray(affectedSectionIds)
                      ? { affectedSectionIds: affectedSectionIds.filter((id): id is string => typeof id === 'string') }
                      : {}),
                    ...(sectionOrder ? { planSectionOrder: sectionOrder } : {}),
                    ...(updatedSegments ? { segments: updatedSegments } : {}),
                  },
                },
                writingWorkflowState: 'awaiting_approval',
              })
            }

            if (updatedSegments) {
              set((state) => ({
                segments: updatedSegments,
                expectedRevision: getExpectedRevisionFromSegments(state.activeScope, updatedSegments)
                  ?? state.expectedRevision,
              }))
            } else if (Array.isArray(affectedSectionIds)) {
              const affected = new Set(
                affectedSectionIds.filter((id): id is string => typeof id === 'string')
              )
              if (affected.size > 0) {
                set((state) => ({
                  segments: state.segments.map((segment) =>
                    affected.has(segment.sectionId)
                      ? {
                        ...segment,
                        status: 'fresh' as const,
                        // The AI write bumped this segment server-side. Without the
                        // full rows we still know the new version — carry it, or the
                        // editor keeps sending a stale expectedVersion and the user's
                        // next manual save 409s against the app's own write.
                        ...(typeof newVersion === 'number' ? { version: newVersion } : {}),
                      }
                      : segment
                  ),
                }))
              }
            }
            if (sectionOrder) {
              set({ planSectionOrder: sectionOrder })
            }
            if (!isPreviewMatch) {
              set({ writingWorkflowState: 'idle', pendingMutationKind: null })
            }
            if (typeof newVersion === 'number') {
              set({ expectedRevision: String(newVersion) })
            }
            // Refetch section assignments so collaboration status reflects backend autoAdvanceToInProgress
            const dpaScope = get().activeScope
            if (dpaScope?.artifactId) {
              void useSectionAssignmentsStore.getState().fetchAssignments(dpaScope.artifactId)
            }
            break
          }

          case 'async_handoff': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            const jobId = event.data['jobId'] as string | undefined
            if (jobId) {
              // Background work just started — pull the shared jobs poll back to
              // its fast cadence in case it had backed off on an idle tab.
              useActiveJobsStore.getState().notifyJobDispatched()
              showInlineTransientStatus(getProgressPhaseStatusText('background'), event.timestamp)
              set((state) => {
                return {
                  asyncJobId: jobId,
                  asyncJobProgress: 0,
                  asyncJobMessage: 'Processing in the background...',
                  isPolling: true,
                  isStreaming: false,
                  isCancellable: true,
                  writingWorkflowState: state.pendingMutationKind === 'revising' ? 'revising' : 'drafting',
                  streamRecoveryState: 'idle',
                  progressCard: {
                    active: true,
                    phase: 'background',
                    detail: null,
                    isBackground: true,
                    percent: 0,
                  },
                  ...releaseReplyPlaceholder(state),
                }
              })
              const { sessionId } = get()
              if (sessionId) {
                attachAsyncJobPoll(jobId, sessionId)
              }
            }
            break
          }

          case 'job_started': {
            // R1 F1′: start_job queued a whole-document job, and this turn ends
            // now with its one-line reply. The job is shown by its own card
            // (PanelDocumentJob, fed by the jobs list) and is never this turn's
            // background job, so nothing here polls it or holds the composer.
            if (currentTurnId && event.turnId !== currentTurnId) break
            useActiveJobsStore.getState().notifyJobDispatched()
            break
          }

          case 'question_raised': {
            // R1 Q2: questions `ask_user` saved. The panel shows them from the
            // questions store, where they outlive this session's messages.
            if (currentTurnId && event.turnId !== currentTurnId) break
            useQuestionsStore.getState().addRaised(parseRaisedQuestions(event.data))
            break
          }

          case 'question_answered': {
            // R1 Q2: what this answer turn recorded (draft update started,
            // waiting, nothing to update, skipped sections).
            if (currentTurnId && event.turnId !== currentTurnId) break
            const answered = parseQuestionAnswered(event.data)
            if (answered) useQuestionsStore.getState().recordTurnOutcome(answered.askId, answered.outcome)
            break
          }

          case 'memory_saved': {
            // The agent saved something to bid memory: record it on this
            // turn's reply so the panel can show "Saved to memory · Undo".
            if (currentTurnId && event.turnId !== currentTurnId) break
            const line = parseMemorySavedLine(event.data)
            if (!line) break
            set((state) => {
              const targetId = state.streamingMessageId
                ?? [...state.messages].reverse().find((m) => m.role === 'assistant')?.id
              if (!targetId) return {}
              return { messages: appendMemorySavedLine(state.messages, targetId, line) }
            })
            break
          }

          case 'fill_proposals_staged': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            const stagedArtifactId = event.data['artifactId'] as string | undefined
            if (!stagedArtifactId) break
            // Only the schedule the user is looking at refetches — a fill session
            // for another artifact must not overwrite the open workbook's review
            // queue.
            const scheduleStore = useScheduleStore.getState()
            if (scheduleStore.artifactId === stagedArtifactId) void scheduleStore.refreshProposals()
            const wordFormStore = useWordFormStore.getState()
            if (wordFormStore.artifactId === stagedArtifactId) void wordFormStore.refreshProposals()
            break
          }

          case 'coverage_check_started':
          case 'coverage_check_passed':
          case 'coverage_warning':
          case 'coverage_blocked': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            set((state) => ({
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: 'verifying',
                detail: null,
                isBackground: false,
                percent: null,
              },
            }))
            break
          }

          case 'verification_results': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            if (!currentTurnId) break
            const status = event.data['status'] as VerifierStatus | undefined
            const summary = event.data['summary'] as string | undefined
            const issues = event.data['issues'] as VerifierIssue[] | undefined
            const metrics = event.data['metrics'] as VerifierMetrics | undefined
            const fixInstructions = event.data['fixInstructions'] as string | undefined

            if (!status || !summary || !issues || !metrics) {
              break
            }

            const verification: VerificationState = {
              status,
              summary,
              issues,
              metrics,
              ...(fixInstructions ? { fixInstructions } : {}),
              timestamp: event.timestamp,
            }

            set((state) => ({
              progressCard: {
                ...state.progressCard,
                active: true,
                phase: 'verifying',
                detail: null,
                isBackground: false,
                percent: null,
              },
            }))
            set((state) => ({
              lastVerification: verification,
              messages: [
                ...state.messages,
                {
                  id: nextMsgId(),
                  role: 'status' as const,
                  content: `Verification ${status}: ${summary}`,
                  timestamp: event.timestamp,
                  eventType: 'verification_results',
                },
              ],
            }))
            break
          }

          case 'turn_completed': {
            if (currentTurnId && event.turnId !== currentTurnId) break
            clearTransientStatusMessages()
            const asyncPending = event.data['status'] === 'async_pending'
            const awaitingConfirmation = event.data['status'] === 'awaiting_confirmation'
            const isFailed = event.data['status'] === 'failed'
            set((state) => {
              // A tool that hands off to a worker mid-turn (start_fill_run) leaves
              // the turn completing normally while the job runs on. Clearing the
              // card here would strand the poll with no visible progress, so an
              // active poll keeps both the card and the workflow state it raised.
              const backgroundJobRunning = state.isPolling || state.asyncJobId !== null
              const keepBackgroundCard = asyncPending || backgroundJobRunning
              return {
                isStreaming: false,
                ...(state.isPolling || asyncPending ? {} : { isCancellable: false }),
                ...(state.isPolling ? {} : { currentTurnId: null }),
                writingWorkflowState: isFailed
                  ? state.writingWorkflowState === 'error' ? 'error' : 'idle'
                  : backgroundJobRunning
                    ? state.writingWorkflowState
                    : awaitingConfirmation || state.draftPatchPreview
                      ? 'awaiting_approval'
                      : 'idle',
                pendingMutationKind: awaitingConfirmation || state.draftPatchPreview
                  ? state.pendingMutationKind
                  : null,
                ...(isFailed ? {} : { errorAction: null }),
                streamRecoveryState: state.streamRecoveryState === 'recovering'
                  ? 'idle'
                  : state.streamRecoveryState,
                progressCard: keepBackgroundCard
                  ? {
                    active: true,
                    phase: 'background' as const,
                    detail: null,
                    isBackground: true,
                    percent: state.progressCard.percent ?? 0,
                  }
                  : {
                    active: false,
                    phase: null,
                    detail: null,
                    isBackground: false,
                    percent: null,
                  },
              }
            })
            if (streamingMessageId) {
              // Capture accumulated turn citations before clearing
              const citationSources = turnCitations.length > 0 ? [...turnCitations] : undefined
              const libCitationSources = turnLibraryCitations.length > 0 ? [...turnLibraryCitations] : undefined
              const wroteDraft = turnWroteDraft
              turnCitations = []
              turnLibraryCitations = []
              turnWroteDraft = false

              set((state) => ({
                messages: (() => {
                  const streamingMessage = state.messages.find((m) => m.id === streamingMessageId)
                  const shouldDropEmptyAssistant = isEmptyAssistantPlaceholder(streamingMessage)
                  if (shouldDropEmptyAssistant) {
                    return state.messages.filter((m) => m.id !== streamingMessageId)
                  }
                  return state.messages.map((m) =>
                    m.id === streamingMessageId
                      ? {
                          ...m,
                          isStreaming: false,
                          ...(citationSources ? { sources: citationSources } : {}),
                          ...(libCitationSources ? { librarySources: libCitationSources } : {}),
                          ...(wroteDraft ? { citationLabels: 'saved' as const } : {}),
                        }
                      : m
                  )
                })(),
                streamingMessageId: null,
              }))
            }
            if (!asyncPending) {
              flushPendingPlanMessage()
            }
            break
          }

          case 'turn_error': {
            // Deliberately ignores a turn_error from a different turn: a late
            // failure from a superseded turn must not clobber the active one.
            // This is only safe while `currentTurnId` is accurate — an async
            // turn used to leave it set after polling ended, which swallowed the
            // next turn's error and stranded the UI on sendTurn's generic
            // "No reply came back" fallback. Released at poll exit now.
            if (currentTurnId && event.turnId !== currentTurnId) break
            pollAbortController?.abort()
            pollAbortController = null
            abortController?.abort()
            abortController = null
            clearTransientStatusMessages()
            flushPendingPlanMessage()

            const rawErrMsg = (event.data['message'] as string | undefined) ?? 'An error occurred'
            const errorCode = event.data['code'] as string | undefined
            const errMsg = getUserFacingTurnError(errorCode, rawErrMsg)
            const isConflictError = isRevisionPreconditionError(errorCode, rawErrMsg)
            const submittedTurn = get().lastSubmittedTurn
            const canOfferConflictRetry = Boolean(
              isConflictError
              && submittedTurn
              && !submittedTurn.retryAttempted
            )

            if (errorCode === 'SESSION_BUSY' && sessionId) {
              if (sessionBusyHintTimer) {
                clearTimeout(sessionBusyHintTimer)
              }
              set({ isLockedByAnotherTab: true })
              sessionBusyHintTimer = setTimeout(() => {
                const currentSessionId = useAgentStore.getState().sessionId
                if (currentSessionId === sessionId) {
                  useAgentStore.setState({ isLockedByAnotherTab: false })
                }
                sessionBusyHintTimer = null
              }, SESSION_BUSY_HINT_TTL_MS)
            }

            // Handle auth errors specially - clear session and let app redirect to login
            if (errorCode === 'AUTH_ERROR' || errorCode === 'UNAUTHORIZED' || errorCode === 'TOKEN_EXPIRED') {
              set({
                error: 'Your session expired. Sign in again to continue.',
                isStreaming: false,
                isCancellable: false,
                isPolling: false,
                asyncJobId: null,
                asyncJobProgress: 0,
                asyncJobMessage: '',
                progressCard: {
                  active: false,
                  phase: null,
                  detail: null,
                  isBackground: false,
                  percent: null,
                },
                sessionId: null, // Clear session to trigger app-level auth check
                sessionInitContext: null,
                activeScope: null,
                activeThreadId: null,
                expectedRevision: null,
                threads: [],
                threadPreviewById: {},
                activeSuggestedOffer: null,
                streamingMessageId: null,
                currentTurnId: null,
                writingWorkflowState: 'error',
                pendingMutationKind: null,
                lastSubmittedTurn: null,
                errorAction: null,
                streamRecoveryState: 'idle',
                isLockedByAnotherTab: false,
              })
              if (streamingMessageId) {
                set((state) => ({
                  messages: state.messages.map((m) =>
                    m.id === streamingMessageId
                      ? { ...m, content: 'Your session expired. Sign in again to continue.', isStreaming: false }
                      : m
                  ),
                }))
              }
              // Note: App.tsx or auth context should observe sessionId becoming null
              // and redirect to login. No window.location.href here to keep store pure.
              break
            }

            // Handle other errors normally
            set({
              error: errMsg,
              isStreaming: false,
              isCancellable: false,
              isPolling: false,
                asyncJobId: null,
                asyncJobProgress: 0,
                asyncJobMessage: '',
                progressCard: {
                  active: false,
                  phase: null,
                  detail: null,
                  isBackground: false,
                  percent: null,
                },
                currentTurnId: null,
                writingWorkflowState: 'error',
                pendingMutationKind: null,
                errorAction: canOfferConflictRetry ? 'refresh_retry_conflict' : null,
                streamRecoveryState: get().streamRecoveryState === 'recovering'
                  ? 'replay_unavailable'
                  : get().streamRecoveryState,
                ...(typeof event.data['currentRevision'] === 'string'
                  ? { expectedRevision: event.data['currentRevision'] }
                  : {}),
              })
              showProgressRecap('error', errMsg)
            if (streamingMessageId) {
              set((state) => ({
                messages: state.messages.map((m) =>
                  m.id === streamingMessageId
                    ? { ...m, content: errMsg, isStreaming: false }
                    : m
                ),
                streamingMessageId: null,
              }))
            }
            break
          }

          default:
            break
        }
      },

      acceptDraftPatchPreview: async () => {
        const preview = get().draftPatchPreview
        if (!preview) return
        const sessionId = get().sessionId

        if (preview.mode === 'staged') {
          await resolveStagedPreview(preview, 'accepted')
          return
        }

        const appliedPatch = preview.appliedPatch
        if (!appliedPatch) {
          // The apply event never arrived (backend write failed or the stream
          // dropped it). The proposed content exists only in preview.after —
          // silently "accepting" here is data loss. Persist it ourselves, the
          // same way reject persists preview.before.
          const targetSegment = get().segments.find(
            (segment) => segment.sectionId === preview.sectionId,
          )
          if (!sessionId || !targetSegment) {
            set({
              isResolvingDraftPatchPreview: false,
              error: 'Could not apply the accepted change — the draft section could not be found. Reject the proposal and try again.',
            })
            return
          }
          set({ isResolvingDraftPatchPreview: true, error: null })
          try {
            const saved = await saveSegment(sessionId, preview.sectionId, {
              content: preview.after,
              expectedVersion: targetSegment.version,
              wordCount: estimateWordCountFromHtml(preview.after),
            })
            set((state) => ({
              segments: state.segments.map((segment) => (
                segment.sectionId === preview.sectionId
                  ? {
                    ...segment,
                    content: preview.after,
                    version: saved.version,
                    status: saved.status,
                    wordCount: saved.wordCount,
                  }
                  : segment
              )),
              expectedRevision: String(saved.version),
              draftPatchPreview: null,
              isResolvingDraftPatchPreview: false,
              writingWorkflowState: 'idle',
              pendingMutationKind: null,
              error: null,
              messages: updateProposalMessageStatus(state.messages, preview.proposalId, 'accepted'),
            }))
            await recordResolutionOrWarn(sessionId, preview.proposalId, 'accepted', (message) => set({ error: message }))
          } catch (error) {
            // Keep the preview alive — the user can retry Accept or Reject.
            set({
              isResolvingDraftPatchPreview: false,
              error: error instanceof Error ? error.message : 'Failed to apply the accepted revision',
            })
          }
          return
        }

        // Track 3: if the user rejected any individual hunks, compute the
        // partial-merged HTML and persist it instead of accepting the full
        // server-applied content. Pending hunks default to accepted, matching
        // "Accept all (and any pending)" semantics.
        const hunkDecisions = preview.hunkDecisions ?? {}
        const hasRejectedHunks = Object.values(hunkDecisions).some((d) => d === 'rejected')
        const targetSegment = appliedPatch.segments?.find(
          (s) => s.sectionId === preview.sectionId,
        )

        if (hasRejectedHunks && sessionId && targetSegment) {
          const diff = buildSegmentDiff(preview.before, preview.after)
          const partialHtml = applyHunkDecisions(diff.segments, hunkDecisions, 'accepted')
          set({ isResolvingDraftPatchPreview: true, error: null })
          try {
            const saved = await saveSegment(sessionId, preview.sectionId, {
              content: partialHtml,
              expectedVersion: targetSegment.version,
              wordCount: estimateWordCountFromHtml(partialHtml),
            })
            set((state) => ({
              segments: state.segments.map((segment) => (
                segment.sectionId === preview.sectionId
                  ? {
                    ...segment,
                    content: partialHtml,
                    version: saved.version,
                    status: saved.status,
                    wordCount: saved.wordCount,
                  }
                  : segment
              )),
              expectedRevision: String(saved.version),
              draftPatchPreview: null,
              isResolvingDraftPatchPreview: false,
              writingWorkflowState: 'idle',
              pendingMutationKind: null,
              error: null,
              messages: updateProposalMessageStatus(state.messages, preview.proposalId, 'accepted'),
            }))
            await recordResolutionOrWarn(sessionId, preview.proposalId, 'accepted', (message) => set({ error: message }))
            return
          } catch (error) {
            set({
              isResolvingDraftPatchPreview: false,
              error: error instanceof Error ? error.message : 'Failed to apply partial revision',
            })
            return
          }
        }

        // Full-accept fast path (no rejections): commit the server-applied
        // segments directly without re-saving.
        set((state) => ({
          ...(appliedPatch.planSectionOrder ? { planSectionOrder: appliedPatch.planSectionOrder } : {}),
          ...(appliedPatch.segments ? { segments: appliedPatch.segments } : {}),
          ...(typeof appliedPatch.newVersion === 'number'
            ? { expectedRevision: String(appliedPatch.newVersion) }
            : {}),
          ...(appliedPatch.segments
            ? {
                expectedRevision: getExpectedRevisionFromSegments(state.activeScope, appliedPatch.segments)
                  ?? state.expectedRevision,
              }
            : {}),
          ...(!appliedPatch.segments && appliedPatch.affectedSectionIds
            ? {
              segments: state.segments.map((segment) =>
                appliedPatch.affectedSectionIds?.includes(segment.sectionId)
                  ? {
                    ...segment,
                    status: 'fresh' as const,
                    // Same reason as the draft_patch_applied handler: the accepted
                    // write already bumped this segment server-side, so the local
                    // version must follow or the next manual save conflicts with it.
                    ...(typeof appliedPatch.newVersion === 'number'
                      ? { version: appliedPatch.newVersion }
                      : {}),
                  }
                  : segment
              ),
            }
            : {}),
          draftPatchPreview: null,
          isResolvingDraftPatchPreview: false,
          writingWorkflowState: 'idle',
          pendingMutationKind: null,
          error: null,
          messages: updateProposalMessageStatus(state.messages, preview.proposalId, 'accepted'),
        }))

        if (sessionId) {
          // The segment is already saved server-side and local state is updated;
          // recording the resolution is awaited only so a failure can warn that
          // the strip may reappear (pending-proposal lifecycle).
          await recordResolutionOrWarn(sessionId, preview.proposalId, 'accepted', (message) => set({ error: message }))
        }
      },

      setHunkDecision: (hunkId, decision) => {
        set((state) => {
          const preview = state.draftPatchPreview
          if (!preview) return state
          const next = { ...preview.hunkDecisions, [hunkId]: decision }
          return {
            draftPatchPreview: { ...preview, hunkDecisions: next },
          }
        })
      },

      acceptPlanMessage: async (planId) => {
        // Stale guard (§4.3a-C3): never approve/draft from a stale plan. The
        // card's stale banner + Regenerate button route the writer to
        // regenerate first; this no-op keeps the action non-destructive.
        if (get().stalePlanIds.has(planId)) {
          set({ error: null, errorAction: null })
          return
        }
        const { approvePlan } = await import('@/lib/api')
        try {
          await approvePlan(planId)
        } catch (error) {
          // The global unhandled-rejection handler in main.tsx deliberately
          // skips ApiRequestError, so letting this bubble is TOTAL silence — no
          // toast, no log, no banner, and the card sits there looking pending.
          // Surface it on the panel's error banner instead: leave the plan
          // pending so Accept stays available, and skip the draft turn.
          set({
            error: writerFacingMessage(error, "Couldn't accept the plan — try again."),
            errorAction: null,
          })
          return
        }
        set((state) => ({
          error: null,
          errorAction: null,
          // An approved plan can no longer be edited (the server 400s), so an
          // open draft must go with the approval — otherwise Task 3's
          // "a draft exists ⇒ edit mode" contract leaves an accepted card
          // editable, and any Save that leaks through hits a terminal 400.
          planEdits: withoutPlanDraft(state.planEdits, planId),
          planConflict: state.planConflict?.planId === planId ? null : state.planConflict,
          messages: state.messages.map((m) =>
            m.role === 'plan' && m.plan?.planId === planId
              ? { ...m, plan: { ...m.plan, status: 'accepted' as const } }
              : m
          ),
          // Only when `currentPlan` IS this plan. Without the id check,
          // accepting plan A marks whatever plan B is loaded as approved.
          currentPlan: state.currentPlan?.id === planId
            ? { ...state.currentPlan, status: 'approved' as const }
            : state.currentPlan,
        }))
        // §"Adds": carry the funnel dimensions so engagement depths are
        // separable. `plan_accepted` only fires when a writer accepts a
        // reviewed plan — Depth 2 by definition (§2.3), so depth is constant.
        const acceptCtx = get().sessionInitContext
        captureEvent('plan_accepted', {
          planId,
          bidId: acceptCtx?.bidId,
          artifactId: acceptCtx?.artifactId,
          sectionKey: acceptCtx?.sectionKey,
          depth: 2,
        })

        // Accept-and-draft: approving the plan must run the drafter on it in one
        // user action. The live agent session drives drafting via a Write-intent
        // turn (POST /agent/turn) — the legacy POST /sessions/{id}/draft endpoint
        // is a deprecated stub that no longer generates content. Kick off the
        // draft turn so "Accept & draft" produces a draft from the approved plan.
        if (get().sessionId) {
          await get().sendTurn('Write the draft based on the approved plan.')
        }
      },

      rejectPlanMessage: async (planId) => {
        const { rejectPlan } = await import('@/lib/api')
        try {
          await rejectPlan(planId)
        } catch (error) {
          // Same reasoning as acceptPlanMessage: main.tsx's unhandled-rejection
          // handler skips ApiRequestError, so letting this bubble is total
          // silence. Surface it on the panel banner and leave the card pending
          // so Reject stays available.
          set({
            error: writerFacingMessage(error, "Couldn't reject the plan — try again."),
            errorAction: null,
          })
          return
        }
        set((state) => ({
          error: null,
          errorAction: null,
          // A rejected plan can never be saved — drop any open draft and the
          // conflict that was waiting on it.
          planEdits: withoutPlanDraft(state.planEdits, planId),
          planConflict: state.planConflict?.planId === planId ? null : state.planConflict,
          messages: state.messages.map((m) =>
            m.role === 'plan' && m.plan?.planId === planId
              ? { ...m, plan: { ...m.plan, status: 'rejected' as const } }
              : m
          ),
          // Id-checked for the same reason as accept: rejecting plan A must not
          // mark an unrelated loaded plan B rejected.
          currentPlan: state.currentPlan?.id === planId
            ? { ...state.currentPlan, status: 'rejected' as const }
            : state.currentPlan,
        }))
        // Twin of `plan_accepted` — same funnel dimensions so accept and reject
        // are comparable at the same engagement depth (§2.3: reviewing a plan
        // and deciding on it is Depth 2 either way).
        const rejectCtx = get().sessionInitContext
        captureEvent('plan_rejected', {
          planId,
          bidId: rejectCtx?.bidId,
          artifactId: rejectCtx?.artifactId,
          sectionKey: rejectCtx?.sectionKey,
          depth: 2,
        })
      },

      // ===================
      // Plan card editing (AI reco 3A)
      // ===================

      beginPlanEdit: (planId) => set((state) => {
        if (state.planEdits[planId]) return state
        const message = state.messages.find(
          (m) => m.role === 'plan' && m.plan?.planId === planId
        )
        const meta = message?.plan
        if (!meta || meta.status !== 'pending') return state
        // A draft forked from an unknown base can NEVER save: `expectedVersion`
        // is `z.number().int().positive()` server-side, so 0 is a zod 400 on
        // every attempt — an editor the writer can type into but never commit.
        // Both hydration paths fall back to 0 when the server omits a version.
        // The card gates its edit affordances on the same predicate, so this is
        // the backstop rather than the only guard.
        if (!isEditablePlanVersion(meta.version)) return state
        return {
          planEdits: {
            ...state.planEdits,
            [planId]: createPlanEditDraft(planId, meta.plan, meta.version),
          },
        }
      }),

      updatePlanGoal: (planId, goal) => {
        get().beginPlanEdit(planId)
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (draft) => ({ ...draft, goal })),
        }))
      },

      updatePlanSection: (planId, sectionId, patch) => {
        get().beginPlanEdit(planId)
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (draft) => ({
            ...draft,
            sections: draft.sections.map((section) =>
              section.sectionId === sectionId ? { ...section, ...patch } : section
            ),
          })),
        }))
      },

      movePlanSection: (planId, sectionId, direction) => {
        get().beginPlanEdit(planId)
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (draft) => {
            const sections = [...draft.sections]
            const index = sections.findIndex((section) => section.sectionId === sectionId)
            const target = index + direction
            if (index < 0 || target < 0 || target >= sections.length) return draft
            const [moved] = sections.splice(index, 1)
            sections.splice(target, 0, moved!)
            return { ...draft, sections }
          }),
        }))
      },

      removePlanSection: (planId, sectionId) => {
        get().beginPlanEdit(planId)
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (draft) => ({
            ...draft,
            sections: draft.sections.filter((section) => section.sectionId !== sectionId),
          })),
        }))
      },

      addPlanKeyPoint: (planId, sectionId, text = '') => {
        get().beginPlanEdit(planId)
        const draft = get().planEdits[planId]
        if (!draft) return null
        if (!draft.sections.some((section) => section.sectionId === sectionId)) return null
        const keyPointId = `kp-${draft.nextKeyPointSeq}`
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (current) => ({
            ...current,
            nextKeyPointSeq: current.nextKeyPointSeq + 1,
            sections: current.sections.map((section) =>
              section.sectionId === sectionId
                ? {
                    ...section,
                    keyPoints: [
                      ...section.keyPoints,
                      { id: keyPointId, text, isMachinery: false },
                    ],
                  }
                : section
            ),
          })),
        }))
        return keyPointId
      },

      updatePlanKeyPoint: (planId, sectionId, keyPointId, text) => {
        get().beginPlanEdit(planId)
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (draft) => ({
            ...draft,
            sections: draft.sections.map((section) =>
              section.sectionId === sectionId
                ? {
                    ...section,
                    // Identity, not index: the card hides machinery key points,
                    // so the i-th rendered bullet is not the i-th stored string.
                    keyPoints: section.keyPoints.map((point) =>
                      point.id === keyPointId ? { ...point, text } : point
                    ),
                  }
                : section
            ),
          })),
        }))
      },

      removePlanKeyPoint: (planId, sectionId, keyPointId) => {
        get().beginPlanEdit(planId)
        set((state) => ({
          planEdits: mutatePlanDraft(state.planEdits, planId, (draft) => ({
            ...draft,
            sections: draft.sections.map((section) =>
              section.sectionId === sectionId
                ? {
                    ...section,
                    keyPoints: section.keyPoints.filter((point) => point.id !== keyPointId),
                  }
                : section
            ),
          })),
        }))
      },

      revertPlanEdits: (planId) => set((state) => ({
        planEdits: withoutPlanDraft(state.planEdits, planId),
        planConflict: state.planConflict?.planId === planId ? null : state.planConflict,
        ...(state.planConflict?.planId === planId ? { errorAction: null, error: null } : {}),
      })),

      savePlanEdits: async (planId) => {
        const draft = get().planEdits[planId]
        if (!draft) {
          return { ok: false, reason: 'error', message: 'There are no changes to save.' }
        }
        if (draft.isSaving) {
          return { ok: false, reason: 'error', message: 'This plan is already saving.' }
        }
        // Locally-known terminal state. `beginPlanEdit` refuses a non-pending
        // plan, but a card can be accepted or rejected AFTER a draft is open —
        // don't spend a round trip to be told the plan left `draft`.
        const cardStatus = get().messages.find(
          (m) => m.role === 'plan' && m.plan?.planId === planId
        )?.plan?.status
        if (cardStatus && cardStatus !== 'pending') {
          // DELIBERATE: the draft goes. These edits can never be saved — the
          // plan left `draft` — and leaving them in an open editor makes
          // unsaveable text look recoverable, on a card whose Save can now only
          // fail. Dropping it also closes edit mode, which is what
          // "a draft exists ⇒ edit mode" needs on a decided card. Same disposal
          // as accept/reject, and as the server-side branch below.
          set((state) => ({
            error: PLAN_NOT_EDITABLE_MESSAGE,
            errorAction: null,
            planEdits: withoutPlanDraft(state.planEdits, planId),
            // Unreachable today (a conflict implies a pending card), but the
            // conflict-rides-with-its-draft invariant should hold by
            // construction, not by luck.
            planConflict: state.planConflict?.planId === planId ? null : state.planConflict,
          }))
          return { ok: false, reason: 'not_editable', message: PLAN_NOT_EDITABLE_MESSAGE }
        }
        if (!draft.isDirty) {
          // Nothing changed — leave edit mode without a round trip.
          set((state) => ({ planEdits: withoutPlanDraft(state.planEdits, planId) }))
          return { ok: true, staleSectionIds: [] }
        }

        // Block the two invariant 400s the card can check itself. The server is
        // still the authority — this only spares a round trip and gives the
        // writer copy they can act on.
        const invalid = validatePlanDraft(draft)
        if (invalid) {
          set((state) => ({
            error: invalid.message,
            errorAction: null,
            planEdits: patchPlanDraft(state.planEdits, planId, {
              conflictingSectionIds: invalid.conflictingSectionIds,
            }),
          }))
          return {
            ok: false,
            reason: 'invalid',
            message: invalid.message,
            conflictingSectionIds: invalid.conflictingSectionIds,
          }
        }

        // Spread the stored plan — a literal would strip the rich fields the
        // card never sees (proofPoints, soWhat, sectionType, …).
        const planJson = planDraftToLivePlan(draft)
        // Fingerprint what is being SENT, taken from the draft itself rather
        // than from `planJson` — see `planDraftSignature`. Compared against the
        // draft again when the response lands, to spot edits made in between.
        const sentSignature = planDraftSignature(draft)

        set((state) => ({
          planEdits: patchPlanDraft(state.planEdits, planId, { isSaving: true }),
        }))

        const { updatePlan } = await import('@/lib/api')
        try {
          const { plan, staleSectionIds } = await updatePlan(planId, {
            planJson,
            expectedVersion: draft.baseVersion,
          })
          set((state) => {
            // REBASE, don't discard. The request carried a SNAPSHOT taken before
            // it was sent; the writer keeps typing into the card while it flies
            // (deliberately — locking the editor mid-save is the ergonomic the
            // Save button exists to avoid). Dropping the draft here would delete
            // every keystroke made during the round trip, silently, with the
            // server's response rendered over the top.
            //
            // So: if the draft still serialises to what was sent, it is fully
            // persisted and goes. If it doesn't, the difference is work that has
            // never been saved — keep it, re-flag it dirty, and re-fork it onto
            // the version that just landed so the NEXT save doesn't 409.
            const live = state.planEdits[planId]
            const hasUnsentEdits = live !== undefined && planDraftSignature(live) !== sentSignature
            // A draft can also hold something the payload cannot carry at all —
            // an added-but-still-empty key point, which the serialiser drops.
            // The round trip can never bring that row back, so dropping the
            // draft deletes it, and the writer's caret with it. Keep it, but do
            // NOT call it dirty: nothing about it is savable, so a dirty flag
            // here would leave Save permanently lit and never satisfiable.
            const hasUnsavableContent = live !== undefined && planDraftHasUnsavableContent(live)
            const keepDraft = live !== undefined && (hasUnsentEdits || hasUnsavableContent)
            return {
              error: null,
              errorAction: null,
              planConflict: state.planConflict?.planId === planId ? null : state.planConflict,
              planEdits: keepDraft
                ? {
                    ...state.planEdits,
                    [planId]: {
                      ...live,
                      // The rich fields ride on the server's copy, not the
                      // pre-save fork — `base` is what `planDraftToLivePlan`
                      // spreads, so a stale one would re-send stale rich fields.
                      base: plan.planJson,
                      baseVersion: plan.version,
                      isDirty: hasUnsentEdits,
                      isSaving: false,
                      conflictingSectionIds: [],
                    },
                  }
                : withoutPlanDraft(state.planEdits, planId),
              currentPlan: state.currentPlan?.id === plan.id ? plan : state.currentPlan,
              messages: state.messages.map((m) =>
                m.role === 'plan' && m.plan?.planId === planId
                  ? { ...m, plan: { ...m.plan, plan: plan.planJson, version: plan.version } }
                  : m
              ),
            }
          })
          return { ok: true, staleSectionIds: staleSectionIds ?? [] }
        } catch (error) {
          set((state) => ({
            planEdits: patchPlanDraft(state.planEdits, planId, { isSaving: false }),
          }))
          const details = errorDetails(error)
          const statusCode = (error as { statusCode?: unknown } | null | undefined)?.statusCode
          const rawMessage = error instanceof Error ? error.message : ''

          // 409 — someone else moved the plan. Recoverable: keep the draft so
          // nothing is lost until the writer refreshes.
          if (details['error'] === 'plan_conflict' || statusCode === 409) {
            set({
              error: 'This plan changed since you opened it. Refresh and reapply your changes.',
              errorAction: 'refresh_retry_conflict',
              planConflict: {
                planId,
                currentVersion:
                  typeof details['currentVersion'] === 'number' ? details['currentVersion'] : 0,
                requestedBaseVersion:
                  typeof details['requestedBaseVersion'] === 'number'
                    ? details['requestedBaseVersion']
                    : draft.baseVersion,
              },
            })
            return {
              ok: false,
              reason: 'conflict',
              message: 'This plan changed since you opened it. Refresh and reapply your changes.',
            }
          }

          // TERMINAL 400 — the plan left `draft` mid-flight (approved or
          // rejected elsewhere). Retrying can never succeed, so this must NOT
          // get the `refresh_retry_conflict` affordance: "get latest and retry"
          // would send the writer round a loop with no exit.
          //
          // The code is the contract (twin: `PLAN_NOT_EDITABLE_DETAILS` in
          // backend/src/handlers/plans.ts). The prose match stays only as a
          // fallback for a Lambda deployed before that code shipped.
          if (
            details['error'] === PLAN_NOT_EDITABLE_CODE
            || (statusCode === 400 && /only draft plans can be edited/i.test(rawMessage))
          ) {
            // Same disposal as the local guard above, and for the same reason.
            set((state) => ({
              error: PLAN_NOT_EDITABLE_MESSAGE,
              errorAction: null,
              // Id-checked like accept/reject: only THIS plan's conflict retires.
              planConflict: state.planConflict?.planId === planId ? null : state.planConflict,
              planEdits: withoutPlanDraft(state.planEdits, planId),
            }))
            return { ok: false, reason: 'not_editable', message: PLAN_NOT_EDITABLE_MESSAGE }
          }

          // Invariant 400 — the server names the offending sections.
          if (details['error'] === 'invariant_violation') {
            const conflictingSectionIds = Array.isArray(details['conflictingSectionIds'])
              ? (details['conflictingSectionIds'] as unknown[]).filter(
                  (id): id is string => typeof id === 'string'
                )
              : []
            const message = writerFacingMessage(error, "Couldn't save the plan — try again.")
            set((state) => ({
              error: message,
              errorAction: null,
              planEdits: patchPlanDraft(state.planEdits, planId, { conflictingSectionIds }),
            }))
            return { ok: false, reason: 'invalid', message, conflictingSectionIds }
          }

          // Any OTHER 400 is a request the server modelled and we didn't — a
          // bound we don't mirror, a field we got wrong, a schema that moved
          // ahead of this client. Retrying identical input can only fail
          // identically, so a bare "try again" is a dead end: the draft is kept,
          // no affordance is offered, and every later Save fails the same way.
          // Arm the refresh path instead — re-fetching the plan rebuilds the
          // card from the server and discards the unsaveable fork, which is the
          // only exit that doesn't require a page reload.
          if (statusCode === 400) {
            const message = "Couldn't save this plan. Refresh it and reapply your changes."
            set({
              error: message,
              errorAction: 'refresh_retry_conflict',
              planConflict: {
                planId,
                // The server didn't name a version — this isn't a version race.
                // `resolvePlanConflict` only reads `planId`; these are context.
                currentVersion: draft.baseVersion,
                requestedBaseVersion: draft.baseVersion,
              },
            })
            return { ok: false, reason: 'conflict', message }
          }

          const message = writerFacingMessage(error, "Couldn't save the plan — try again.")
          set({ error: message, errorAction: null })
          return { ok: false, reason: 'error', message }
        }
      },

      resolvePlanConflict: async () => {
        const conflict = get().planConflict
        if (!conflict) return
        // The card is the only thing this refresh can write into. If it's gone
        // (thread switch, session reset), refreshing would fetch a plan nobody
        // is looking at and blank the banner — retire the conflict instead.
        if (!hasPlanCard(get().messages, conflict.planId)) {
          set({ planConflict: null })
          return
        }
        const { getPlanById } = await import('@/lib/api')
        let plan: ResponsePlan
        try {
          ({ plan } = await getPlanById(conflict.planId))
        } catch (error) {
          // Keep the affordance so the writer can try the refresh again.
          set({
            error: writerFacingMessage(error, "Couldn't refresh the plan — try again."),
            errorAction: 'refresh_retry_conflict',
          })
          return
        }
        const nextStatus: PlanChatStatus =
          plan.status === 'approved' ? 'accepted' : plan.status === 'rejected' ? 'rejected' : 'pending'
        set((state) => ({
          // Informational, not a failure — but the banner is the only channel
          // that reaches the writer, and silently dropping their typing would
          // be worse. Rebasing the draft onto a moved plan is not an option:
          // it would resurrect sections the other writer deleted.
          error: 'The plan was updated elsewhere. Your unsaved edits were discarded — review the refreshed plan.',
          errorAction: null,
          planConflict: null,
          planEdits: withoutPlanDraft(state.planEdits, conflict.planId),
          currentPlan: state.currentPlan?.id === plan.id ? plan : state.currentPlan,
          messages: state.messages.map((m) =>
            m.role === 'plan' && m.plan?.planId === conflict.planId
              ? {
                  ...m,
                  plan: {
                    ...m.plan,
                    plan: plan.planJson,
                    version: plan.version,
                    status: nextStatus,
                  },
                }
              : m
          ),
        }))
      },

      markPlanStaleForSection: (sectionKey) => set((state) => {
        const next = new Set(state.stalePlanIds)
        for (const m of state.messages) {
          if (
            m.role === 'plan' &&
            m.plan?.status === 'pending' &&
            m.plan.sectionKey === sectionKey
          ) {
            next.add(m.plan.planId)
          }
        }
        return { stalePlanIds: next }
      }),

      dismissPlanSuggestion: (planId, suggestionId) => {
        // Find the suggestion before removing it — for telemetry, and for the
        // undo stash. The index goes with it so a restore lands where the writer
        // saw it, not appended to the bottom of a re-ranked list.
        const messages = get().messages
        let dismissed: PlanSuggestion | undefined
        let dismissedIndex = -1
        for (const m of messages) {
          if (m.role === 'plan' && m.plan?.planId === planId) {
            dismissedIndex = m.plan.suggestions.findIndex((s) => s.id === suggestionId)
            dismissed = dismissedIndex >= 0 ? m.plan.suggestions[dismissedIndex] : undefined
            break
          }
        }
        // Nothing removed → nothing to stash. Leave any earlier undo intact.
        if (!dismissed) return
        const removed = dismissed
        const removedIndex = dismissedIndex
        set((state) => ({
          messages: state.messages.map((m) => {
            if (m.role !== 'plan' || m.plan?.planId !== planId) return m
            return {
              ...m,
              plan: {
                ...m.plan,
                suggestions: m.plan.suggestions.filter((s) => s.id !== suggestionId),
                dismissedSuggestion: { suggestion: removed, index: removedIndex },
              },
            }
          }),
        }))
        const ctx = get().sessionInitContext
        captureEvent('suggestion_dismissed', {
          kind: removed.kind,
          bidId: ctx?.bidId,
          artifactId: removed.artifactId,
          sectionKey: removed.sectionKey,
        })
      },

      restorePlanSuggestion: (planId) => {
        const messages = get().messages
        let stash: { suggestion: PlanSuggestion; index: number } | null = null
        for (const m of messages) {
          if (m.role === 'plan' && m.plan?.planId === planId) {
            stash = m.plan.dismissedSuggestion ?? null
            break
          }
        }
        if (!stash) return
        const restored = stash
        set((state) => ({
          messages: state.messages.map((m) => {
            if (m.role !== 'plan' || m.plan?.planId !== planId) return m
            // Guard the double-click: a second restore must not duplicate the
            // chip. (`dismissedSuggestion` is cleared below, so this only bites
            // if the same suggestion arrived again from a fresh event.)
            if (m.plan.suggestions.some((s) => s.id === restored.suggestion.id)) {
              return { ...m, plan: { ...m.plan, dismissedSuggestion: null } }
            }
            const next = [...m.plan.suggestions]
            next.splice(Math.min(Math.max(restored.index, 0), next.length), 0, restored.suggestion)
            return {
              ...m,
              plan: { ...m.plan, suggestions: next, dismissedSuggestion: null },
            }
          }),
        }))
        const ctx = get().sessionInitContext
        captureEvent('suggestion_dismiss_undone', {
          kind: restored.suggestion.kind,
          bidId: ctx?.bidId,
          artifactId: restored.suggestion.artifactId,
          sectionKey: restored.suggestion.sectionKey,
        })
      },

      acceptPlanSuggestion: async (planId, suggestion) => {
        const ctx = get().sessionInitContext
        const bidId = ctx?.bidId ?? ''
        const { artifactId, sectionKey } = suggestion

        // Every branch below writes to the server. A rejection must not escape:
        // main.tsx skips ApiRequestError in its unhandled-rejection handler, so
        // an uncaught failure removes nothing, says nothing, and leaves the
        // writer believing the accept landed. Keep the chip, keep the plan
        // fresh, and surface the failure on the panel's error banner.
        try {
          switch (suggestion.kind) {
            case 'requirement':
            case 'criterion': {
              await useCoverageStore.getState().link(
                bidId,
                suggestion.requirementKind === 'compliance'
                  ? {
                      complianceItemId: suggestion.requirementId!,
                      artifactId,
                      sectionKey,
                    }
                  : {
                      evaluationCriterionId: suggestion.requirementId!,
                      artifactId,
                      sectionKey,
                    },
                { artifactName: '', sectionTitle: '' }
              )
              break
            }
            case 'theme': {
              // A single document has no outline entry to hold themeIds — its
              // themes live on its unit brief (lib/brief-themes.ts). saveThemes
              // REPLACES the list, so append to the stored one, de-duped — read
              // only once both stores are known to hold THIS document.
              if (sectionKey === DOCUMENT_SECTION_KEY) {
                const outline = await loadBriefContextFor(artifactId)
                if (themesLiveOnUnitBrief(outline, sectionKey)) {
                  const briefStore = useUnitBriefStore.getState()
                  const storedThemeIds = briefStore.get(sectionKey)?.themeIds ?? []
                  if (!storedThemeIds.includes(suggestion.themeId!)) {
                    const saved = await briefStore.saveThemes(artifactId, sectionKey, [
                      ...storedThemeIds,
                      suggestion.themeId!,
                    ])
                    // saveThemes never throws: false is a lost version race
                    // (conflictSectionKey set) or any other failed write.
                    if (!saved) {
                      throw new Error(
                        useUnitBriefStore.getState().conflictSectionKey === sectionKey
                          ? "Couldn't accept the suggestion — the brief was edited elsewhere. Reload and try again."
                          : ACCEPT_FAILED_MESSAGE
                      )
                    }
                  }
                  break
                }
              }
              // updateSectionAndSave REPLACES themeIds, so read the section's current
              // themeIds from the outline first and append — never clobber existing
              // theme links. De-dupe in case the theme is somehow already mapped.
              const existingThemeIds =
                useDraftingStore
                  .getState()
                  .outline?.sections.find((s) => s.sectionKey === sectionKey)?.themeIds ?? []
              if (!existingThemeIds.includes(suggestion.themeId!)) {
                const saved = await useDraftingStore
                  .getState()
                  .updateSectionAndSave(artifactId, sectionKey, {
                    themeIds: [...existingThemeIds, suggestion.themeId!],
                  })
                // updateSectionAndSave swallows its own failure and reports it as
                // `false` rather than throwing — treat that as a failed accept.
                if (!saved) {
                  throw new Error("Couldn't accept the suggestion — the outline change didn't save. Try again.")
                }
              }
              break
            }
            case 'document': {
              // Read the stored pins only once the brief store is known to hold
              // THIS document's briefs (see loadBriefContextFor).
              await loadBriefContextFor(artifactId)
              // De-dupe before saving — a double-click / retry or the same doc
              // surfacing via two suggestions must not pin it twice (mirrors theme).
              const existingPins =
                useUnitBriefStore.getState().get(sectionKey)?.pinnedDocIds ?? []
              if (!existingPins.includes(suggestion.documentId!)) {
                await useUnitBriefStore.getState().savePins(artifactId, sectionKey, [
                  ...existingPins,
                  suggestion.documentId!,
                ])
                // savePins swallows a 409 — it records conflictSectionKey and
                // resolves instead of throwing. The only reader of that flag is
                // BriefModal, which isn't open from the AI panel, so without
                // this check a lost version race is silent. Same shape as the
                // `!saved` throw in the theme branch.
                if (useUnitBriefStore.getState().conflictSectionKey === sectionKey) {
                  throw new Error(
                    "Couldn't accept the suggestion — the brief was edited elsewhere. Reload and try again."
                  )
                }
              }
              break
            }
          }
        } catch (error) {
          set({
            error: writerFacingMessage(error, "Couldn't accept the suggestion — try again."),
            errorAction: null,
          })
          return
        }

        // Remove the suggestion chip from the message.
        set((state) => ({
          error: null,
          errorAction: null,
          messages: state.messages.map((m) => {
            if (m.role !== 'plan' || m.plan?.planId !== planId) return m
            return {
              ...m,
              plan: {
                ...m.plan,
                suggestions: m.plan.suggestions.filter((s) => s.id !== suggestion.id),
              },
            }
          }),
        }))

        // Accept changed the Brief → plan is now stale (§4.3a).
        get().markPlanStaleForSection(sectionKey)

        captureEvent('suggestion_accepted', {
          kind: suggestion.kind,
          bidId,
          artifactId,
          sectionKey,
        })
      },

      regeneratePlan: async () => {
        // Re-run plan generation through the same live write-intent path
        // Accept&draft uses. Plan generation is only ever reached by sending a
        // normal write-intent utterance — the empty-state "Create a response
        // plan" CTA (3C) enters the same path with its own utterance. The resulting
        // plan_patch_applied appends a fresh card with a new briefSnapshotAt,
        // whose planId is not in stalePlanIds → it renders un-stale.
        await get().sendTurn('Regenerate the response plan for this section.')
      },

      rejectDraftPatchPreview: async () => {
        const { draftPatchPreview, sessionId } = get()
        if (!draftPatchPreview) return
        if (draftPatchPreview.mode === 'staged' && sessionId) {
          await resolveStagedPreview(draftPatchPreview, 'rejected')
          return
        }
        if (!sessionId) {
          set((state) => ({
            draftPatchPreview: null,
            isResolvingDraftPatchPreview: false,
            writingWorkflowState: 'idle',
            pendingMutationKind: null,
            messages: updateProposalMessageStatus(state.messages, draftPatchPreview.proposalId, 'rejected'),
          }))
          return
        }

        set({ isResolvingDraftPatchPreview: true, error: null })

        // R0-A: the server puts the earlier text back itself, compare-and-set
        // on the AI write's version, and only then records the rejection. The
        // browser used to do the restore, so a save that never happened left
        // the AI text in the section while the proposal said "rejected".
        let serverResult: Awaited<ReturnType<typeof resolveProposal>>
        try {
          serverResult = await resolveProposal(sessionId, draftPatchPreview.proposalId, 'rejected')
        } catch (error) {
          const { code, details } = (error ?? {}) as { code?: unknown; details?: Record<string, unknown> }
          if (code === 'PROPOSAL_SECTION_CHANGED') {
            // The section changed after the AI write, so nothing was undone and
            // the suggestion is no longer live. Show the newer text.
            const currentContent = typeof details?.['currentContent'] === 'string' ? details['currentContent'] : null
            const currentVersion = typeof details?.['currentVersion'] === 'number' ? details['currentVersion'] : null
            set((state) => ({
              segments: currentContent !== null && currentVersion !== null
                ? state.segments.map((segment) => (
                  segment.sectionId === draftPatchPreview.sectionId
                    ? { ...segment, content: currentContent, version: currentVersion }
                    : segment
                ))
                : state.segments,
              ...(currentVersion !== null ? { expectedRevision: String(currentVersion) } : {}),
              draftPatchPreview: null,
              isResolvingDraftPatchPreview: false,
              writingWorkflowState: 'idle',
              pendingMutationKind: null,
              error: REJECT_CONFLICT,
            }))
            return
          }
          // Nothing is resolved server-side until the restore lands, so Reject
          // can simply be tried again.
          set({
            isResolvingDraftPatchPreview: false,
            error: error instanceof Error ? error.message : 'Failed to reject revision preview',
          })
          return
        }

        if (serverResult) {
          const restored = serverResult.segment
          set((state) => ({
            segments: restored
              ? state.segments.map((segment) => (
                segment.sectionId === draftPatchPreview.sectionId
                  ? {
                    ...segment,
                    content: restored.content,
                    version: restored.version,
                    status: restored.status,
                    wordCount: restored.wordCount,
                  }
                  : segment
              ))
              : state.segments,
            ...(restored ? { expectedRevision: String(restored.version) } : {}),
            draftPatchPreview: null,
            isResolvingDraftPatchPreview: false,
            writingWorkflowState: 'idle',
            pendingMutationKind: null,
            error: null,
            messages: updateProposalMessageStatus(state.messages, draftPatchPreview.proposalId, 'rejected'),
          }))
          return
        }

        // An older backend (204): it recorded the decision but restored
        // nothing, so put the earlier text back from here, as before.
        try {
          const appliedSegments = draftPatchPreview.appliedPatch?.segments
          const appliedTarget = appliedSegments?.find((segment) => (
            segment.sectionId === draftPatchPreview.sectionId
          ))

          // The apply event never arrived (the stream dropped between
          // `draft_patch_proposed` and `draft_patch_applied`, or the turn was
          // cancelled in the gap). The backend writes the rewrite BEFORE it
          // proposes it, so bailing out here leaves the AI's version saved while
          // the panel says "rejected" — silent data loss. Mirror the accept-side
          // fallback: read the version off the store's own segment and revert
          // against that.
          const fallbackTarget = appliedTarget
            ?? get().segments.find((segment) => (
              segment.sectionId === draftPatchPreview.sectionId
            ))

          if (!fallbackTarget) {
            set((state) => ({
              draftPatchPreview: null,
              isResolvingDraftPatchPreview: false,
              writingWorkflowState: 'idle',
              pendingMutationKind: null,
              messages: updateProposalMessageStatus(state.messages, draftPatchPreview.proposalId, 'rejected'),
            }))
            return
          }

          const reverted = await saveSegment(
            sessionId,
            draftPatchPreview.sectionId,
            {
              content: draftPatchPreview.before,
              expectedVersion: fallbackTarget.version,
              wordCount: estimateWordCountFromHtml(draftPatchPreview.before),
            }
          )

          set((state) => ({
            segments: state.segments.map((segment) => (
              segment.sectionId === draftPatchPreview.sectionId
                ? {
                  ...segment,
                  content: draftPatchPreview.before,
                  version: reverted.version,
                  status: reverted.status,
                  wordCount: reverted.wordCount,
                }
                : segment
            )),
            expectedRevision: String(reverted.version),
            draftPatchPreview: null,
            isResolvingDraftPatchPreview: false,
            writingWorkflowState: 'idle',
            pendingMutationKind: null,
            error: null,
            messages: updateProposalMessageStatus(state.messages, draftPatchPreview.proposalId, 'rejected'),
          }))
        } catch (error) {
          set({
            isResolvingDraftPatchPreview: false,
            error: error instanceof Error ? error.message : 'Failed to reject revision preview',
          })
        }
      },

      // ===================
      // Segments (DraftEditor)
      // ===================

      setSegments: (segments, planSectionOrder) => {
        set((state) => ({
          segments,
          planSectionOrder,
          expectedRevision: getExpectedRevisionFromSegments(state.activeScope, segments)
            ?? state.expectedRevision,
        }))
      },

      updateSegmentVersion: (sectionId, newVersion, updates) => {
        set((state) => ({
          segments: state.segments.map((s) =>
            s.sectionId === sectionId
              ? { ...s, version: newVersion, ...(updates ?? {}) }
              : s
          ),
          ...(state.activeScope?.kind === 'unit' && state.activeScope.unitId === sectionId
            ? { expectedRevision: String(newVersion) }
            : {}),
        }))
      },

      updateSegmentStatus: (sectionId, status) => {
        set((state) => ({
          segments: state.segments.map((s) =>
            s.sectionId === sectionId ? { ...s, status } : s
          ),
        }))
      },

      upsertSegment: (segment) => {
        set((state) => ({
          segments: state.segments.some((s) => s.sectionId === segment.sectionId)
            ? state.segments.map((s) => (s.sectionId === segment.sectionId ? segment : s))
            : [...state.segments, segment],
        }))
      },

      regenerateStaleSegments: async (sectionIds) => {
        if (sectionIds.length === 0) return
        const { segments, isStreaming, isPolling, isCancellable } = get()
        // Same gate the plan card's regenerate entries carry, and for the same
        // reason: `sendTurn` has no isStreaming guard and `abortController` is a
        // module singleton, so a turn started on top of a live one orphans the
        // first stream. Stop then only cancels the newest.
        if (isStreaming || isPolling || isCancellable) {
          set({ error: 'Please wait for the current background task to finish or cancel it first.' })
          return
        }
        // Resolve headings from the store, not the caller — the banner renders
        // from the same list, and a section deleted between render and click
        // must simply drop out rather than be named at the drafter.
        const wanted = new Set(sectionIds)
        const targets = segments.filter((s) => wanted.has(s.sectionId))
        if (targets.length === 0) return

        // ONE turn, not one per section. Sequencing N turns would mean waiting
        // for each to settle (a drafting turn hands off to a background worker
        // and `sendTurn` resolves long before that finishes), and firing them
        // together would clobber the abort controller. The multi-stale banner
        // only ever renders in the composite editor, whose panel scope is
        // `artifact` — cross-section instructions are in scope there, and the
        // unit-scoped "this panel can only edit the open section" guard does
        // not apply.
        const headings = targets.map((s) => `"${s.heading}"`).join(', ')
        const message =
          targets.length === 1
            ? `Regenerate the ${headings} section from the updated plan.`
            : `Regenerate these sections from the updated plan: ${headings}.`
        await get().sendTurn(message)
      },

      // ===================
      // Selection editing (DraftEditor)
      // ===================

      setSelection: (selection) => {
        set({ selection, selectionEditResult: null })
      },

      editCurrentSelection: async (sessionId, instruction, selectedEvidence, options) => {
        const { selection } = get()
        if (!selection) {
          set({ error: 'Select text first, then ask for a rewrite.' })
          return
        }

        const requestId = get().activeSelectionEditRequestId + 1
        // Log the user's instruction (and the snippet they had selected) to
        // chat history immediately. Without this the panel goes silent for
        // several seconds while the API runs, and the LLM has no record of
        // selection-edit instructions on subsequent turns.
        const userMsgId = nextMsgId()
        set((state) => ({
          activeSelectionEditRequestId: requestId,
          isEditingSelection: true,
          selectionEditResult: null,
          error: null,
          messages: [
            ...state.messages,
            {
              id: userMsgId,
              role: 'user' as const,
              content: instruction,
              timestamp: new Date().toISOString(),
              quotedText: truncateQuotedText(selection.text),
            },
          ],
        }))

        try {
          const response = await editSelection(sessionId, {
            selectedText: selection.text,
            selectionRange: selection.range,
            instruction,
            selectedEvidence,
            ...(options?.attachments && options.attachments.length > 0
              ? { attachments: options.attachments }
              : {}),
            ...(options?.factBankOnly ? { factBankOnly: true } : {}),
          })

          if (get().activeSelectionEditRequestId !== requestId) return

          set({
            // Stamp the text the range held at request time so Accept can
            // verify the document has not moved underneath the suggestion.
            selectionEditResult: { ...response, sourceText: selection.text },
            isEditingSelection: false,
          })
        } catch (error) {
          if (get().activeSelectionEditRequestId !== requestId) return

          set({
            error: error instanceof Error ? error.message : "Couldn't rewrite the selected text. Try again.",
            isEditingSelection: false,
          })
          throw error
        }
      },

      clearSelectionEdit: () => {
        set((state) => ({
          selection: null,
          selectionEditResult: null,
          isEditingSelection: false,
          activeSelectionEditRequestId: state.activeSelectionEditRequestId + 1,
        }))
      },

      setApplySelectionRewrite: (fn) => {
        set({ applySelectionRewrite: fn })
      },

      setThreadTransitionSaveFlusher: (fn) => {
        set({ flushDraftSavesBeforeThreadChange: fn })
      },

      // ===================
      // Panel
      // ===================

      openPanel: () => set({ isPanelOpen: true }),
      closePanel: () => set({ isPanelOpen: false }),
      togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),

      // ===================
      // General
      // ===================

      clearError: () => set({ error: null }),

      reset: () => {
        initSessionRequestCounter++
        initSessionInFlight = null
        abortController?.abort()
        abortController = null
        pollAbortController?.abort()
        pollAbortController = null
        clearTransientStatusMessages()
        clearProgressRecapTimer()
        lastProcessedSeqByTurn.clear()
        // Drop any deferred plan card so it can't leak into the next section's
        // thread on the next sendTurn (cross-section flush guard, plan-card-ux D1).
        clearPendingPlanMessage()
        set({
          sessionId: null,
          sessionInitContext: null,
          activeScope: null,
          activeThreadId: null,
          expectedRevision: null,
          threads: [],
          threadPreviewById: {},
          selectedIntent: 'write',
          stateDefault: null,
          currentTurnId: null,
          isStreaming: false,
          isCancellable: false,
          asyncJobId: null,
          asyncJobProgress: 0,
          asyncJobMessage: '',
          isPolling: false,
          progressCard: {
            active: false,
            phase: null,
            detail: null,
            isBackground: false,
            percent: null,
          },
          progressRecap: null,
          messages: [],
          streamingMessageId: null,
          activeSuggestedOffer: null,
          writingWorkflowState: 'idle',
          pendingMutationKind: null,
          streamRecoveryState: 'idle',
          lastSubmittedTurn: null,
          errorAction: null,
          isLockedByAnotherTab: false,
          documentJobEnabled: false,
          currentPlan: null,
          proposedPlan: null,
          stalePlanIds: new Set<string>(),
          ...clearedPlanEditState(),
          draftPatchPreview: null,
          isResolvingDraftPatchPreview: false,
          segments: [],
          planSectionOrder: [],
          lastVerification: null,
          selection: null,
          selectionEditResult: null,
          isEditingSelection: false,
          activeSelectionEditRequestId: 0,
          applySelectionRewrite: null,
          flushDraftSavesBeforeThreadChange: null,
          lastStreamEvent: null,
          activityPhases: initialPhaseState(),
          error: null,
        })
      },
    }
    },
    { name: 'agent-store' }
  )
)

// ===================
// §2.7 activity-phase feed (store-level subscription)
// ===================

/**
 * Fold every raw `lastStreamEvent` through the activity-phase reducer — the same
 * seam tender-chat-store and assistant-store use. This MUST be a store-level
 * subscription (not a React render selector): the callback fires synchronously on
 * every `set`, so it observes every event in order — React batching would skip
 * intermediate events. The identity guard on `lastStreamEvent` makes the
 * re-entrant `setState` below terminate (updating `activityPhases` doesn't change
 * `lastStreamEvent`). The reducer keys on `event.turnId` and resets on a new turn.
 */
useAgentStore.subscribe((state, prev) => {
  if (state.lastStreamEvent === prev.lastStreamEvent) return
  const event = state.lastStreamEvent
  if (!event) return
  const next = reduceActivityPhases(state.activityPhases, event)
  if (next !== state.activityPhases) {
    useAgentStore.setState({ activityPhases: next })
  }
})
